/**
 * Shared elevation drawing utility
 * Used by both ElevationView (full-screen) and PaperSpaceView (viewport)
 *
 * This provides the core elevation rendering that draws walls, foundation,
 * doors, windows, and roofs for a given elevation direction.
 */

import { GRID_SIZE } from '../constants/grid';
import { ROOF_PITCHES } from '../constants/roofs';
import { getPostFootprint, getPostColor, POST_BASE_TYPES } from '../constants/posts';

// Scale factors: converts model inches to paper pixels at 96 DPI
// Formula: (paper_inches_per_foot / 12) * 96 DPI
const ELEVATION_SCALE_MAP = {
  '1/16" = 1\'': (0.0625 / 12) * 96,  // 0.5 pixels per inch
  '1/8" = 1\'': (0.125 / 12) * 96,    // 1 pixel per inch
  '3/16" = 1\'': (0.1875 / 12) * 96,  // 1.5 pixels per inch
  '1/4" = 1\'': (0.25 / 12) * 96,     // 2 pixels per inch
  '3/8" = 1\'': (0.375 / 12) * 96,    // 3 pixels per inch
  '1/2" = 1\'': (0.5 / 12) * 96,      // 4 pixels per inch
  '3/4" = 1\'': (0.75 / 12) * 96,     // 6 pixels per inch
  '1" = 1\'': (1 / 12) * 96,          // 8 pixels per inch
};

// Default options for elevation rendering
const DEFAULT_OPTIONS = {
  renderMode: 'color',          // 'color' | 'line' | 'sketchy'
  sidingPattern: 'horizontal',  // 'horizontal' | 'board-batten' | 'shingle'
  detailedOpenings: false,      // Use detailed door/window rendering
  clipPatterns: true,           // Clip siding patterns around openings
  showDimensions: false,        // Show dimension annotations
  marginX: 40,
  marginY: 50,
  foundationHeightInches: 0,    // Set to 0 to match ElevationView (no foundation)
  showFoundation: false,        // Whether to draw foundation block pattern
  showNoWallsMessage: true,
  useFixedScale: true,          // If true, use viewport's scale; if false, auto-fit
  // Interactive options (for ElevationView)
  selectedRoofId: null,
  selectedPostId: null,         // ID of selected roof to highlight
  selectedLevelId: null,        // ID of selected level to highlight ('level1' or 'level2')
  showLevelLines: true,         // Whether to show level lines
  levelLineExtensions: null,    // { grade: 0, level1: 0, level2: 0 } - pixel extensions for grip dragging
  showGripPoints: false,        // Whether to show grip points on level lines
};

// ========== RENDER MODE STYLE HELPERS ==========

/**
 * Get wall fill and stroke styles based on render mode
 */
function getWallStyle(renderMode) {
  switch (renderMode) {
    case 'line':
      return { fill: '#ffffff', stroke: '#000000', lineWidth: 2.5 };
    case 'sketchy':
      return { fill: '#ffffff', stroke: '#000000', lineWidth: 1.5 };
    case 'color':
    default:
      return { fill: '#f8f8f8', stroke: '#333333', lineWidth: 2.5 };
  }
}

/**
 * Get foundation fill and stroke styles based on render mode
 */
function getFoundationStyle(renderMode) {
  switch (renderMode) {
    case 'line':
      return { fill: 'rgba(0,0,0,0.05)', stroke: '#000000', lineWidth: 1, patternColor: 'rgba(0,0,0,0.3)' };
    case 'sketchy':
      return { fill: 'rgba(0,0,0,0.03)', stroke: '#000000', lineWidth: 1, patternColor: 'rgba(0,0,0,0.25)' };
    case 'color':
    default:
      return { fill: '#d0d0d0', stroke: '#000000', lineWidth: 1, patternColor: 'rgba(0,0,0,0.3)' };
  }
}

/**
 * Get roof fill and stroke styles based on render mode
 */
function getRoofStyle(renderMode) {
  switch (renderMode) {
    case 'line':
      return { fill: '#f5f5f5', stroke: '#000000', lineWidth: 2, fasciaFill: '#e8e8e8' };
    case 'sketchy':
      return { fill: '#f8f8f8', stroke: '#000000', lineWidth: 1.5, fasciaFill: '#ebebeb' };
    case 'color':
    default:
      return { fill: '#e8e0d8', stroke: '#000000', lineWidth: 2, fasciaFill: '#d0c8c0' };
  }
}

/**
 * Get siding pattern styles based on render mode
 */
function getSidingStyle(renderMode) {
  switch (renderMode) {
    case 'line':
      return { stroke: 'rgba(0,0,0,0.25)', lineWidth: 0.5 };
    case 'sketchy':
      return { stroke: 'rgba(0,0,0,0.2)', lineWidth: 0.5 };
    case 'color':
    default:
      return { stroke: 'rgba(0,0,0,0.15)', lineWidth: 0.5 };
  }
}

/**
 * Draw a sketchy/hand-drawn line
 */
// Simple seeded pseudo-random for consistent sketchy lines between renders
function seededRandom(seed) {
  const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function drawSketchyLine(ctx, x1, y1, x2, y2) {
  const segments = Math.max(3, Math.floor(Math.sqrt((x2-x1)**2 + (y2-y1)**2) / 20));
  // Use coordinates as seed for consistent random values
  const baseSeed = x1 * 1000 + y1 * 100 + x2 * 10 + y2;
  ctx.beginPath();
  ctx.moveTo(x1 + (seededRandom(baseSeed) - 0.5) * 2, y1 + (seededRandom(baseSeed + 1) - 0.5) * 2);
  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const x = x1 + (x2 - x1) * t + (seededRandom(baseSeed + i * 2) - 0.5) * 3;
    const y = y1 + (y2 - y1) * t + (seededRandom(baseSeed + i * 2 + 1) - 0.5) * 3;
    ctx.lineTo(x, y);
  }
  ctx.stroke();
}

/**
 * Draw a sketchy rectangle
 */
function drawSketchyRect(ctx, x, y, w, h) {
  drawSketchyLine(ctx, x, y, x + w, y);
  drawSketchyLine(ctx, x + w, y, x + w, y + h);
  drawSketchyLine(ctx, x + w, y + h, x, y + h);
  drawSketchyLine(ctx, x, y + h, x, y);
}

// ========== DETAILED DOOR RENDERING ==========

/**
 * Draw a detailed door with trim based on door type
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} doorType - 'single', 'double', 'french', 'sliding', 'garage', 'bifold', 'barn', 'pocket'
 * @param {number} doorX - Center X position of door
 * @param {number} doorY - Top Y position of door
 * @param {number} doorW - Width in pixels
 * @param {number} doorH - Height in pixels
 * @param {number} elevScale - Scale factor
 * @param {string} renderMode - 'color', 'line', or 'sketchy'
 * @param {boolean} flipKnob - Whether to flip the knob position (for mirrored elevation views)
 */
function drawDetailedDoor(ctx, doorType, doorX, doorY, doorW, doorH, elevScale, renderMode, flipKnob = false) {
  const isColorMode = renderMode === 'color';
  const isSketchyMode = renderMode === 'sketchy';

  // Exterior trim dimensions - 4 inches wide trim boards
  const doorTrimWidth = 4 * elevScale;
  const doorHeadTrimExtra = 2 * elevScale;

  // Door knob and panel inset dimensions - scale with elevation
  // Use proportional minimums based on door width to handle very small scales
  // Knob is approximately 2.5 inches in diameter, positioned 4.5 inches from edge (inside panel area)
  const knobRadius = Math.max(1, Math.min(doorW * 0.04, 1.25 * elevScale));
  const knobOffset = Math.max(2, Math.min(doorW * 0.15, 4.5 * elevScale));
  // Panel insets - approximately 3 inches from door edge
  const panelInset = Math.max(1, Math.min(doorW * 0.08, 1.5 * elevScale));

  // Adjust position for centered door
  const x = doorX - doorW / 2;
  const y = doorY;

  if (doorType === 'double' || doorType === 'french') {
    // Double door - single frame with two panels meeting in the middle
    const panelWidth = doorW / 2;

    if (isColorMode) {
      // Exterior casing/trim
      ctx.fillStyle = '#f5f5f0';
      ctx.fillRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.fillRect(x - doorTrimWidth, y, doorTrimWidth, doorH);
      ctx.fillRect(x + doorW, y, doorTrimWidth, doorH);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorH + doorTrimWidth + doorHeadTrimExtra);

      // Outer frame (single unit)
      ctx.fillStyle = '#8b4513';
      ctx.fillRect(x, y, doorW, doorH);

      // Center seam where the two doors meet
      ctx.strokeStyle = '#5d3a1a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x + panelWidth, y);
      ctx.lineTo(x + panelWidth, y + doorH);
      ctx.stroke();

      // Panel insets
      ctx.strokeRect(x + panelInset, y + panelInset, panelWidth - panelInset * 2, doorH - panelInset * 2);
      ctx.strokeRect(x + panelWidth + panelInset, y + panelInset, panelWidth - panelInset * 2, doorH - panelInset * 2);

      // Door handles
      ctx.fillStyle = '#c0a000';
      ctx.beginPath();
      ctx.arc(x + panelWidth - knobOffset, y + doorH / 2, knobRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x + panelWidth + knobOffset, y + doorH / 2, knobRadius, 0, Math.PI * 2);
      ctx.fill();

      // French door glass panels
      if (doorType === 'french') {
        ctx.fillStyle = 'rgba(135, 206, 250, 0.3)';
        ctx.fillRect(x + panelInset * 2, y + panelInset * 2, panelWidth - panelInset * 4, doorH - panelInset * 4);
        ctx.fillRect(x + panelWidth + panelInset * 2, y + panelInset * 2, panelWidth - panelInset * 4, doorH - panelInset * 4);
        // Muntins
        ctx.strokeStyle = '#5d3a1a';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + panelWidth / 2, y + 8);
        ctx.lineTo(x + panelWidth / 2, y + doorH - 8);
        ctx.moveTo(x + 8, y + doorH / 2);
        ctx.lineTo(x + panelWidth - 8, y + doorH / 2);
        ctx.moveTo(x + panelWidth * 1.5, y + 8);
        ctx.lineTo(x + panelWidth * 1.5, y + doorH - 8);
        ctx.moveTo(x + panelWidth + 8, y + doorH / 2);
        ctx.lineTo(x + doorW - 8, y + doorH / 2);
        ctx.stroke();
      }
    } else {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = isSketchyMode ? 1 : 1.5;
      // Trim
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      ctx.fillRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.strokeRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.strokeRect(x - doorTrimWidth, y, doorTrimWidth, doorH);
      ctx.strokeRect(x + doorW, y, doorTrimWidth, doorH);
      // Frame and center seam
      ctx.lineWidth = isSketchyMode ? 1.5 : 2;
      if (isSketchyMode) {
        drawSketchyRect(ctx, x, y, doorW, doorH);
        drawSketchyLine(ctx, x + panelWidth, y, x + panelWidth, y + doorH);
      } else {
        ctx.strokeRect(x, y, doorW, doorH);
        ctx.beginPath();
        ctx.moveTo(x + panelWidth, y);
        ctx.lineTo(x + panelWidth, y + doorH);
        ctx.stroke();
      }
    }
  } else if (doorType === 'sliding') {
    // Sliding door - two glass panels
    const panelWidth = doorW / 2;

    if (isColorMode) {
      // Trim
      ctx.fillStyle = '#f5f5f0';
      ctx.fillRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.fillRect(x - doorTrimWidth, y, doorTrimWidth, doorH);
      ctx.fillRect(x + doorW, y, doorTrimWidth, doorH);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorH + doorTrimWidth + doorHeadTrimExtra);

      // Back panel (full height glass)
      ctx.fillStyle = 'rgba(135, 206, 250, 0.4)';
      ctx.fillRect(x, y, panelWidth, doorH);
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, panelWidth, doorH);

      // Front panel (overlapping)
      ctx.fillStyle = 'rgba(135, 206, 250, 0.5)';
      ctx.fillRect(x + panelWidth, y, panelWidth, doorH);
      ctx.strokeRect(x + panelWidth, y, panelWidth, doorH);

      // Frame divider
      ctx.fillStyle = '#555';
      ctx.fillRect(x + panelWidth - 3, y, 6, doorH);
    } else {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = isSketchyMode ? 1.5 : 2;
      if (isSketchyMode) {
        drawSketchyRect(ctx, x, y, panelWidth, doorH);
        drawSketchyRect(ctx, x + panelWidth, y, panelWidth, doorH);
      } else {
        ctx.strokeRect(x, y, panelWidth, doorH);
        ctx.strokeRect(x + panelWidth, y, panelWidth, doorH);
      }
    }
  } else if (doorType === 'garage') {
    // Garage door - sectional with windows at top
    const sectionHeight = doorH / 4;

    if (isColorMode) {
      // Trim
      ctx.fillStyle = '#f5f5f0';
      ctx.fillRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.fillRect(x - doorTrimWidth, y, doorTrimWidth, doorH);
      ctx.fillRect(x + doorW, y, doorTrimWidth, doorH);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorH + doorTrimWidth + doorHeadTrimExtra);

      ctx.fillStyle = '#ddd';
      ctx.fillRect(x, y, doorW, doorH);
      ctx.strokeStyle = '#999';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, doorW, doorH);

      // Horizontal section lines
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(x, y + sectionHeight * i);
        ctx.lineTo(x + doorW, y + sectionHeight * i);
        ctx.stroke();
      }

      // Windows in top section
      const windowWidth = doorW / 4 - 8;
      ctx.fillStyle = 'rgba(135, 206, 250, 0.4)';
      for (let i = 0; i < 4; i++) {
        ctx.fillRect(x + 4 + i * (doorW / 4), y + 4, windowWidth, sectionHeight - 8);
        ctx.strokeRect(x + 4 + i * (doorW / 4), y + 4, windowWidth, sectionHeight - 8);
      }
    } else {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = isSketchyMode ? 1.5 : 2;
      if (isSketchyMode) {
        drawSketchyRect(ctx, x, y, doorW, doorH);
      } else {
        ctx.strokeRect(x, y, doorW, doorH);
      }
      // Section lines
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(x, y + sectionHeight * i);
        ctx.lineTo(x + doorW, y + sectionHeight * i);
        ctx.stroke();
      }
    }
  } else if (doorType === 'bifold') {
    // Bifold door - multiple panels
    const panelCount = 4;
    const panelWidth = doorW / panelCount;

    if (isColorMode) {
      // Trim
      ctx.fillStyle = '#f5f5f0';
      ctx.fillRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.fillRect(x - doorTrimWidth, y, doorTrimWidth, doorH);
      ctx.fillRect(x + doorW, y, doorTrimWidth, doorH);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorH + doorTrimWidth + doorHeadTrimExtra);

      const panelGap = Math.max(1, 0.5 * elevScale);
      for (let i = 0; i < panelCount; i++) {
        ctx.fillStyle = '#8b4513';
        ctx.fillRect(x + i * panelWidth + panelGap, y, panelWidth - panelGap * 2, doorH);
        ctx.strokeStyle = '#5d3a1a';
        ctx.lineWidth = 1;
        ctx.strokeRect(x + i * panelWidth + panelInset, y + panelInset, panelWidth - panelInset * 2, doorH - panelInset * 2);
      }
    } else {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = isSketchyMode ? 1.5 : 2;
      const panelGap = Math.max(1, 0.5 * elevScale);
      for (let i = 0; i < panelCount; i++) {
        if (isSketchyMode) {
          drawSketchyRect(ctx, x + i * panelWidth + panelGap, y, panelWidth - panelGap * 2, doorH);
        } else {
          ctx.strokeRect(x + i * panelWidth + panelGap, y, panelWidth - panelGap * 2, doorH);
        }
      }
    }
  } else {
    // Single door (default) - includes 'single', 'barn', 'pocket'
    if (isColorMode) {
      // Exterior casing/trim
      ctx.fillStyle = '#f5f5f0';
      ctx.fillRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.fillRect(x - doorTrimWidth, y, doorTrimWidth, doorH);
      ctx.fillRect(x + doorW, y, doorTrimWidth, doorH);
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorH + doorTrimWidth + doorHeadTrimExtra);

      // Door panel
      ctx.fillStyle = '#8b4513';
      ctx.fillRect(x, y, doorW, doorH);

      // Panel inset detail
      ctx.strokeStyle = '#5d3a1a';
      ctx.lineWidth = 2;
      // Upper panel
      ctx.strokeRect(x + panelInset * 2, y + panelInset * 2, doorW - panelInset * 4, doorH * 0.35 - panelInset * 2);
      // Lower panel
      ctx.strokeRect(x + panelInset * 2, y + doorH * 0.4, doorW - panelInset * 4, doorH * 0.55 - panelInset * 2);

      // Door handle - position based on flipKnob parameter
      // flipKnob=false: knob on right side (default, hinge on left)
      // flipKnob=true: knob on left side (hinge on right)
      ctx.fillStyle = '#c0a000';
      ctx.beginPath();
      const knobX = flipKnob ? x + knobOffset : x + doorW - knobOffset;
      ctx.arc(knobX, y + doorH / 2, knobRadius, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = isSketchyMode ? 1 : 1.5;
      // Trim
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      ctx.fillRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.strokeRect(x - doorTrimWidth, y - doorTrimWidth - doorHeadTrimExtra, doorW + doorTrimWidth * 2, doorTrimWidth + doorHeadTrimExtra);
      ctx.strokeRect(x - doorTrimWidth, y, doorTrimWidth, doorH);
      ctx.strokeRect(x + doorW, y, doorTrimWidth, doorH);
      // Door frame
      ctx.lineWidth = isSketchyMode ? 1.5 : 2;
      if (isSketchyMode) {
        drawSketchyRect(ctx, x, y, doorW, doorH);
      } else {
        ctx.strokeRect(x, y, doorW, doorH);
      }
    }
  }
}

// ========== DETAILED WINDOW RENDERING ==========

/**
 * Draw a detailed window with trim, mullions, and sill
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} winX - Left X position of window
 * @param {number} winY - Top Y position of window
 * @param {number} winW - Width in pixels
 * @param {number} winH - Height in pixels
 * @param {number} elevScale - Scale factor
 * @param {string} renderMode - 'color', 'line', or 'sketchy'
 */
function drawDetailedWindow(ctx, winX, winY, winW, winH, elevScale, renderMode) {
  const isColorMode = renderMode === 'color';
  const isSketchyMode = renderMode === 'sketchy';

  // Exterior trim dimensions - 4 inches wide trim boards
  const trimWidth = 4 * elevScale;
  const headTrimExtra = 2 * elevScale;

  // Scale-dependent dimensions for glass inset and sill
  // Use proportional values based on window size for small scales
  const glassInset = Math.max(1, Math.min(winW * 0.05, 1.5 * elevScale));
  const sillHeight = Math.max(2, Math.min(winH * 0.08, 3 * elevScale));
  const sillExtension = Math.max(1, Math.min(winW * 0.03, 1 * elevScale));

  if (isColorMode) {
    // Exterior casing/trim (white or light colored)
    ctx.fillStyle = '#f5f5f0';
    // Head (top) trim - slightly thicker
    ctx.fillRect(winX - trimWidth, winY - trimWidth - headTrimExtra, winW + trimWidth * 2, trimWidth + headTrimExtra);
    // Side trim (jambs)
    ctx.fillRect(winX - trimWidth, winY, trimWidth, winH);
    ctx.fillRect(winX + winW, winY, trimWidth, winH);
    // Trim outline
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1;
    ctx.strokeRect(winX - trimWidth, winY - trimWidth - headTrimExtra, winW + trimWidth * 2, winH + trimWidth + headTrimExtra);

    // Window frame (light color)
    ctx.fillStyle = '#f0f8ff';
    ctx.fillRect(winX, winY, winW, winH);

    // Glass
    ctx.fillStyle = 'rgba(135, 206, 250, 0.3)';
    ctx.fillRect(winX + glassInset, winY + glassInset, winW - glassInset * 2, winH - glassInset * 2);

    // Frame outline
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.strokeRect(winX, winY, winW, winH);

    // Muntins (dividers)
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(winX + winW / 2, winY);
    ctx.lineTo(winX + winW / 2, winY + winH);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(winX, winY + winH / 2);
    ctx.lineTo(winX + winW, winY + winH / 2);
    ctx.stroke();

    // Window sill (projecting)
    ctx.fillStyle = '#ddd';
    ctx.fillRect(winX - trimWidth - sillExtension, winY + winH, winW + trimWidth * 2 + sillExtension * 2, sillHeight);
    ctx.strokeStyle = '#aaa';
    ctx.lineWidth = 1;
    ctx.strokeRect(winX - trimWidth - sillExtension, winY + winH, winW + trimWidth * 2 + sillExtension * 2, sillHeight);
  } else {
    ctx.strokeStyle = '#000000';

    // Exterior trim (casing)
    ctx.lineWidth = isSketchyMode ? 1 : 1.5;
    if (isSketchyMode) {
      // Simplified trim for sketchy mode
      drawSketchyRect(ctx, winX - trimWidth, winY - trimWidth, winW + trimWidth * 2, winH + trimWidth);
    } else {
      // Head trim (top) - thicker
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      ctx.fillRect(winX - trimWidth, winY - trimWidth - headTrimExtra, winW + trimWidth * 2, trimWidth + headTrimExtra);
      ctx.strokeRect(winX - trimWidth, winY - trimWidth - headTrimExtra, winW + trimWidth * 2, trimWidth + headTrimExtra);
      // Side trim
      ctx.strokeRect(winX - trimWidth, winY, trimWidth, winH);
      ctx.strokeRect(winX + winW, winY, trimWidth, winH);
    }

    // Window frame
    ctx.lineWidth = isSketchyMode ? 1.5 : 2;
    if (isSketchyMode) {
      drawSketchyRect(ctx, winX, winY, winW, winH);
      drawSketchyLine(ctx, winX + winW / 2, winY, winX + winW / 2, winY + winH);
      drawSketchyLine(ctx, winX, winY + winH / 2, winX + winW, winY + winH / 2);
    } else {
      ctx.strokeRect(winX, winY, winW, winH);
      // Muntins (cross pattern)
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(winX + winW / 2, winY);
      ctx.lineTo(winX + winW / 2, winY + winH);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(winX, winY + winH / 2);
      ctx.lineTo(winX + winW, winY + winH / 2);
      ctx.stroke();

      // Window sill
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(winX - trimWidth - sillExtension, winY + winH);
      ctx.lineTo(winX + winW + trimWidth + sillExtension, winY + winH);
      ctx.stroke();
    }
  }
}

/**
 * Draw an elevation view into a specified bounds
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} bounds - { x, y, width, height, scale } - area to draw into, with optional scale
 * @param {Object} data - { walls, roofs, doors, windows }
 * @param {string} elevation - 'north', 'south', 'east', or 'west'
 * @param {Object} options - Optional settings (see DEFAULT_OPTIONS)
 * @returns {Object|null} - Render info or null if nothing to draw
 */
export function drawElevation(ctx, bounds, data, elevation, options = {}) {
  const { walls = [], roofs = [], doors = [], windows = [], posts = [] } = data;

  // Merge options with defaults
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const {
    renderMode,
    sidingPattern,
    detailedOpenings,
    clipPatterns,
    showDimensions,
    marginX,
    marginY,
    foundationHeightInches,
    showFoundation,
    showNoWallsMessage,
    useFixedScale,
    selectedRoofId,
    selectedPostId,
    selectedLevelId,
    showLevelLines,
    levelLineExtensions,
    showGripPoints,
  } = opts;

  // Get styles based on render mode
  const isSketchyMode = renderMode === 'sketchy';
  const wallStyle = getWallStyle(renderMode);
  const foundationStyle = getFoundationStyle(renderMode);
  const roofStyle = getRoofStyle(renderMode);
  const sidingStyle = getSidingStyle(renderMode);

  const isHorizontal = elevation === 'north' || elevation === 'south';

  // Filter exterior walls by orientation first
  const orientedWalls = walls.filter(wall => {
    if (wall.type !== 'exterior' && wall.type !== 'exterior-2x4' && !wall.type?.startsWith('post-')) return false;
    const wallAngle = Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x);
    const isHorizontalWall = Math.abs(Math.cos(wallAngle)) > 0.7;
    if (isHorizontal) {
      return isHorizontalWall;
    } else {
      return !isHorizontalWall;
    }
  });

  if (orientedWalls.length === 0) {
    if (showNoWallsMessage) {
      ctx.fillStyle = '#999';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No walls for this elevation', bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
    }
    return null;
  }

  // Calculate overall bounds to determine which walls are on the correct side
  let allMinX = Infinity, allMaxX = -Infinity;
  let allMinY = Infinity, allMaxY = -Infinity;
  orientedWalls.forEach(wall => {
    allMinX = Math.min(allMinX, wall.start.x, wall.end.x);
    allMaxX = Math.max(allMaxX, wall.start.x, wall.end.x);
    allMinY = Math.min(allMinY, wall.start.y, wall.end.y);
    allMaxY = Math.max(allMaxY, wall.start.y, wall.end.y);
  });

  // Filter to walls on correct side for elevation view.
  //
  // A wall is hidden only when nearer walls cover ALL of it. The obvious rule -
  // keep a wall only if it is the nearest one anywhere in its span - fails as soon
  // as something narrow stands proud of the facade: one porch post, chimney chase
  // or bump-out jamb in front of a 42' wall disqualifies the whole wall, and its
  // windows and doors vanish with it. Coverage is measured span by span instead,
  // so a 6" post hides 6" of wall and the rest of the facade still draws.
  const sideTolerance = 50;

  // Span = the wall's extent along the axis we see (X for N/S views, Y for E/W).
  const wallSpan = (wall) => (isHorizontal
    ? { min: Math.min(wall.start.x, wall.end.x), max: Math.max(wall.start.x, wall.end.x) }
    : { min: Math.min(wall.start.y, wall.end.y), max: Math.max(wall.start.y, wall.end.y) });

  // Depth = distance from the viewer, smaller being nearer. North and west look
  // from the low end of their axis, south and east from the high end.
  const wallDepth = (wall) => {
    const mid = isHorizontal
      ? (wall.start.y + wall.end.y) / 2
      : (wall.start.x + wall.end.x) / 2;
    return (elevation === 'north' || elevation === 'west') ? mid : -mid;
  };

  const elevationWalls = orientedWalls.filter(wall => {
    const span = wallSpan(wall);
    const depth = wallDepth(wall);

    // Spans of every wall standing meaningfully nearer to the viewer than this one
    const blockers = orientedWalls
      .filter(other => other !== wall && wallDepth(other) < depth - sideTolerance)
      .map(wallSpan)
      .filter(s => s.min < span.max && s.max > span.min)
      .sort((a, b) => a.min - b.min);

    // Sweep the span, skipping whatever the blockers cover. A gap between them is
    // a line of sight to this wall, so anything left uncovered keeps it visible.
    let uncovered = span.min;
    for (const blocker of blockers) {
      if (blocker.min > uncovered) break;
      uncovered = Math.max(uncovered, blocker.max);
    }

    return uncovered < span.max;
  });

  if (elevationWalls.length === 0) {
    if (showNoWallsMessage) {
      ctx.fillStyle = '#999';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('No walls for this elevation', bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
    }
    return null;
  }

  // Get wall IDs for filtering doors/windows
  const elevationWallIds = new Set(elevationWalls.map(w => w.id));

  // Calculate bounds in model space - use ALL oriented walls to get full extent
  // This ensures wall return lines at setbacks are positioned correctly
  let minPos = Infinity, maxPos = -Infinity;
  orientedWalls.forEach(wall => {
    if (isHorizontal) {
      minPos = Math.min(minPos, wall.start.x, wall.end.x);
      maxPos = Math.max(maxPos, wall.start.x, wall.end.x);
    } else {
      minPos = Math.min(minPos, wall.start.y, wall.end.y);
      maxPos = Math.max(maxPos, wall.start.y, wall.end.y);
    }
  });

  // Get wall height - use max height from all facing walls for overall sizing
  const maxWallHeight = orientedWalls.length > 0
    ? Math.max(...orientedWalls.map(w => w.height || 96))
    : 96;
  const wallHeightInches = maxWallHeight;
  // Standard wall height for depth transitions (don't extend these lines above normal plate level)
  const standardWallHeightInches = 96;
  const foundationHeight = foundationHeightInches;
  const totalWallHeight = wallHeightInches + foundationHeight;

  // Build wall height map by position for stepped rendering
  // Use ALL oriented walls (not just outermost elevationWalls) to capture different heights
  // Include depth info to determine which walls are outermost (closest to viewer)
  // Include floor elevation for multi-floor stacking
  const wallHeightMap = orientedWalls.map(wall => ({
    wall,
    height: wall.height || 96,
    floorElevation: wall.floorElevation || 0, // Height from grade where this floor starts
    startPos: isHorizontal ? Math.min(wall.start.x, wall.end.x) : Math.min(wall.start.y, wall.end.y),
    endPos: isHorizontal ? Math.max(wall.start.x, wall.end.x) : Math.max(wall.start.y, wall.end.y),
    depth: isHorizontal ? (wall.start.y + wall.end.y) / 2 : (wall.start.x + wall.end.x) / 2,
  }));

  // Calculate roof peak height if applicable
  // Process ALL roofs and calculate their properties for this elevation view
  let roofPeakHeight = 0;
  let roofOverhang = 0;

  // Helper to get roof bounds from points or legacy format
  const getRoofBounds = (roof) => {
    let points;
    if (roof.points && roof.points.length >= 3) {
      points = roof.points;
    } else if (typeof roof.x === 'number' && typeof roof.width === 'number') {
      points = [
        { x: roof.x, y: roof.y },
        { x: roof.x + roof.width, y: roof.y },
        { x: roof.x + roof.width, y: roof.y + roof.height },
        { x: roof.x, y: roof.y + roof.height },
      ];
    } else {
      points = [
        { x: allMinX, y: allMinY },
        { x: allMaxX, y: allMinY },
        { x: allMaxX, y: allMaxY },
        { x: allMinX, y: allMaxY },
      ];
    }
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    return { minX, maxX, minY, maxY, width: maxX - minX, height: maxY - minY, points };
  };

  // Helper to calculate roof peak height
  const calcRoofPeakHeight = (roof, roofBounds) => {
    const pitchDef = ROOF_PITCHES[roof.pitch] || ROOF_PITCHES['6:12'];
    const ridgeDir = roof.ridgeDirection || 'horizontal';
    const { width: roofWidth, height: roofHeight } = roofBounds;

    if (roof.type === 'gable') {
      const runDim = ridgeDir === 'vertical' ? roofWidth : roofHeight;
      const runInches = runDim * (6 / GRID_SIZE) / 2;
      return runInches * (pitchDef.rise / pitchDef.run);
    } else if (roof.type === 'hip') {
      const perpDim = isHorizontal ? roofHeight : roofWidth;
      const runInches = (perpDim / 2) * (6 / GRID_SIZE);
      return runInches * (pitchDef.rise / pitchDef.run);
    } else if (roof.type === 'shed') {
      const shedEdge = roof.shedSlopeEdge || 'north';
      const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
      const runDim = slopeRunsNS ? roofHeight : roofWidth;
      return (runDim * (6 / GRID_SIZE)) * (pitchDef.rise / pitchDef.run);
    }
    return 0;
  };

  // Helper to determine if a roof shows gable end from this elevation
  // Gable ENDs are perpendicular to the ridge direction:
  // - Horizontal ridge (E-W): ENDs face East and West
  // - Vertical ridge (N-S): ENDs face North and South
  const isGableEndForRoof = (roof) => {
    if (roof.type !== 'gable') return false;
    const ridgeDir = roof.ridgeDirection || 'horizontal';
    // North/South elevations (isHorizontal=true) see gable END when ridge is vertical (N-S)
    // East/West elevations (isHorizontal=false) see gable END when ridge is horizontal (E-W)
    return (isHorizontal && ridgeDir === 'vertical') || (!isHorizontal && ridgeDir === 'horizontal');
  };

  // Helper to get roof's horizontal range in elevation view (model space position along view axis)
  const getRoofElevationRange = (roof, roofBounds) => {
    // For N/S views: horizontal position is X coordinate
    // For E/W views: horizontal position is Y coordinate
    if (isHorizontal) {
      return { start: roofBounds.minX, end: roofBounds.maxX };
    } else {
      return { start: roofBounds.minY, end: roofBounds.maxY };
    }
  };

  // Process all roofs and build enhanced roof info array
  // Calculate the range along the elevation axis (the direction we see in the elevation view)
  // For N/S views: we see along X axis, so filter roofs that overlap the X range
  // For E/W views: we see along Y axis, so filter roofs that overlap the Y range
  let wallParallelMin = Infinity, wallParallelMax = -Infinity;
  elevationWalls.forEach(wall => {
    if (isHorizontal) {
      // N/S views - walls run horizontally (along X), we see X range
      wallParallelMin = Math.min(wallParallelMin, wall.start.x, wall.end.x);
      wallParallelMax = Math.max(wallParallelMax, wall.start.x, wall.end.x);
    } else {
      // E/W views - walls run vertically (along Y), we see Y range
      wallParallelMin = Math.min(wallParallelMin, wall.start.y, wall.end.y);
      wallParallelMax = Math.max(wallParallelMax, wall.start.y, wall.end.y);
    }
  });
  // Add tolerance for roof overhang
  const parallelTolerance = 100;
  wallParallelMin -= parallelTolerance;
  wallParallelMax += parallelTolerance;

  const allRoofs = (roofs || []).filter(r => r.type === 'gable' || r.type === 'hip' || r.type === 'shed');

  // Filter roofs to only those that overlap with the elevation view's visible range
  // A roof is visible if its footprint overlaps with the range we can see in this elevation
  const visibleRoofs = allRoofs.filter(roof => {
    const roofBounds = getRoofBounds(roof);
    if (isHorizontal) {
      // N/S views - check if roof's X range overlaps the visible X range
      return roofBounds.minX < wallParallelMax && roofBounds.maxX > wallParallelMin;
    } else {
      // E/W views - check if roof's Y range overlaps the visible Y range
      return roofBounds.minY < wallParallelMax && roofBounds.maxY > wallParallelMin;
    }
  });

  const processedRoofs = visibleRoofs.map(roof => {
    const bounds = getRoofBounds(roof);
    const peakHeight = calcRoofPeakHeight(roof, bounds);
    const isGableEnd = isGableEndForRoof(roof);
    const elevRange = getRoofElevationRange(roof, bounds);
    // Use roof's baseHeight if specified, otherwise use default wall height (96")
    // Add floor elevation for multi-floor stacking
    const roofFloorElevation = roof.floorElevation || 0;
    const baseHeight = roof.baseHeight || 96;
    const plateHeight = baseHeight + roofFloorElevation;
    return {
      ...roof,
      bounds,
      peakHeight,
      isGableEnd,
      elevRange,
      overhang: roof.overhang || 12,
      plateHeight, // Height from ground where eave sits (in inches, includes floor elevation)
      roofFloorElevation, // Store original floor elevation for reference
    };
  });

  // Sort roofs for proper drawing order based on depth (distance from viewer)
  // Roofs further from the viewer should draw first so closer roofs occlude them
  // Also consider gable END vs SIDE - END views are typically more prominent
  processedRoofs.sort((a, b) => {
    // Calculate depth position for each roof (perpendicular to elevation view)
    // For N/S views: depth is Y position (north view looks south, so higher Y = further)
    // For E/W views: depth is X position (west view looks east, so higher X = further)
    const getDepth = (roof) => {
      if (isHorizontal) {
        // N/S views - depth is Y
        return elevation === 'north'
          ? roof.bounds.maxY  // North view: further south (higher Y) = further away
          : roof.bounds.minY; // South view: further north (lower Y) = further away
      } else {
        // E/W views - depth is X
        return elevation === 'west'
          ? roof.bounds.maxX  // West view: further east (higher X) = further away
          : roof.bounds.minX; // East view: further west (lower X) = further away
      }
    };

    const depthA = getDepth(a);
    const depthB = getDepth(b);

    // Draw further roofs first (they're behind)
    if (Math.abs(depthA - depthB) > 20) {
      return elevation === 'north' || elevation === 'west'
        ? depthB - depthA  // Higher value = further away, draw first
        : depthA - depthB; // Lower value = further away, draw first
    }

    // If same depth, END views draw after SIDE views
    if (a.isGableEnd !== b.isGableEnd) {
      return a.isGableEnd ? 1 : -1;
    }

    // Within same type and depth, sort by position
    return a.elevRange.start - b.elevRange.start;
  });

  // For backward compatibility, keep relevantRoof as the first/primary roof
  const relevantRoof = processedRoofs[0] || null;

  // Calculate overall roof peak height (max of all roofs) for scaling
  roofPeakHeight = processedRoofs.reduce((max, r) => Math.max(max, r.peakHeight), 0);
  roofOverhang = processedRoofs.reduce((max, r) => Math.max(max, r.overhang), 12);

  if (relevantRoof) {
    const pitchDef = ROOF_PITCHES[relevantRoof.pitch] || ROOF_PITCHES['6:12'];
    roofOverhang = (relevantRoof.overhang || 12);

    // Get roof dimensions - handle both points array and legacy x/y/width/height format
    let roofPoints;
    if (relevantRoof.points && relevantRoof.points.length >= 3) {
      roofPoints = relevantRoof.points;
    } else if (typeof relevantRoof.x === 'number' && typeof relevantRoof.width === 'number') {
      // Legacy format with explicit coordinates
      roofPoints = [
        { x: relevantRoof.x, y: relevantRoof.y },
        { x: relevantRoof.x + relevantRoof.width, y: relevantRoof.y },
        { x: relevantRoof.x + relevantRoof.width, y: relevantRoof.y + relevantRoof.height },
        { x: relevantRoof.x, y: relevantRoof.y + relevantRoof.height },
      ];
    } else {
      // Fallback: try to infer from wall bounds
      roofPoints = [
        { x: allMinX, y: allMinY },
        { x: allMaxX, y: allMinY },
        { x: allMaxX, y: allMaxY },
        { x: allMinX, y: allMaxY },
      ];
    }
    const roofMinX = Math.min(...roofPoints.map(p => p.x));
    const roofMaxX = Math.max(...roofPoints.map(p => p.x));
    const roofMinY = Math.min(...roofPoints.map(p => p.y));
    const roofMaxY = Math.max(...roofPoints.map(p => p.y));
    const roofWidth = roofMaxX - roofMinX;
    const roofHeight = roofMaxY - roofMinY;

    // Calculate peak based on roof type and viewing direction
    // ridgeDirection tells us which way the ridge runs:
    // - 'horizontal': ridge runs E-W (left-right on plan), gable ends face E/W
    //   -> North/South views see SIDE (flat eave) - looking across the slope
    //   -> East/West views see GABLE END (triangle) - looking at the end
    // - 'vertical': ridge runs N-S (up-down on plan), gable ends face N/S
    //   -> East/West views see SIDE (flat eave) - looking across the slope
    //   -> North/South views see GABLE END (triangle) - looking at the end
    const ridgeDir = relevantRoof.ridgeDirection || 'horizontal';

    // Calculate peak for this roof but preserve max from all processed roofs
    // (roofPeakHeight was already set to the max from processedRoofs.reduce above)
    if (relevantRoof.type === 'gable') {
      // Gable ends are perpendicular to the ridge direction
      // - 'horizontal' ridge: runs E-W, gable ends face N/S, slopes face E/W
      // - 'vertical' ridge: runs N-S, gable ends face E/W, slopes face N/S
      // Always calculate peak height - it's the same for both END and SIDE views
      // The run is perpendicular to the ridge (half the span across the slope)
      // For vertical ridge (N-S), run is based on E-W dimension (roofWidth)
      // For horizontal ridge (E-W), run is based on N-S dimension (roofHeight)
      const runDim = ridgeDir === 'vertical' ? roofWidth : roofHeight;
      const runInches = runDim * (6 / GRID_SIZE) / 2;
      const calculatedPeak = runInches * (pitchDef.rise / pitchDef.run);
      roofPeakHeight = Math.max(roofPeakHeight, calculatedPeak);
    } else if (relevantRoof.type === 'hip') {
      // Hip roof - always see trapezoid from any side
      const perpDim = isHorizontal ? roofHeight : roofWidth;
      const runInches = (perpDim / 2) * (6 / GRID_SIZE);
      const calculatedPeak = runInches * (pitchDef.rise / pitchDef.run);
      roofPeakHeight = Math.max(roofPeakHeight, calculatedPeak);
    } else if (relevantRoof.type === 'shed') {
      const shedEdge = relevantRoof.shedSlopeEdge || 'north';
      const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';

      // Calculate peak height based on the slope run dimension
      // The run dimension is perpendicular to the slope edge:
      // - For N/S slope (slopeRunsNS): run is the N-S dimension (roofHeight in plan view)
      // - For E/W slope (slopeRunsEW): run is the E-W dimension (roofWidth in plan view)
      const runDim = slopeRunsNS ? roofHeight : roofWidth;
      const fullShedPeakHeight = (runDim * (6 / GRID_SIZE)) * (pitchDef.rise / pitchDef.run);

      // Use max of existing (from all roofs) and this calculation
      roofPeakHeight = Math.max(roofPeakHeight, fullShedPeakHeight);
    }
  }

  // Determine if we're viewing the high side of a shed roof (wall extends above roofBaseY)
  // This is used to skip drawing the wall top line at roofBaseY
  let isShedHighSideView = false;
  let isShedSlopeView = false;
  if (relevantRoof?.type === 'shed') {
    const shedEdge = relevantRoof.shedSlopeEdge || 'north';
    const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
    const slopeRunsEW = shedEdge === 'east' || shedEdge === 'west';
    const isEndView = (slopeRunsNS && isHorizontal) || (slopeRunsEW && !isHorizontal);
    // High side view: we're looking at the end view but NOT at the low side (shedEdge)
    if (isEndView && elevation !== shedEdge) {
      isShedHighSideView = true;
    }
    // Slope view: E/W views when slope runs N-S, N/S views when slope runs E-W
    if (!isEndView) {
      isShedSlopeView = true;
    }
  }

  // Determine if we're viewing the gable END of a gable roof
  // In gable end view, the wall extends up into the gable triangle
  // Gable ENDs are perpendicular to the ridge direction:
  // - Horizontal ridge (E-W): ENDs face East and West
  // - Vertical ridge (N-S): ENDs face North and South
  let isGableEndView = false;
  if (relevantRoof?.type === 'gable') {
    const ridgeDir = relevantRoof.ridgeDirection || 'horizontal';
    // North/South elevations (isHorizontal=true) see gable END when ridge is vertical (N-S)
    // East/West elevations (isHorizontal=false) see gable END when ridge is horizontal (E-W)
    isGableEndView = (isHorizontal && ridgeDir === 'vertical') || (!isHorizontal && ridgeDir === 'horizontal');
  }

  // Calculate scale - either fixed from viewport or auto-fit
  // Model width should encompass both walls AND all visible roofs
  let modelMinPos = minPos;
  let modelMaxPos = maxPos;

  // Expand model bounds to include all roof positions along the elevation axis
  processedRoofs.forEach(roof => {
    modelMinPos = Math.min(modelMinPos, roof.elevRange.start);
    modelMaxPos = Math.max(modelMaxPos, roof.elevRange.end);
  });

  // Posts too, so a post standing off the end of the wall is not cropped away
  (posts || []).forEach(post => {
    const axisPos = isHorizontal ? post.x : post.y;
    modelMinPos = Math.min(modelMinPos, axisPos);
    modelMaxPos = Math.max(modelMaxPos, axisPos);
  });

  const modelWidthInches = (modelMaxPos - modelMinPos) * (6 / GRID_SIZE) + roofOverhang * 2;
  // Model height should include the highest point (max of wall heights OR roof plate heights + peaks)
  const maxRoofPlateHeight = processedRoofs.reduce((max, r) => Math.max(max, r.plateHeight || 96), 0);
  const effectiveMaxHeight = Math.max(wallHeightInches, maxRoofPlateHeight);
  const modelHeightInches = effectiveMaxHeight + foundationHeight + roofPeakHeight + 20;

  console.log(`Height calc: wallHeightInches=${wallHeightInches}, maxRoofPlateHeight=${maxRoofPlateHeight}, effectiveMaxHeight=${effectiveMaxHeight}, roofPeakHeight=${roofPeakHeight.toFixed(1)}, foundationHeight=${foundationHeight}, modelHeightInches=${modelHeightInches.toFixed(1)}`);

  // Reserve space for level line extensions (markers, labels, grip points)
  const levelLineReservedSpace = showLevelLines ? 150 : 0;

  let elevScale;
  let actualScaleLabel = bounds.scale || '1/4" = 1\'';

  if (useFixedScale && bounds.scale && ELEVATION_SCALE_MAP[bounds.scale]) {
    // Use fixed scale from viewport setting
    elevScale = ELEVATION_SCALE_MAP[bounds.scale];
  } else {
    // Auto-fit to bounds - reserve space for level lines on the right
    const availWidth = bounds.width - marginX * 2 - levelLineReservedSpace;
    const availHeight = bounds.height - marginY * 2;
    const scaleX = availWidth / modelWidthInches;
    const scaleY = availHeight / modelHeightInches;
    elevScale = Math.min(scaleX, scaleY);
    console.log(`Auto-fit: bounds=${bounds.width}x${bounds.height}, avail=${availWidth.toFixed(0)}x${availHeight.toFixed(0)}, scaleX=${scaleX.toFixed(3)}, scaleY=${scaleY.toFixed(3)}, elevScale=${elevScale.toFixed(3)}`);

    // Calculate what scale this corresponds to for display
    // elevScale = pixels per inch, so paper_inches_per_foot = elevScale * 12 / 96
    const paperInchesPerFoot = elevScale * 12 / 96;
    if (paperInchesPerFoot >= 0.875) actualScaleLabel = '1" = 1\'';
    else if (paperInchesPerFoot >= 0.4375) actualScaleLabel = '1/2" = 1\'';
    else if (paperInchesPerFoot >= 0.3125) actualScaleLabel = '3/8" = 1\'';
    else if (paperInchesPerFoot >= 0.1875) actualScaleLabel = '1/4" = 1\'';
    else actualScaleLabel = '1/8" = 1\'';
  }

  // Calculate drawing dimensions in pixels
  // Use the expanded model bounds that include all roofs
  const elevWidth = (modelMaxPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
  const drawWallHeight = wallHeightInches * elevScale;
  const drawStandardWallHeight = standardWallHeightInches * elevScale; // For depth transition lines
  const drawFoundationHeight = foundationHeight * elevScale;
  const drawRoofPeak = roofPeakHeight * elevScale;
  const drawOverhang = roofOverhang * elevScale;

  // Center in bounds (accounting for reserved level line space on right)
  const totalDrawWidth = elevWidth + drawOverhang * 2;
  const totalDrawHeight = drawFoundationHeight + drawWallHeight + drawRoofPeak;
  // Center only the building portion, leaving level line space on the right
  const availableForCentering = bounds.width - levelLineReservedSpace;
  const offsetX = bounds.x + (availableForCentering - totalDrawWidth) / 2 + drawOverhang;
  const groundY = bounds.y + bounds.height - marginY;

  // Determine if elevation needs to be mirrored (flipped horizontally)
  // North elevation: looking south, west should be on RIGHT (flip X)
  // South elevation: looking north, west should be on LEFT (no flip)
  // East elevation: looking west, north should be on RIGHT (flip X)
  // West elevation: looking east, north should be on LEFT (no flip)
  const flipX = (elevation === 'north' || elevation === 'east');

  // Calculate screen position for each processed roof
  // Helper to convert model position to screen X coordinate
  // Uses expanded model bounds to ensure all roofs fit
  const modelToScreenX = (modelPos) => {
    if (flipX) {
      return offsetX + (modelMaxPos - modelPos) * (6 / GRID_SIZE) * elevScale;
    } else {
      return offsetX + (modelPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
    }
  };

  // Screen extent of the WALL span, as opposed to the drawing's full extent.
  // minPos..maxPos covers only the walls; modelMinPos..modelMaxPos also covers the
  // roofs, and the two differ whenever a roof projects past the wall along this
  // elevation's horizontal axis - a porch or a carport off the end of the wall we
  // are looking at. Wall geometry must be placed with the same origin as the roofs
  // (modelToScreenX) or it drifts sideways by the size of that projection, and must
  // be drawn at its own width rather than the drawing's.
  const wallEdgeA = modelToScreenX(minPos);
  const wallEdgeB = modelToScreenX(maxPos);
  const wallLeftEdgeX = Math.min(wallEdgeA, wallEdgeB);
  const wallRightEdgeX = Math.max(wallEdgeA, wallEdgeB);
  const wallSpanWidth = wallRightEdgeX - wallLeftEdgeX;

  // Add screen coordinates to each processed roof
  processedRoofs.forEach(roof => {
    const screenStart = modelToScreenX(roof.elevRange.start);
    const screenEnd = modelToScreenX(roof.elevRange.end);
    // Handle flipped case where start > end
    roof.screenX = Math.min(screenStart, screenEnd);
    roof.screenWidth = Math.abs(screenEnd - screenStart);
    roof.drawPeakHeight = roof.peakHeight * elevScale;
    roof.drawOverhang = roof.overhang * elevScale;
  });

  // ========== ROOF VALLEY/RIDGE CONNECTION DETECTION ==========
  // Detect adjacent roofs that should have valley lines connecting them
  const roofConnections = [];
  const adjacencyTolerance = 50; // Grid units tolerance for detecting adjacent roofs

  console.log(`Checking roof adjacencies: ${processedRoofs.length} roofs`);
  for (let i = 0; i < processedRoofs.length; i++) {
    for (let j = i + 1; j < processedRoofs.length; j++) {
      const roof1 = processedRoofs[i];
      const roof2 = processedRoofs[j];

      // Check if roofs are adjacent (bounds touch or overlap slightly)
      const bounds1 = roof1.bounds;
      const bounds2 = roof2.bounds;

      console.log(`  Checking roof pair: ${roof1.type} vs ${roof2.type}`);
      console.log(`    Roof1 bounds: minX=${bounds1.minX}, maxX=${bounds1.maxX}, minY=${bounds1.minY}, maxY=${bounds1.maxY}`);
      console.log(`    Roof2 bounds: minX=${bounds2.minX}, maxX=${bounds2.maxX}, minY=${bounds2.minY}, maxY=${bounds2.maxY}`);
      console.log(`    Edge distances: E1-W2=${Math.abs(bounds1.maxX - bounds2.minX)}, W1-E2=${Math.abs(bounds1.minX - bounds2.maxX)}, S1-N2=${Math.abs(bounds1.maxY - bounds2.minY)}, N1-S2=${Math.abs(bounds1.minY - bounds2.maxY)}, tolerance=${adjacencyTolerance}`);

      // Check for shared edge (one roof's edge aligns with another's)
      let sharedEdge = null;

      // Check if roof1's east edge meets roof2's west edge
      if (Math.abs(bounds1.maxX - bounds2.minX) < adjacencyTolerance) {
        const yOverlap = Math.min(bounds1.maxY, bounds2.maxY) - Math.max(bounds1.minY, bounds2.minY);
        if (yOverlap > adjacencyTolerance) {
          sharedEdge = { edge1: 'east', edge2: 'west', axis: 'vertical' };
        }
      }
      // Check if roof1's west edge meets roof2's east edge
      else if (Math.abs(bounds1.minX - bounds2.maxX) < adjacencyTolerance) {
        const yOverlap = Math.min(bounds1.maxY, bounds2.maxY) - Math.max(bounds1.minY, bounds2.minY);
        console.log(`    W1-E2 check passed! yOverlap=${yOverlap}, tolerance=${adjacencyTolerance}`);
        if (yOverlap > adjacencyTolerance) {
          sharedEdge = { edge1: 'west', edge2: 'east', axis: 'vertical' };
          console.log(`    SHARED EDGE DETECTED: west-east`);
        }
      }
      // Check if roof1's south edge meets roof2's north edge
      else if (Math.abs(bounds1.maxY - bounds2.minY) < adjacencyTolerance) {
        const xOverlap = Math.min(bounds1.maxX, bounds2.maxX) - Math.max(bounds1.minX, bounds2.minX);
        if (xOverlap > adjacencyTolerance) {
          sharedEdge = { edge1: 'south', edge2: 'north', axis: 'horizontal' };
        }
      }
      // Check if roof1's north edge meets roof2's south edge
      else if (Math.abs(bounds1.minY - bounds2.maxY) < adjacencyTolerance) {
        const xOverlap = Math.min(bounds1.maxX, bounds2.maxX) - Math.max(bounds1.minX, bounds2.minX);
        if (xOverlap > adjacencyTolerance) {
          sharedEdge = { edge1: 'north', edge2: 'south', axis: 'horizontal' };
        }
      }

      console.log(`    sharedEdge result: ${sharedEdge ? JSON.stringify(sharedEdge) : 'null'}`);

      if (sharedEdge) {
        // Determine which roof is END view and which is SIDE view for this elevation
        const roof1IsEnd = roof1.isGableEnd;
        const roof2IsEnd = roof2.isGableEnd;

        // Valley is visible when one roof is END view and one is SIDE view (gable-gable case)
        const hasGableValley = roof1IsEnd !== roof2IsEnd;

        // Also check for shed + gable intersection
        // When a gable END view meets a shed SLOPE view, there's a valley
        const roof1IsShed = roof1.type === 'shed';
        const roof2IsShed = roof2.type === 'shed';
        const roof1IsGable = roof1.type === 'gable';
        const roof2IsGable = roof2.type === 'gable';

        console.log(`    Shed-gable check: roof1IsShed=${roof1IsShed}, roof2IsShed=${roof2IsShed}, roof1IsGable=${roof1IsGable}, roof2IsGable=${roof2IsGable}, roof1IsEnd=${roof1IsEnd}, roof2IsEnd=${roof2IsEnd}`);

        // Check if one is a shed (slope view) and the other is a gable
        // For shed-gable valleys, we need the shed to be in SLOPE view
        // The gable can be in either END or SIDE view - if their slopes meet, there's a valley
        let hasShedGableValley = false;
        let shedRoof = null;
        let gableRoof = null;
        let isGableSideView = false;

        if (roof1IsShed && roof2IsGable) {
          // roof1 is shed, roof2 is gable
          const shedEdge = roof1.shedSlopeEdge || 'north';
          const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
          const slopeRunsEW = shedEdge === 'east' || shedEdge === 'west';
          const isShedSlopeView = (slopeRunsNS && !isHorizontal) || (slopeRunsEW && isHorizontal);
          const isGableEndView = roof2.isGableEnd;
          const isGableSide = !isGableEndView;
          console.log(`    Shed slope check: shedEdge=${shedEdge}, slopeRunsNS=${slopeRunsNS}, slopeRunsEW=${slopeRunsEW}, isHorizontal=${isHorizontal}, isShedSlopeView=${isShedSlopeView}, isGableEndView=${isGableEndView}, isGableSide=${isGableSide}`);
          // Valley/ridge connection forms when shed is in slope view AND gable is adjacent
          // - If gable is in END view: valley line where gable slope meets shed
          // - If gable is in SIDE view: ridge line extends to meet shed slope
          if (isShedSlopeView) {
            hasShedGableValley = true;
            shedRoof = roof1;
            gableRoof = roof2;
            isGableSideView = isGableSide;
            console.log(`    Shed-gable connection DETECTED: shed slope view meets gable ${isGableEndView ? 'END' : 'SIDE'} view`);
          }
        } else if (roof2IsShed && roof1IsGable) {
          // roof2 is shed, roof1 is gable
          const shedEdge = roof2.shedSlopeEdge || 'north';
          const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
          const slopeRunsEW = shedEdge === 'east' || shedEdge === 'west';
          const isShedSlopeView = (slopeRunsNS && !isHorizontal) || (slopeRunsEW && isHorizontal);
          const isGableEndView = roof1.isGableEnd;
          const isGableSide = !isGableEndView;
          console.log(`    Shed slope check: shedEdge=${shedEdge}, slopeRunsNS=${slopeRunsNS}, slopeRunsEW=${slopeRunsEW}, isHorizontal=${isHorizontal}, isShedSlopeView=${isShedSlopeView}, isGableEndView=${isGableEndView}, isGableSide=${isGableSide}`);
          // Valley/ridge connection forms when shed is in slope view AND gable is adjacent
          if (isShedSlopeView) {
            hasShedGableValley = true;
            shedRoof = roof2;
            gableRoof = roof1;
            isGableSideView = isGableSide;
            console.log(`    Shed-gable connection DETECTED: shed slope view meets gable ${isGableEndView ? 'END' : 'SIDE'} view`);
          }
        }

        if (hasGableValley) {
          const endRoof = roof1IsEnd ? roof1 : roof2;
          const sideRoof = roof1IsEnd ? roof2 : roof1;

          const connection = {
            endRoof,
            sideRoof,
            sharedEdge,
            roof1,
            roof2,
            connectionType: 'gable-gable',
          };
          roofConnections.push(connection);

          // Store valley connection on the END roof for use during drawing
          if (!endRoof.valleyConnections) {
            endRoof.valleyConnections = [];
          }
          endRoof.valleyConnections.push(connection);

          // Also store on the SIDE roof so it can draw its connection to the valley
          if (!sideRoof.valleyConnections) {
            sideRoof.valleyConnections = [];
          }
          sideRoof.valleyConnections.push(connection);

          console.log(`Roof connection found: END roof (${endRoof.isGableEnd ? 'gable' : 'side'}) meets SIDE roof, shared edge: ${sharedEdge.edge1}/${sharedEdge.edge2}`);
        }

        if (hasShedGableValley && shedRoof && gableRoof) {
          const connection = {
            shedRoof,
            gableRoof,
            endRoof: gableRoof,  // For compatibility
            sideRoof: shedRoof,  // For compatibility
            sharedEdge,
            roof1,
            roof2,
            connectionType: 'shed-gable',
            isGableSideView,  // true if gable is in SIDE view (ridge visible), false if END view (gable triangle)
          };
          roofConnections.push(connection);

          // Store on gable roof
          if (!gableRoof.valleyConnections) {
            gableRoof.valleyConnections = [];
          }
          gableRoof.valleyConnections.push(connection);

          // Store on shed roof
          if (!shedRoof.valleyConnections) {
            shedRoof.valleyConnections = [];
          }
          shedRoof.valleyConnections.push(connection);

          console.log(`Shed-Gable connection found: shed (slope view) meets gable (${isGableSideView ? 'SIDE' : 'END'} view), shared edge: ${sharedEdge.edge1}/${sharedEdge.edge2}`);
        }
      }
    }
  }

  // Calculate valley line geometry for each connection
  // roofBaseY will be set later, so we calculate relative positions and finalize during drawing
  roofConnections.forEach(connection => {
    const { endRoof, sideRoof, sharedEdge } = connection;

    // For N/S elevations (isHorizontal=true), the END roof shows as gable triangle
    // For E/W elevations (isHorizontal=false), depends on ridge direction
    // Valley is where gable's sloped edge meets the side roof's flat top

    // Determine which slope of the gable meets the side roof
    // Use SCREEN coordinates (already calculated), not model bounds
    // The slope that faces the side roof is determined by their relative screen positions
    let gableSlopeDirection; // 'left' or 'right' - which slope of the gable meets the side roof

    // Calculate screen center positions
    const endScreenCenterX = endRoof.screenX + endRoof.screenWidth / 2;
    const sideScreenCenterX = sideRoof.screenX + sideRoof.screenWidth / 2;

    // If side roof center is to the right of end roof center in screen coords, use right slope
    // Screen coordinates are already flipped for North view, so this works for all views
    if (sideScreenCenterX > endScreenCenterX) {
      gableSlopeDirection = 'right';
    } else {
      gableSlopeDirection = 'left';
    }

    // Store the slope direction for use during drawing
    connection.gableSlopeDirection = gableSlopeDirection;

    // Calculate the intersection point in screen coordinates
    // Gable peak is at: (endRoof.screenX + endRoof.screenWidth/2, roofBaseY - endRoof.drawPeakHeight)
    // Gable corner is at: left=(endRoof.screenX, roofBaseY) or right=(endRoof.screenX + endRoof.screenWidth, roofBaseY)
    // Side roof top is at: y = roofBaseY - sideRoof.drawPeakHeight (the visible depth of the side roof)

    const peakX = endRoof.screenX + endRoof.screenWidth / 2;
    const cornerX = gableSlopeDirection === 'left' ? endRoof.screenX : endRoof.screenX + endRoof.screenWidth;

    // The intersection calculation needs roofBaseY which is set later
    // Store the parameters needed for final calculation
    connection.peakX = peakX;
    connection.cornerX = cornerX;
    connection.endRoofPeakHeight = endRoof.drawPeakHeight;
    connection.sideRoofTopHeight = sideRoof.drawPeakHeight || (8 * elevScale); // Default fascia height if no peak

    console.log(`  Valley geometry: gable slope=${gableSlopeDirection}, endScreenCenter=${endScreenCenterX.toFixed(1)}, sideScreenCenter=${sideScreenCenterX.toFixed(1)}, peakX=${peakX.toFixed(1)}, cornerX=${cornerX.toFixed(1)}`);
  });

  ctx.save();

  // Ground/grade level and floor level
  const gradeY = groundY;
  const floorY = gradeY - drawFoundationHeight;

  // Draw ground line
  ctx.strokeStyle = foundationStyle.stroke;
  ctx.lineWidth = 1.5;
  if (isSketchyMode) {
    drawSketchyLine(ctx, offsetX - drawOverhang - 10, gradeY, offsetX + elevWidth + drawOverhang + 10, gradeY);
  } else {
    ctx.beginPath();
    ctx.moveTo(offsetX - drawOverhang - 10, gradeY);
    ctx.lineTo(offsetX + elevWidth + drawOverhang + 10, gradeY);
    ctx.stroke();
  }

  // Draw foundation/block ABOVE ground line (only if showFoundation is enabled)
  if (showFoundation && drawFoundationHeight > 0) {
    ctx.fillStyle = foundationStyle.fill;
    ctx.fillRect(wallLeftEdgeX, floorY, wallSpanWidth, drawFoundationHeight);
    ctx.strokeStyle = foundationStyle.stroke;
    ctx.lineWidth = foundationStyle.lineWidth;
    if (isSketchyMode) {
      drawSketchyRect(ctx, wallLeftEdgeX, floorY, wallSpanWidth, drawFoundationHeight);
    } else {
      ctx.strokeRect(wallLeftEdgeX, floorY, wallSpanWidth, drawFoundationHeight);
    }

    // Foundation block pattern (CMU blocks - 8" tall x 16" wide)
    ctx.strokeStyle = foundationStyle.patternColor;
    ctx.lineWidth = 0.5;
    const blockH = 8 * elevScale; // 8" tall blocks
    const blockW = 16 * elevScale; // 16" wide blocks

    // Horizontal mortar lines
    for (let by = floorY + blockH; by < gradeY; by += blockH) {
      ctx.beginPath();
      ctx.moveTo(wallLeftEdgeX, by);
      ctx.lineTo(wallRightEdgeX, by);
      ctx.stroke();
    }

    // Vertical block joints - staggered pattern (offset every other row)
    let rowIndex = 0;
    for (let by = floorY; by < gradeY; by += blockH) {
      const rowOffset = (rowIndex % 2) * (blockW / 2); // Stagger every other row
      for (let bx = wallLeftEdgeX + rowOffset + blockW; bx < wallRightEdgeX; bx += blockW) {
        ctx.beginPath();
        ctx.moveTo(bx, by);
        ctx.lineTo(bx, Math.min(by + blockH, gradeY));
        ctx.stroke();
      }
      rowIndex++;
    }
  }

  // Draw wall ABOVE foundation - with stepped profile for different wall heights
  ctx.fillStyle = wallStyle.fill;

  // Sort wall segments by position for stepped rendering
  const sortedWallSegments = [...wallHeightMap].sort((a, b) => a.startPos - b.startPos);

  // Merge adjacent walls at the same height into segments
  // For siding, we use ALL walls and take MINIMUM height at each position
  const wallSegments = [];

  // Helper to add wall info to a segments array
  const addToSegments = (segmentsArray, wallInfo) => {
    // Convert model positions to screen coordinates
    let screenStart, screenEnd;
    if (flipX) {
      screenStart = offsetX + (modelMaxPos - wallInfo.endPos) * (6 / GRID_SIZE) * elevScale;
      screenEnd = offsetX + (modelMaxPos - wallInfo.startPos) * (6 / GRID_SIZE) * elevScale;
    } else {
      screenStart = offsetX + (wallInfo.startPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
      screenEnd = offsetX + (wallInfo.endPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
    }

    const segHeight = wallInfo.height;
    const segFloorElevation = wallInfo.floorElevation || 0;
    const isPostFrame = wallInfo.wall?.type?.startsWith('post-');
    const lastSeg = segmentsArray[segmentsArray.length - 1];

    // Merge with previous segment if same height, floor elevation, wall type category, and adjacent
    if (lastSeg && lastSeg.height === segHeight && lastSeg.floorElevation === segFloorElevation && lastSeg.isPostFrame === isPostFrame && Math.abs(lastSeg.screenEnd - screenStart) < 5) {
      lastSeg.screenEnd = Math.max(lastSeg.screenEnd, screenEnd);
    } else {
      segmentsArray.push({
        screenStart: Math.min(screenStart, screenEnd),
        screenEnd: Math.max(screenStart, screenEnd),
        height: segHeight,
        drawHeight: segHeight * elevScale,
        floorElevation: segFloorElevation,
        drawFloorElevation: segFloorElevation * elevScale,
        isPostFrame,
      });
    }
  };

  // Populate wallSegments from ALL walls
  sortedWallSegments.forEach(wallInfo => addToSegments(wallSegments, wallInfo));

  // If no segments, fall back to default wall
  if (wallSegments.length === 0) {
    ctx.fillRect(wallLeftEdgeX, floorY - drawWallHeight, wallSpanWidth, drawWallHeight);
  } else {
    // Sort segments by screen position for proper step line detection
    wallSegments.sort((a, b) => a.screenStart - b.screenStart);
    console.log(`Wall segments for ${elevation} elevation (sorted):`, wallSegments.map(s =>
      `[${s.screenStart.toFixed(1)}-${s.screenEnd.toFixed(1)}, h=${s.height}]`
    ).join(', '));

    // Draw each wall segment at its height (accounting for floor elevation for multi-floor)
    wallSegments.forEach((segment, idx) => {
      // Floor elevation shifts the wall up from grade
      const segFloorOffset = segment.drawFloorElevation || 0;
      const segTop = floorY - segment.drawHeight - segFloorOffset;
      const segWidth = segment.screenEnd - segment.screenStart;

      // Draw wall fill for this segment (metal gray for post-frame, standard for others)
      if (segment.isPostFrame) {
        ctx.save();
        ctx.fillStyle = renderMode === 'blueprint' ? '#1a2a3a' : '#c8cdd2';
        ctx.fillRect(segment.screenStart, segTop, segWidth, segment.drawHeight);
        ctx.restore();
      } else {
        ctx.fillRect(segment.screenStart, segTop, segWidth, segment.drawHeight);
      }

      // Draw vertical step line if height changes from previous segment
      if (idx > 0) {
        const prevSeg = wallSegments[idx - 1];
        if (Math.abs(prevSeg.height - segment.height) > 1) {
          const stepX = segment.screenStart;
          const lowerTop = floorY - Math.min(prevSeg.drawHeight, segment.drawHeight);
          const higherTop = floorY - Math.max(prevSeg.drawHeight, segment.drawHeight);

          // Skip step lines at the outermost building edges (only draw interior transitions)
          const sortedSegs = [...wallSegments].sort((a, b) => a.screenStart - b.screenStart);
          const leftmostX = sortedSegs[0].screenStart;
          const rightmostX = sortedSegs[sortedSegs.length - 1].screenEnd;
          const edgeTolerance = 5;
          if (Math.abs(stepX - leftmostX) < edgeTolerance || Math.abs(stepX - rightmostX) < edgeTolerance) {
            // This is at the building perimeter, not an interior transition - skip
            console.log(`Skipping step line at x=${stepX.toFixed(1)} - at building perimeter`);
            return; // continue to next segment in forEach
          }

          // Check if this step position is at a shed roof's boundary (where it meets a gable)
          // If so, we need to draw only the portion of the step line that's above the shed roof
          let shedRoofAtBoundary = null;
          const tolerance = 5; // Increased tolerance for boundary detection

          for (const roof of processedRoofs) {
            if (roof.type !== 'shed') continue;
            // Check if this roof is in slope view for this elevation
            const shedEdge = roof.shedSlopeEdge || 'north';
            const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
            const slopeRunsEW = shedEdge === 'east' || shedEdge === 'west';
            const roofIsSlopeView = (slopeRunsNS && !isHorizontal) || (slopeRunsEW && isHorizontal);

            // Check if stepX is at either edge of this shed roof
            const shedLeft = roof.screenX;
            const shedRight = roof.screenX + roof.screenWidth;

            console.log(`  Checking shed: shedLeft=${shedLeft.toFixed(1)}, shedRight=${shedRight.toFixed(1)}, shedEdge=${shedEdge}, roofIsSlopeView=${roofIsSlopeView}, leftDist=${Math.abs(stepX - shedLeft).toFixed(1)}, rightDist=${Math.abs(stepX - shedRight).toFixed(1)}`);

            if (!roofIsSlopeView) continue;

            // Check if step is at left or right edge of this shed
            const isAtShedEdge = Math.abs(stepX - shedLeft) < tolerance || Math.abs(stepX - shedRight) < tolerance;

            if (isAtShedEdge) {
              shedRoofAtBoundary = roof;
              console.log(`  Found shed at stepX=${stepX.toFixed(1)}: shedLeft=${shedLeft.toFixed(1)}, shedRight=${shedRight.toFixed(1)}, shedEdge=${shedEdge}`);
              break;
            }
          }

          console.log(`Step line check: stepX=${stepX.toFixed(1)}, prevHeight=${prevSeg.height}, currHeight=${segment.height}, shedAtBoundary=${!!shedRoofAtBoundary}`);

          // Draw step line with consistent weight
          ctx.strokeStyle = wallStyle.stroke;
          ctx.lineWidth = wallStyle.lineWidth;
          ctx.beginPath();

          if (shedRoofAtBoundary) {
            // Use the shed's own plate height to find where its surface is
            const shedPlateHeight = shedRoofAtBoundary.plateHeight || 96;
            const shedBaseY = floorY - (shedPlateHeight * elevScale);
            const shedPeakHeight = shedRoofAtBoundary.drawPeakHeight || 0;

            // The shed roof surface at the peak (inner edge where step line is)
            const fasciaOffset = 8;
            const shedPeakY = shedBaseY - shedPeakHeight + fasciaOffset;

            // Draw from shed roof peak to the taller wall top
            if (shedPeakY > higherTop) {
              console.log(`  Drawing partial step line at x=${stepX.toFixed(1)} from shedPeakY=${shedPeakY.toFixed(1)} to higherTop=${higherTop.toFixed(1)}`);
              ctx.moveTo(stepX, shedPeakY);
              ctx.lineTo(stepX, higherTop);
              ctx.stroke();
            }
          } else {
            // No shed roof at boundary, draw the full step line
            console.log(`  Drawing full step line at x=${stepX.toFixed(1)} from y=${lowerTop.toFixed(1)} to y=${higherTop.toFixed(1)}`);
            ctx.moveTo(stepX, lowerTop);
            ctx.lineTo(stepX, higherTop);
            ctx.stroke();
          }
        }
      }
    });
  }

  // ========== DRAW WALL RETURNS FOR SETBACK WALLS ==========
  // Find where the building depth changes along the elevation axis
  // by looking at ALL facing walls at different depths (not just the filtered outermost ones)
  // This is important because setback walls get filtered out by the "outermost" logic,
  // but we still need to detect where they transition to draw the wall return line.

  // Use ALL oriented walls (facing walls) for depth transition detection
  const allFacingWallDepthInfo = orientedWalls.map(wall => {
    const wallMinPos = isHorizontal
      ? Math.min(wall.start.x, wall.end.x)
      : Math.min(wall.start.y, wall.end.y);
    const wallMaxPos = isHorizontal
      ? Math.max(wall.start.x, wall.end.x)
      : Math.max(wall.start.y, wall.end.y);
    const wallDepth = isHorizontal
      ? (wall.start.y + wall.end.y) / 2
      : (wall.start.x + wall.end.x) / 2;
    return { wall, minPos: wallMinPos, maxPos: wallMaxPos, depth: wallDepth };
  });

  // Sort by position along elevation axis
  allFacingWallDepthInfo.sort((a, b) => a.minPos - b.minPos);

  console.log(`Wall return detection: ${allFacingWallDepthInfo.length} facing walls for ${elevation} elevation`);
  allFacingWallDepthInfo.forEach((info, idx) => {
    console.log(`  Wall ${idx}: depth=${info.depth.toFixed(1)}, pos=[${info.minPos.toFixed(1)}, ${info.maxPos.toFixed(1)}]`);
  });



  // Find depth transitions by looking at perpendicular walls
  const depthTolerance = 20;
  ctx.strokeStyle = wallStyle.stroke;
  ctx.lineWidth = wallStyle.lineWidth;

  // Track wall depth transitions for roof occlusion
  const wallDepthTransitions = [];

  // Method 1: Check ALL facing walls for depth changes
  // We use allFacingWallDepthInfo (not just filtered elevationWalls) to detect setbacks
  // Look for walls at different depths where one wall's start/end falls within another wall's range
  const gapTolerance = 50;

  // For each pair of walls at different depths, check if one wall's start or end
  // falls within the range of the other wall - this indicates a depth transition
  for (let i = 0; i < allFacingWallDepthInfo.length; i++) {
    const wallA = allFacingWallDepthInfo[i];

    for (let j = i + 1; j < allFacingWallDepthInfo.length; j++) {
      const wallB = allFacingWallDepthInfo[j];

      // Skip if depths are similar (same plane)
      if (Math.abs(wallA.depth - wallB.depth) < depthTolerance) continue;

      // Determine which wall is closer to viewer
      let aIsCloser;
      if (isHorizontal) {
        aIsCloser = elevation === 'north'
          ? wallA.depth < wallB.depth
          : wallA.depth > wallB.depth;
      } else {
        aIsCloser = elevation === 'west'
          ? wallA.depth < wallB.depth
          : wallA.depth > wallB.depth;
      }

      const closerWall = aIsCloser ? wallA : wallB;
      const furtherWall = aIsCloser ? wallB : wallA;

      // Check if the CLOSER wall starts inside the FURTHER wall's range
      // This means the closer wall "pops out" at this point
      if (closerWall.minPos > furtherWall.minPos + gapTolerance &&
          closerWall.minPos < furtherWall.maxPos - gapTolerance) {
        const transitionPos = closerWall.minPos;

        // Check if another wall at the same depth continues at this position
        // If so, the closer plane is continuous and there's no actual depth transition
        const continuingWall = allFacingWallDepthInfo.find(w =>
          w !== closerWall &&
          Math.abs(w.depth - closerWall.depth) < depthTolerance &&
          w.minPos <= transitionPos + gapTolerance &&
          w.maxPos >= transitionPos - gapTolerance
        );

        if (continuingWall) {
          console.log(`  Skipping depth transition at pos=${transitionPos.toFixed(1)} - wall at same depth continues`);
          continue;
        }

        const existingTransition = wallDepthTransitions.find(
          t => Math.abs(t.modelPos - transitionPos) < gapTolerance
        );
        if (!existingTransition) {
          let transitionX;
          if (flipX) {
            transitionX = offsetX + (modelMaxPos - transitionPos) * (6 / GRID_SIZE) * elevScale;
          } else {
            transitionX = offsetX + (transitionPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
          }

          console.log(`  Depth transition (closer wall start): closer depth=${closerWall.depth.toFixed(1)}, further depth=${furtherWall.depth.toFixed(1)}, pos=${transitionPos.toFixed(1)}, screenX=${transitionX.toFixed(1)}`);

          // Use standard wall height for depth transitions - don't extend above normal plate level
          ctx.beginPath();
          ctx.moveTo(transitionX, floorY - drawStandardWallHeight);
          ctx.lineTo(transitionX, gradeY);
          ctx.stroke();

          // In model space, closer wall is to the right of the transition
          // But when flipX=true, screen coords are mirrored, so invert
          let closerSideStart = 'right';
          if (flipX) {
            closerSideStart = 'left';
          }

          wallDepthTransitions.push({
            screenX: transitionX,
            modelPos: transitionPos,
            closerSide: closerSideStart,
            closerDepth: closerWall.depth,
            furtherDepth: furtherWall.depth,
          });
        }
      }

      // Check if the CLOSER wall ends inside the FURTHER wall's range
      // This means the closer wall "recedes" at this point
      if (closerWall.maxPos > furtherWall.minPos + gapTolerance &&
          closerWall.maxPos < furtherWall.maxPos - gapTolerance) {
        const transitionPos = closerWall.maxPos;

        // Check if another wall at the same depth continues at this position
        // If so, the closer plane is continuous and there's no actual depth transition
        const continuingWall = allFacingWallDepthInfo.find(w =>
          w !== closerWall &&
          Math.abs(w.depth - closerWall.depth) < depthTolerance &&
          w.minPos <= transitionPos + gapTolerance &&
          w.maxPos >= transitionPos - gapTolerance
        );

        if (continuingWall) {
          console.log(`  Skipping depth transition at pos=${transitionPos.toFixed(1)} - wall at same depth continues`);
          continue;
        }

        const existingTransition = wallDepthTransitions.find(
          t => Math.abs(t.modelPos - transitionPos) < gapTolerance
        );
        if (!existingTransition) {
          let transitionX;
          if (flipX) {
            transitionX = offsetX + (modelMaxPos - transitionPos) * (6 / GRID_SIZE) * elevScale;
          } else {
            transitionX = offsetX + (transitionPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
          }

          console.log(`  Depth transition (closer wall end): closer depth=${closerWall.depth.toFixed(1)}, further depth=${furtherWall.depth.toFixed(1)}, pos=${transitionPos.toFixed(1)}, screenX=${transitionX.toFixed(1)}`);

          // Use standard wall height for depth transitions - don't extend above normal plate level
          ctx.beginPath();
          ctx.moveTo(transitionX, floorY - drawStandardWallHeight);
          ctx.lineTo(transitionX, gradeY);
          ctx.stroke();

          // In model space, closer wall is to the left of the transition
          // But when flipX=true, screen coords are mirrored, so invert
          let closerSideEnd = 'left';
          if (flipX) {
            closerSideEnd = 'right';
          }

          wallDepthTransitions.push({
            screenX: transitionX,
            modelPos: transitionPos,
            closerSide: closerSideEnd,
            closerDepth: closerWall.depth,
            furtherDepth: furtherWall.depth,
          });
        }
      }
    }
  }

  // Also check for adjacent walls (original Method 1 logic)
  // This handles cases where two walls at different depths meet end-to-end
  for (let i = 0; i < allFacingWallDepthInfo.length; i++) {
    const current = allFacingWallDepthInfo[i];
    for (let j = 0; j < allFacingWallDepthInfo.length; j++) {
      if (i === j) continue;
      const other = allFacingWallDepthInfo[j];
      const gap = other.minPos - current.maxPos;
      if (gap >= -gapTolerance && gap <= gapTolerance) {
        if (Math.abs(current.depth - other.depth) > depthTolerance) {
          const transitionPos = (current.maxPos + other.minPos) / 2;
          const existingTransition = wallDepthTransitions.find(
            t => Math.abs(t.modelPos - transitionPos) < gapTolerance
          );
          if (existingTransition) continue;

          const currentIsCloser = isHorizontal
            ? (elevation === 'north' ? current.depth < other.depth : current.depth > other.depth)
            : (elevation === 'west' ? current.depth < other.depth : current.depth > other.depth);

          // Check if both walls are "outermost" for their respective positions
          // A wall return line is only visible if BOTH walls are at the front plane for their positions
          // If either wall has another wall in front of it, the transition is not visible

          // Check if CURRENT wall is outermost for its position
          const currentIsOutermost = !allFacingWallDepthInfo.some(w => {
            if (w === current || w === other) return false;
            const overlap = Math.max(0, Math.min(w.maxPos, current.maxPos) - Math.max(w.minPos, current.minPos));
            if (overlap < gapTolerance) return false;
            if (isHorizontal) {
              return elevation === 'north' ? w.depth < current.depth : w.depth > current.depth;
            } else {
              return elevation === 'west' ? w.depth < current.depth : w.depth > current.depth;
            }
          });

          // Check if OTHER wall is outermost for its position
          const otherIsOutermost = !allFacingWallDepthInfo.some(w => {
            if (w === other || w === current) return false;
            const overlap = Math.max(0, Math.min(w.maxPos, other.maxPos) - Math.max(w.minPos, other.minPos));
            if (overlap < gapTolerance) return false;
            if (isHorizontal) {
              return elevation === 'north' ? w.depth < other.depth : w.depth > other.depth;
            } else {
              return elevation === 'west' ? w.depth < other.depth : w.depth > other.depth;
            }
          });

          if (!currentIsOutermost || !otherIsOutermost) {
            console.log(`  Depth transition (adjacent walls): SKIPPING - wall not outermost. current depth=${current.depth.toFixed(1)} (outermost=${currentIsOutermost}), other depth=${other.depth.toFixed(1)} (outermost=${otherIsOutermost}), pos=${transitionPos.toFixed(1)}`);
            continue;
          }

          let transitionX;
          if (flipX) {
            transitionX = offsetX + (modelMaxPos - transitionPos) * (6 / GRID_SIZE) * elevScale;
          } else {
            transitionX = offsetX + (transitionPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
          }

          console.log(`  Depth transition (adjacent walls): current depth=${current.depth.toFixed(1)}, other depth=${other.depth.toFixed(1)}, pos=${transitionPos.toFixed(1)}, screenX=${transitionX.toFixed(1)}`);

          // Use standard wall height for depth transitions - don't extend above normal plate level
          ctx.beginPath();
          ctx.moveTo(transitionX, floorY - drawStandardWallHeight);
          ctx.lineTo(transitionX, gradeY);
          ctx.stroke();

          // Determine which side of the transition the closer wall is on
          // current wall ends here (on the left in model space), other wall starts here (on the right)
          // If current is closer, closer side is 'left'; if other is closer, closer side is 'right'
          // But when flipX=true (North, East), screen coordinates are mirrored
          let closerSide = currentIsCloser ? 'left' : 'right';
          if (flipX) {
            closerSide = closerSide === 'left' ? 'right' : 'left';
          }

          wallDepthTransitions.push({
            screenX: transitionX,
            modelPos: transitionPos,
            closerSide: closerSide,
            closerDepth: currentIsCloser ? current.depth : other.depth,
            furtherDepth: currentIsCloser ? other.depth : current.depth,
          });
        }
      }
    }
  }

  // Method 2: DISABLED for now - perpendicular wall detection needs more work
  // The issue is distinguishing between:
  // - Interior setback walls (should draw return line)
  // - Exterior boundary walls (should NOT draw return line)
  // For now, wall returns only work via Method 1 (facing walls at different depths)

  console.log(`Wall depth transitions found: ${wallDepthTransitions.length}`);

  // ========== BUILD OPENING RECTANGLES FOR CLIPPING ==========
  // Collect all door and window openings for this elevation
  const openings = [];

  // Collect door openings
  (doors || []).forEach(door => {
    if (!elevationWallIds.has(door.wallId)) return;
    const wall = walls.find(w => w.id === door.wallId);
    if (!wall) return;

    let doorPos;
    if (isHorizontal) {
      doorPos = wall.start.x + (wall.end.x - wall.start.x) * door.position;
    } else {
      doorPos = wall.start.y + (wall.end.y - wall.start.y) * door.position;
    }

    // Apply flip if needed
    let doorX;
    if (flipX) {
      doorX = offsetX + (modelMaxPos - doorPos) * (6 / GRID_SIZE) * elevScale;
    } else {
      doorX = offsetX + (doorPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
    }
    const doorW = (door.width || 36) * elevScale;
    const doorH = (door.height || 84) * elevScale;
    // Account for floor elevation (multi-floor stacking) for siding clip
    const doorFloorElevationClip = (door.floorElevation || 0) * elevScale;
    const doorY = floorY - doorH - doorFloorElevationClip;
    const trimWidth = detailedOpenings ? 4 * elevScale : 0;

    openings.push({
      type: 'door',
      x: doorX - doorW / 2 - trimWidth,
      y: doorY - (detailedOpenings ? 6 * elevScale : 0), // Head trim
      width: doorW + trimWidth * 2,
      height: doorH + (detailedOpenings ? 6 * elevScale : 0),
    });
  });

  // Collect window openings
  (windows || []).forEach(win => {
    if (!elevationWallIds.has(win.wallId)) return;
    const wall = walls.find(w => w.id === win.wallId);
    if (!wall) return;

    let winPos;
    if (isHorizontal) {
      winPos = wall.start.x + (wall.end.x - wall.start.x) * win.position;
    } else {
      winPos = wall.start.y + (wall.end.y - wall.start.y) * win.position;
    }

    // Apply flip if needed
    let winCenterX;
    if (flipX) {
      winCenterX = offsetX + (modelMaxPos - winPos) * (6 / GRID_SIZE) * elevScale;
    } else {
      winCenterX = offsetX + (winPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
    }
    const winW = (win.width || 36) * elevScale;
    const winH = (win.height || 48) * elevScale;
    const winSill = (win.sillHeight || 36) * elevScale;
    // Account for floor elevation (multi-floor stacking) for siding clip
    const winFloorElevationClip = (win.floorElevation || 0) * elevScale;
    const winY = floorY - winSill - winH - winFloorElevationClip;
    const winX = winCenterX - winW / 2;
    const trimWidth = detailedOpenings ? 4 * elevScale : 0;
    const headTrimExtra = detailedOpenings ? 2 * elevScale : 0;

    openings.push({
      type: 'window',
      x: winX - trimWidth,
      y: winY - trimWidth - headTrimExtra,
      width: winW + trimWidth * 2,
      height: winH + trimWidth + headTrimExtra + (detailedOpenings ? 6 : 0), // Include sill
    });
  });

  // Helper function to check if a horizontal line segment intersects any opening
  const getClippedSegments = (y, startX, endX) => {
    if (!clipPatterns || openings.length === 0) {
      return [{ start: startX, end: endX }];
    }

    // Find all openings that intersect this Y level
    const intersecting = openings.filter(op => y >= op.y && y <= op.y + op.height);

    if (intersecting.length === 0) {
      return [{ start: startX, end: endX }];
    }

    // Sort by X position
    intersecting.sort((a, b) => a.x - b.x);

    // Build segments that avoid openings
    const segments = [];
    let currentStart = startX;

    for (const op of intersecting) {
      if (op.x > currentStart) {
        // Add segment before this opening
        segments.push({ start: currentStart, end: Math.min(op.x, endX) });
      }
      // Move past this opening
      currentStart = Math.max(currentStart, op.x + op.width);
    }

    // Add final segment after last opening
    if (currentStart < endX) {
      segments.push({ start: currentStart, end: endX });
    }

    return segments;
  };

  // Helper function to check if a vertical line segment intersects any opening
  const getClippedVerticalSegments = (x, startY, endY) => {
    if (!clipPatterns || openings.length === 0) {
      return [{ start: startY, end: endY }];
    }

    // Find all openings that intersect this X position
    const intersecting = openings.filter(op => x >= op.x && x <= op.x + op.width);

    if (intersecting.length === 0) {
      return [{ start: startY, end: endY }];
    }

    // Sort by Y position (top to bottom, smaller Y first)
    intersecting.sort((a, b) => a.y - b.y);

    // Build segments that avoid openings (remember: smaller Y = higher on screen)
    const segments = [];
    let currentStart = startY;

    for (const op of intersecting) {
      if (op.y > currentStart) {
        // Add segment before this opening (above it)
        segments.push({ start: currentStart, end: Math.min(op.y, endY) });
      }
      // Move past this opening (below it)
      currentStart = Math.max(currentStart, op.y + op.height);
    }

    // Add final segment after last opening (below all openings)
    if (currentStart < endY) {
      segments.push({ start: currentStart, end: endY });
    }

    return segments;
  };

  // Draw siding pattern based on option
  ctx.strokeStyle = sidingStyle.stroke;
  ctx.lineWidth = sidingStyle.lineWidth;
  const sidingSpacing = 6 * elevScale;

  // For stepped walls, draw siding within each wall segment's height
  // This ensures siding respects the different wall heights
  const sidingTopY = floorY - drawWallHeight; // fallback for non-stepped walls

  // Helper to find wall height at a given X position
  // Uses ALL wallSegments and returns MAXIMUM height (tallest wall at each position)
  // The tallest segment determines the visible wall profile
  const getWallHeightAtX = (x) => {
    let maxHeight = 0;
    let found = false;

    for (const seg of wallSegments) {
      // Use larger tolerance for boundary detection to handle gaps between segments
      if (x >= seg.screenStart - 5 && x <= seg.screenEnd + 5) {
        found = true;
        if (seg.drawHeight > maxHeight) {
          maxHeight = seg.drawHeight;
        }
      }
    }

    // If no segment found with tolerance, find the nearest segment
    if (!found && wallSegments.length > 0) {
      let nearestSeg = wallSegments[0];
      let nearestDist = Infinity;
      for (const seg of wallSegments) {
        const distToStart = Math.abs(x - seg.screenStart);
        const distToEnd = Math.abs(x - seg.screenEnd);
        const dist = Math.min(distToStart, distToEnd);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestSeg = seg;
        }
      }
      return nearestSeg.drawHeight;
    }

    // Fallback to default if no segment found
    return found ? maxHeight : drawWallHeight;
  };

  // Draw metal siding for post-frame wall segments (vertical ribs like standing seam)
  const drawMetalSiding = (segment) => {
    const segFloorOffset = segment.drawFloorElevation || 0;
    const segBaseY = floorY - segFloorOffset;
    const segTopY = floorY - segment.drawHeight - segFloorOffset;
    const ribSpacing = 12 * elevScale; // 12" rib spacing (standing seam panels)

    ctx.save();
    ctx.strokeStyle = renderMode === 'blueprint' ? 'rgba(200,220,255,0.5)' : 'rgba(100,120,140,0.6)';
    ctx.lineWidth = sidingStyle.lineWidth * 0.8;

    // Vertical ribs
    for (let sx = segment.screenStart + ribSpacing; sx < segment.screenEnd; sx += ribSpacing) {
      ctx.beginPath();
      ctx.moveTo(sx, segBaseY);
      ctx.lineTo(sx, segTopY);
      ctx.stroke();
    }

    // Subtle horizontal seam lines (panel joints every ~3 feet)
    const seamSpacing = 36 * elevScale;
    ctx.setLineDash([3, 4]);
    ctx.lineWidth = sidingStyle.lineWidth * 0.5;
    for (let sy = segBaseY - seamSpacing; sy > segTopY; sy -= seamSpacing) {
      ctx.beginPath();
      ctx.moveTo(segment.screenStart, sy);
      ctx.lineTo(segment.screenEnd, sy);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.restore();
  };

  // Draw metal siding on all post-frame segments first
  wallSegments.forEach(segment => {
    if (segment.isPostFrame) {
      drawMetalSiding(segment);
    }
  });

  if (sidingPattern === 'horizontal') {
    // Horizontal lap siding - with opening clipping
    // If we have wall segments, draw siding per segment to respect different heights
    if (wallSegments.length > 0) {
      wallSegments.forEach(segment => {
        if (segment.isPostFrame) return; // already drawn as metal siding
        // Account for floor elevation (multi-floor stacking)
        const segFloorOffset = segment.drawFloorElevation || 0;
        const segBaseY = floorY - segFloorOffset; // Base of this floor's walls
        const segTopY = floorY - segment.drawHeight - segFloorOffset; // Top of wall
        for (let sy = segBaseY - sidingSpacing; sy > segTopY; sy -= sidingSpacing) {
          // Clip to this segment's horizontal bounds
          const clippedSegments = getClippedSegments(sy, segment.screenStart, segment.screenEnd);
          for (const seg of clippedSegments) {
            if (seg.end > seg.start) {
              ctx.beginPath();
              ctx.moveTo(seg.start, sy);
              ctx.lineTo(seg.end, sy);
              ctx.stroke();
            }
          }
        }
      });
    } else {
      // Fallback for no segments - draw full width at max height
      for (let sy = floorY - sidingSpacing; sy > sidingTopY; sy -= sidingSpacing) {
        const segments = getClippedSegments(sy, wallLeftEdgeX, wallRightEdgeX);
        for (const seg of segments) {
          if (seg.end > seg.start) {
            ctx.beginPath();
            ctx.moveTo(seg.start, sy);
            ctx.lineTo(seg.end, sy);
            ctx.stroke();
          }
        }
      }
    }
  } else if (sidingPattern === 'board-batten') {
    // Vertical board-and-batten siding - with opening clipping
    const boardSpacing = 24 * elevScale; // 24" board width
    const battenWidth = 3 * elevScale; // 3" batten strips

    // Draw per segment to support multi-floor with different floor elevations
    wallSegments.forEach(segment => {
      if (segment.isPostFrame) return; // already drawn as metal siding
      const segFloorOffset = segment.drawFloorElevation || 0;
      const segBaseY = floorY - segFloorOffset; // Base of this floor's walls
      const segTopY = floorY - segment.drawHeight - segFloorOffset; // Top of wall

      // Use GLOBAL spacing starting from the wall's left edge for consistent alignment across all segments
      for (let sx = wallLeftEdgeX + boardSpacing; sx < segment.screenEnd; sx += boardSpacing) {
        // Only draw if within this segment's horizontal bounds
        if (sx < segment.screenStart) continue;

        const battenX = sx + battenWidth / 2;
        const clippedSegs = getClippedVerticalSegments(sx, segTopY, segBaseY);

        for (const seg of clippedSegs) {
          if (seg.end > seg.start) {
            // Draw board edge line
            ctx.beginPath();
            ctx.moveTo(sx, seg.start);
            ctx.lineTo(sx, seg.end);
            ctx.stroke();

            // Draw batten (thicker line) next to the board edge
            if (battenX < segment.screenEnd) {
              const battenClippedSegs = getClippedVerticalSegments(battenX, segTopY, segBaseY);
              for (const battenSeg of battenClippedSegs) {
                if (battenSeg.end > battenSeg.start) {
                  ctx.lineWidth = sidingStyle.lineWidth * 2;
                  ctx.beginPath();
                  ctx.moveTo(battenX, battenSeg.start);
                  ctx.lineTo(battenX, battenSeg.end);
                  ctx.stroke();
                  ctx.lineWidth = sidingStyle.lineWidth;
                }
              }
            }
          }
        }
      }
    });
  } else if (sidingPattern === 'shingle') {
    // Shingle/shake siding pattern - with opening clipping
    // Draw per segment to support multi-floor with different floor elevations
    const rowHeight = 10 * elevScale;
    const shingleWidth = 8 * elevScale;

    wallSegments.forEach(segment => {
      if (segment.isPostFrame) return; // already drawn as metal siding
      const segFloorOffset = segment.drawFloorElevation || 0;
      const segBaseY = floorY - segFloorOffset; // Base of this floor's walls
      const segTopY = floorY - segment.drawHeight - segFloorOffset; // Top of wall
      const segLeftEdge = segment.screenStart;
      const segRightEdge = segment.screenEnd;

      // Draw horizontal rows at GLOBAL Y positions within this segment's bounds
      for (let sy = segBaseY; sy > segTopY - rowHeight; sy -= rowHeight) {
        const rowIndex = Math.round((segBaseY - sy) / rowHeight);
        const rowOffset = (rowIndex % 2) * (shingleWidth / 2);

        // For each row, iterate across X within this segment
        for (let sx = segLeftEdge; sx < segRightEdge; sx += shingleWidth) {
          const segEndX = Math.min(sx + shingleWidth, segRightEdge);

          // Draw this shingle cell if any part of the row is within the wall height
          if (sy >= segTopY && sy <= segBaseY) {
            // Draw horizontal line at sy (row bottom)
            const clipped = getClippedSegments(sy, sx, segEndX);
            for (const seg of clipped) {
              if (seg.end > seg.start) {
                ctx.beginPath();
                ctx.moveTo(seg.start, sy);
                ctx.lineTo(seg.end, sy);
                ctx.stroke();
              }
            }

            // If this row's top extends above segTopY, draw a closing line at segTopY
            if (sy - rowHeight < segTopY) {
              const closingClipped = getClippedSegments(segTopY, sx, segEndX);
              for (const seg of closingClipped) {
                if (seg.end > seg.start) {
                  ctx.beginPath();
                  ctx.moveTo(seg.start, segTopY);
                  ctx.lineTo(seg.end, segTopY);
                  ctx.stroke();
                }
              }
            }

            // Draw vertical joint at start of shingle (with stagger)
            const jointX = sx + rowOffset;
            if (jointX > segLeftEdge && jointX < segRightEdge) {
              const jointTop = Math.max(sy - rowHeight, segTopY);
              const jointBottom = Math.min(sy, segBaseY);

              if (jointTop < jointBottom) {
                const vertSegs = getClippedVerticalSegments(jointX, jointTop, jointBottom);
                for (const vseg of vertSegs) {
                  if (vseg.end > vseg.start) {
                    ctx.beginPath();
                    ctx.moveTo(jointX, vseg.start);
                    ctx.lineTo(jointX, vseg.end);
                    ctx.stroke();
                  }
                }
              }
            }
          }
        }
      }

      // Fill the gap at the top of the shingle pattern for each column
      for (let sx = segLeftEdge; sx < segRightEdge; sx += shingleWidth) {
        const segEndX = Math.min(sx + shingleWidth, segRightEdge);

        // Find the last row line position that was drawn for this column
        let lastSyDrawn = segBaseY;
        for (let sy = segBaseY; sy > segTopY - rowHeight; sy -= rowHeight) {
          if (sy >= segTopY) {
            lastSyDrawn = sy;
          }
        }

        // Draw closing line and joint if there's a gap
        if (lastSyDrawn > segTopY + 0.5) {
          const clipped = getClippedSegments(segTopY, sx, segEndX);
          for (const seg of clipped) {
            if (seg.end > seg.start) {
              ctx.beginPath();
              ctx.moveTo(seg.start, segTopY);
              ctx.lineTo(seg.end, segTopY);
              ctx.stroke();
            }
          }

          const closingRowIndex = Math.round((segBaseY - lastSyDrawn) / rowHeight);
          const closingRowOffset = (closingRowIndex % 2) * (shingleWidth / 2);
          const jointX = sx + closingRowOffset;

          if (jointX > segLeftEdge && jointX < segRightEdge) {
            const vertSegs = getClippedVerticalSegments(jointX, segTopY, lastSyDrawn);
            for (const vseg of vertSegs) {
              if (vseg.end > vseg.start) {
                ctx.beginPath();
                ctx.moveTo(jointX, vseg.start);
                ctx.lineTo(jointX, vseg.end);
                ctx.stroke();
              }
            }
          }
        }
      }
    });
  }

  // Draw wall outline
  // For shed slope view, only draw the bottom edge here - vertical edges handled in roof section
  // For shed high side view, draw left, bottom, right (no top)
  // For gable end view, draw left, bottom, right (no top - gable triangle handles the top)
  // For hip/other roofs, stop wall edges at fascia bottom so they don't show through fascia
  ctx.strokeStyle = wallStyle.stroke;
  ctx.lineWidth = wallStyle.lineWidth;
  if (isSketchyMode) {
    // Draw bottom line for sketchy mode
    // Left/right edges are drawn by shed roof code if in shed slope view,
    // otherwise draw them here
    const wallLeftX = wallLeftEdgeX;
    const wallRightX = wallRightEdgeX;
    const wallTopY = floorY - drawWallHeight;
    drawSketchyLine(ctx, wallLeftX, floorY, wallRightX, floorY); // Bottom
    if (!isShedSlopeView) {
      // Only draw left/right edges if NOT shed slope view (sheds draw their own outer edges)
      drawSketchyLine(ctx, wallLeftX, floorY, wallLeftX, wallTopY); // Left edge
      drawSketchyLine(ctx, wallRightX, floorY, wallRightX, wallTopY); // Right edge
    }
  } else if (isShedSlopeView) {
    // For shed slope view, draw bottom edge and vertical wall edges
    // The vertical edges need to go from grade up to plate line (floorY - drawWallHeight)
    // The shed roof drawing will extend the walls up to meet the sloped fascia
    ctx.beginPath();
    ctx.moveTo(wallLeftEdgeX, floorY);
    ctx.lineTo(wallRightEdgeX, floorY);
    ctx.stroke();

    // Draw stepped wall outline
    if (wallSegments.length > 0) {
      // Sort segments by screen position
      const sortedByScreen = [...wallSegments].sort((a, b) => a.screenStart - b.screenStart);
      const leftmostSeg = sortedByScreen[0];
      const rightmostSeg = sortedByScreen.reduce((r, s) => s.screenEnd > r.screenEnd ? s : r, sortedByScreen[0]);

      // Draw bottom line
      ctx.beginPath();
      ctx.moveTo(leftmostSeg.screenStart, floorY);
      ctx.lineTo(rightmostSeg.screenEnd, floorY);
      ctx.stroke();

      // Draw left edge (from floor up to leftmost segment's wall height)
      ctx.beginPath();
      ctx.moveTo(leftmostSeg.screenStart, floorY);
      ctx.lineTo(leftmostSeg.screenStart, floorY - leftmostSeg.drawHeight);
      ctx.stroke();

      // Draw right edge (from floor up to rightmost segment's wall height)
      ctx.beginPath();
      ctx.moveTo(rightmostSeg.screenEnd, floorY);
      ctx.lineTo(rightmostSeg.screenEnd, floorY - rightmostSeg.drawHeight);
      ctx.stroke();
    } else {
      // Fallback: draw bottom line and left/right edges using default wall dimensions
      const wallLeftX = wallLeftEdgeX;
      const wallRightX = wallRightEdgeX;
      const wallTopY = floorY - drawWallHeight;

      // Bottom line
      ctx.beginPath();
      ctx.moveTo(wallLeftX, floorY);
      ctx.lineTo(wallRightX, floorY);
      ctx.stroke();

      // Left edge
      ctx.beginPath();
      ctx.moveTo(wallLeftX, floorY);
      ctx.lineTo(wallLeftX, wallTopY);
      ctx.stroke();

      // Right edge
      ctx.beginPath();
      ctx.moveTo(wallRightX, floorY);
      ctx.lineTo(wallRightX, wallTopY);
      ctx.stroke();
    }
  } else if (isShedHighSideView || isGableEndView) {
    // For shed high side view or gable end view, draw bottom and left/right edges
    const wallLeftX = wallLeftEdgeX;
    const wallRightX = wallRightEdgeX;
    const wallTopY = floorY - drawWallHeight;

    // Bottom line
    ctx.beginPath();
    ctx.moveTo(wallLeftX, floorY);
    ctx.lineTo(wallRightX, floorY);
    ctx.stroke();

    // Left edge (from floor to wall top)
    ctx.beginPath();
    ctx.moveTo(wallLeftX, floorY);
    ctx.lineTo(wallLeftX, wallTopY);
    ctx.stroke();

    // Right edge (from floor to wall top)
    ctx.beginPath();
    ctx.moveTo(wallRightX, floorY);
    ctx.lineTo(wallRightX, wallTopY);
    ctx.stroke();
  } else {
    // For hip roofs and gable SIDE views, draw combined wall outline
    // This supports multi-floor buildings - draw one outline around the entire wall mass
    if (wallSegments.length > 0) {
      // Find the overall bounds of all wall segments
      let overallLeftX = Infinity;
      let overallRightX = -Infinity;
      let overallTopY = Infinity;
      let overallBottomY = -Infinity;

      wallSegments.forEach(segment => {
        const segFloorOffset = segment.drawFloorElevation || 0;
        const segTopY = floorY - segment.drawHeight - segFloorOffset;
        const segBottomY = floorY - segFloorOffset;

        overallLeftX = Math.min(overallLeftX, segment.screenStart);
        overallRightX = Math.max(overallRightX, segment.screenEnd);
        overallTopY = Math.min(overallTopY, segTopY);
        overallBottomY = Math.max(overallBottomY, segBottomY);
      });

      // Draw a single combined outline around all walls
      const totalWidth = overallRightX - overallLeftX;
      const totalHeight = overallBottomY - overallTopY;
      ctx.strokeRect(overallLeftX, overallTopY, totalWidth, totalHeight);
    } else {
      // Fallback: draw single outline using default dimensions
      const wallLeftX = wallLeftEdgeX;
      const wallRightX = wallRightEdgeX;
      const wallTopY = floorY - drawWallHeight;

      // Bottom line
      ctx.beginPath();
      ctx.moveTo(wallLeftX, floorY);
      ctx.lineTo(wallRightX, floorY);
      ctx.stroke();

      // Left edge (from floor to wall top)
      ctx.beginPath();
      ctx.moveTo(wallLeftX, floorY);
      ctx.lineTo(wallLeftX, wallTopY);
      ctx.stroke();

      // Right edge (from floor to wall top)
      ctx.beginPath();
      ctx.moveTo(wallRightX, floorY);
      ctx.lineTo(wallRightX, wallTopY);
      ctx.stroke();
    }
  }

  // Draw doors
  (doors || []).forEach(door => {
    if (!elevationWallIds.has(door.wallId)) return;

    const wall = walls.find(w => w.id === door.wallId);
    if (!wall) return;

    let doorPos;
    if (isHorizontal) {
      doorPos = wall.start.x + (wall.end.x - wall.start.x) * door.position;
    } else {
      doorPos = wall.start.y + (wall.end.y - wall.start.y) * door.position;
    }

    // Calculate door X position, accounting for flip
    let doorX;
    if (flipX) {
      // Mirror the position: distance from maxPos instead of minPos
      doorX = offsetX + (modelMaxPos - doorPos) * (6 / GRID_SIZE) * elevScale;
    } else {
      doorX = offsetX + (doorPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
    }
    const doorW = (door.width || 36) * elevScale;
    const doorH = (door.height || 84) * elevScale; // Standard 7' door
    // Account for floor elevation (multi-floor stacking)
    const doorFloorElevation = (door.floorElevation || 0) * elevScale;
    const doorY = floorY - doorH - doorFloorElevation; // Door sits on its floor level

    // Calculate flipKnob based on door swing and elevation direction
    // Door swing indicates which side the hinge is on when viewed from INSIDE:
    // - swing='left' means hinges on left, knob on right (from inside)
    // When viewing from OUTSIDE (elevation view), left/right are mirrored:
    // - A door with swing='left' (knob right inside) shows knob on LEFT from outside
    //
    // flipKnob controls knob position in the drawing:
    // - flipKnob=false: knob on right side of door
    // - flipKnob=true: knob on left side of door
    //
    // For exterior view of a swing='left' door: knob should be on LEFT (flipKnob=true)
    // For exterior view of a swing='right' door: knob should be on RIGHT (flipKnob=false)
    const doorSwing = door.swing || 'left';
    let flipKnob = doorSwing === 'left';

    if (detailedOpenings) {
      // Use detailed door rendering with all door types
      const doorType = door.type || 'single';
      drawDetailedDoor(ctx, doorType, doorX, doorY, doorW, doorH, elevScale, renderMode, flipKnob);
    } else {
      // Simple door rendering
      ctx.fillStyle = '#fff';
      ctx.fillRect(doorX - doorW / 2, doorY, doorW, doorH);
      ctx.strokeStyle = wallStyle.stroke;
      ctx.lineWidth = 1;
      if (isSketchyMode) {
        drawSketchyRect(ctx, doorX - doorW / 2, doorY, doorW, doorH);
      } else {
        ctx.strokeRect(doorX - doorW / 2, doorY, doorW, doorH);
      }
      // Door panel
      ctx.strokeRect(doorX - doorW / 2 + 3, doorY + 3, doorW - 6, doorH - 6);
    }
  });

  // Draw windows
  (windows || []).forEach(win => {
    if (!elevationWallIds.has(win.wallId)) return;

    const wall = walls.find(w => w.id === win.wallId);
    if (!wall) return;

    let winPos;
    if (isHorizontal) {
      winPos = wall.start.x + (wall.end.x - wall.start.x) * win.position;
    } else {
      winPos = wall.start.y + (wall.end.y - wall.start.y) * win.position;
    }

    // Calculate window X position, accounting for flip
    let winCenterX;
    if (flipX) {
      winCenterX = offsetX + (modelMaxPos - winPos) * (6 / GRID_SIZE) * elevScale;
    } else {
      winCenterX = offsetX + (winPos - modelMinPos) * (6 / GRID_SIZE) * elevScale;
    }
    const winW = (win.width || 36) * elevScale;
    const winH = (win.height || 48) * elevScale;
    const winSill = (win.sillHeight || 36) * elevScale;
    // Account for floor elevation (multi-floor stacking)
    const winFloorElevation = (win.floorElevation || 0) * elevScale;
    const winY = floorY - winSill - winH - winFloorElevation;
    const winX = winCenterX - winW / 2; // Left edge of window

    if (detailedOpenings) {
      // Use detailed window rendering with trim, mullions, and sill
      drawDetailedWindow(ctx, winX, winY, winW, winH, elevScale, renderMode);
    } else {
      // Simple window rendering
      ctx.fillStyle = renderMode === 'color' ? '#e8f4fc' : '#fff';
      ctx.fillRect(winX, winY, winW, winH);
      ctx.strokeStyle = wallStyle.stroke;
      ctx.lineWidth = 1;
      if (isSketchyMode) {
        drawSketchyRect(ctx, winX, winY, winW, winH);
      } else {
        ctx.strokeRect(winX, winY, winW, winH);
      }
      // Window panes (cross pattern)
      ctx.beginPath();
      ctx.moveTo(winX + winW / 2, winY);
      ctx.lineTo(winX + winW / 2, winY + winH);
      ctx.moveTo(winX, winY + winH / 2);
      ctx.lineTo(winX + winW, winY + winH / 2);
      ctx.stroke();
    }
  });

  // Draw roof
  const roofBaseY = floorY - drawWallHeight;
  const fasciaHeight = 8 * elevScale;

  // Track roof path for hit testing and selection
  let roofPath = null;
  let allRoofBounds = []; // Array to store bounds for ALL roofs
  let roofOutlinePoints = []; // Store the outline points for selection highlight

  // Draw each roof at its specific position
  // processedRoofs is sorted by depth (furthest first), so closer roofs draw on top
  // TWO-PASS DRAWING: Draw SIDE view roofs first, then END view roofs on top
  // This ensures gable END views appear on top of extended SIDE roofs
  const drawRoof = (currentRoof, roofIndex) => {
    // Skip if roof data is invalid
    if (!currentRoof || typeof currentRoof.screenX !== 'number' || typeof currentRoof.screenWidth !== 'number') {
      return;
    }

    // Calculate this roof's base Y position based on its plate height
    // plateHeight is the height from ground where the eave sits (in inches)
    const roofPlateHeight = currentRoof.plateHeight || wallHeightInches;
    const drawRoofPlateHeight = roofPlateHeight * elevScale;
    const thisRoofBaseY = floorY - drawRoofPlateHeight;
    // Shadow the outer roofBaseY so all code in this function uses the per-roof value
    const roofBaseY = thisRoofBaseY;

    // Draw wall extension if this roof's plate height is higher than default wall height
    // This fills the wall area from the standard wall top up to this roof's plate height
    const defaultWallTopY = floorY - drawWallHeight;
    if (roofPlateHeight > wallHeightInches && currentRoof.screenX !== undefined) {
      const wallExtensionHeight = (roofPlateHeight - wallHeightInches) * elevScale;
      const extensionLeft = currentRoof.screenX;
      const extensionWidth = currentRoof.screenWidth;
      const extensionTop = roofBaseY;
      const extensionBottom = roofBaseY + wallExtensionHeight;

      // Draw wall fill for the extension
      ctx.fillStyle = wallStyle.fill;
      ctx.fillRect(extensionLeft, extensionTop, extensionWidth, wallExtensionHeight);

      // Draw wall stroke outline for the extension
      ctx.strokeStyle = wallStyle.stroke;
      ctx.lineWidth = wallStyle.lineWidth;
      // Top horizontal line
      ctx.beginPath();
      ctx.moveTo(extensionLeft, extensionTop);
      ctx.lineTo(extensionLeft + extensionWidth, extensionTop);
      ctx.stroke();
      // Left vertical edge
      // Left vertical edge
      ctx.beginPath();
      ctx.moveTo(extensionLeft, extensionTop);
      ctx.lineTo(extensionLeft, extensionBottom);
      ctx.stroke();
      // Right vertical edge
      ctx.beginPath();
      ctx.moveTo(extensionLeft + extensionWidth, extensionTop);
      ctx.lineTo(extensionLeft + extensionWidth, extensionBottom);
      ctx.stroke();

      // Draw siding pattern on the wall extension
      ctx.strokeStyle = sidingStyle.stroke;
      ctx.lineWidth = sidingStyle.lineWidth;

      if (sidingPattern === 'horizontal' || sidingPattern === 'none') {
        // Horizontal lap siding
        const sidingSpacing = 6 * elevScale;
        for (let sy = extensionBottom - sidingSpacing; sy > extensionTop; sy -= sidingSpacing) {
          ctx.beginPath();
          ctx.moveTo(extensionLeft, sy);
          ctx.lineTo(extensionLeft + extensionWidth, sy);
          ctx.stroke();
        }
      } else if (sidingPattern === 'board-batten') {
        // Vertical board-and-batten siding - use GLOBAL spacing from the wall's left edge
        const boardSpacing = 24 * elevScale;
        const battenWidth = 3 * elevScale;
        for (let sx = wallLeftEdgeX + boardSpacing; sx < extensionLeft + extensionWidth; sx += boardSpacing) {
          if (sx < extensionLeft || sx > extensionLeft + extensionWidth) continue;
          // Main board edge
          ctx.lineWidth = sidingStyle.lineWidth;
          ctx.beginPath();
          ctx.moveTo(sx, extensionTop);
          ctx.lineTo(sx, extensionBottom);
          ctx.stroke();
          // Batten
          const battenX = sx + battenWidth / 2;
          if (battenX <= extensionLeft + extensionWidth && battenX >= extensionLeft) {
            ctx.lineWidth = sidingStyle.lineWidth * 2;
            ctx.beginPath();
            ctx.moveTo(battenX, extensionTop);
            ctx.lineTo(battenX, extensionBottom);
            ctx.stroke();
          }
        }
        ctx.lineWidth = sidingStyle.lineWidth;
      } else if (sidingPattern === 'shingle') {
        // Shingle pattern
        const rowHeight = 10 * elevScale;
        const shingleWidth = 8 * elevScale;
        let shingleRowIndex = 0;
        for (let sy = extensionBottom - rowHeight; sy > extensionTop; sy -= rowHeight) {
          const rowOffset = (shingleRowIndex % 2) * (shingleWidth / 2);
          // Horizontal row line
          ctx.beginPath();
          ctx.moveTo(extensionLeft, sy);
          ctx.lineTo(extensionLeft + extensionWidth, sy);
          ctx.stroke();
          // Vertical joints
          for (let sx = extensionLeft + rowOffset + shingleWidth; sx < extensionLeft + extensionWidth; sx += shingleWidth) {
            ctx.beginPath();
            ctx.moveTo(sx, sy);
            ctx.lineTo(sx, sy + rowHeight);
            ctx.stroke();
          }
          shingleRowIndex++;
        }
      }
    }

    // Use this roof's specific dimensions
    const roofDrawPeak = currentRoof.drawPeakHeight || 0;
    const roofDrawOverhang = currentRoof.drawOverhang || 12;

    // A shed abutting a gable end does not stop at the wall - it runs on into the
    // gable until the two roof planes meet. Drawn only to its own footprint it left
    // a gap that the valley wedge then filled with roof colour over bare gable
    // siding, which reads as the roof hatch bleeding into the siding hatch. The
    // whole shed is drawn from this span, so widening it here carries the surface,
    // the fascia and the overhangs across together.
    let roofOffsetX = currentRoof.screenX;
    let roofElevWidth = currentRoof.screenWidth;

    if (currentRoof.type === 'shed' && Array.isArray(currentRoof.valleyConnections)) {
      const shedTopY = roofBaseY - roofDrawPeak;

      for (const conn of currentRoof.valleyConnections) {
        const endRoof = conn.endRoof;
        if (!endRoof || !endRoof.isGableEnd || endRoof === currentRoof) continue;

        const gablePeakX = endRoof.screenX + endRoof.screenWidth / 2;
        const gablePeakY = roofBaseY - (endRoof.drawPeakHeight || 0);
        const slopeRise = roofBaseY - gablePeakY;
        if (Math.abs(slopeRise) < 0.1) continue;

        // Which of the gable's two slopes faces this shed
        const shedCenter = roofOffsetX + roofElevWidth / 2;
        const gableFacingX = shedCenter < gablePeakX ? endRoof.screenX : endRoof.screenX + endRoof.screenWidth;

        // Where that slope stands at the height of this shed's high edge
        const t = (shedTopY - gablePeakY) / slopeRise;
        if (!(t > 0 && t < 1)) continue;
        const valleyX = gablePeakX + (gableFacingX - gablePeakX) * t;

        if (shedCenter < gablePeakX) {
          if (valleyX > roofOffsetX + roofElevWidth) {
            roofElevWidth = valleyX - roofOffsetX;
          }
        } else if (valleyX < roofOffsetX) {
          roofElevWidth += roofOffsetX - valleyX;
          roofOffsetX = valleyX;
        }
      }
    }
    const currentRoofIsGableEnd = currentRoof.isGableEnd;

    // Check if this roof is fully behind another roof (completely occluded)
    // A roof at a further depth should be hidden if a closer roof covers its entire screen width
    const thisRoofLeft = roofOffsetX - roofDrawOverhang;
    const thisRoofRight = roofOffsetX + roofElevWidth + roofDrawOverhang;

    const getDepthValue = (roof) => {
      if (isHorizontal) {
        return elevation === 'north' ? roof.bounds.maxY : roof.bounds.minY;
      } else {
        return elevation === 'west' ? roof.bounds.maxX : roof.bounds.minX;
      }
    };

    const thisDepth = getDepthValue(currentRoof);
    let fullyOccluded = false;

    for (const otherRoof of processedRoofs) {
      if (otherRoof === currentRoof) continue;

      const otherDepth = getDepthValue(otherRoof);
      // Check if other roof is closer to the viewer
      const otherIsCloser = elevation === 'north' || elevation === 'west'
        ? otherDepth < thisDepth
        : otherDepth > thisDepth;

      if (otherIsCloser) {
        // Check if the closer roof covers the full screen width of this roof
        const otherLeft = otherRoof.screenX - (otherRoof.drawOverhang || 12);
        const otherRight = otherRoof.screenX + otherRoof.screenWidth + (otherRoof.drawOverhang || 12);

        // If the closer roof fully covers this roof's screen area AND is at least as tall,
        // then this roof is occluded. But if this roof is TALLER, it should be visible above.
        const thisPeakHeight = currentRoof.drawPeakHeight || 0;
        const thisPlateHeight = (currentRoof.plateHeight || 96) * elevScale;
        const thisTotalHeight = thisPlateHeight + thisPeakHeight;

        const otherPeakHeight = otherRoof.drawPeakHeight || 0;
        const otherPlateHeight = (otherRoof.plateHeight || 96) * elevScale;
        const otherTotalHeight = otherPlateHeight + otherPeakHeight;

        // Only mark as fully occluded if the closer roof covers the width AND is at least as tall
        if (otherLeft <= thisRoofLeft && otherRight >= thisRoofRight && otherTotalHeight >= thisTotalHeight) {
          console.log(`  Roof ${roofIndex} FULLY OCCLUDED by closer roof: thisDepth=${thisDepth.toFixed(1)}, otherDepth=${otherDepth.toFixed(1)}, thisHeight=${thisTotalHeight.toFixed(1)}, otherHeight=${otherTotalHeight.toFixed(1)}`);
          fullyOccluded = true;
          break;
        } else if (otherLeft <= thisRoofLeft && otherRight >= thisRoofRight) {
          console.log(`  Roof ${roofIndex} NOT occluded despite width coverage - this roof is taller: thisHeight=${thisTotalHeight.toFixed(1)}, otherHeight=${otherTotalHeight.toFixed(1)}`);
        }
      }
    }

    if (fullyOccluded) {
      return; // Don't draw this roof - it's completely behind another roof
    }

    // Check if this roof should be clipped by any depth transitions
    // A roof is clipped if it's at a FURTHER depth than a wall transition
    let clipLeft = null;
    let clipRight = null;

    wallDepthTransitions.forEach(transition => {
      // Check if this roof should be occluded by the closer wall at this transition
      // The roof is occluded if:
      // 1. It's positioned on the FURTHER side of the transition (where the further wall is)
      // 2. AND the roof's position overlaps with the transition point

      // First, check if the roof's screen position overlaps the transition
      const roofLeft = roofOffsetX - roofDrawOverhang;
      const roofRight = roofOffsetX + roofElevWidth + roofDrawOverhang;
      const transitionInRoofRange = transition.screenX >= roofLeft && transition.screenX <= roofRight;

      if (!transitionInRoofRange) {
        console.log(`  Roof ${roofIndex} depth check: transition at ${transition.screenX.toFixed(1)} not in roof range [${roofLeft.toFixed(1)}, ${roofRight.toFixed(1)}]`);
        return; // This transition doesn't affect this roof
      }

      // Check if this roof belongs to the FURTHER building section
      // A roof belongs to the further building if its near edge (facing the viewer) is
      // beyond the CLOSER wall's depth. The closer wall occludes roofs that are behind it.
      let roofNearEdge, roofIsOnFurtherSide;
      if (isHorizontal) {
        // For N/S elevations, check Y bounds
        if (elevation === 'north') {
          // Viewer from north (negative Y), so minY is closest to viewer
          roofNearEdge = currentRoof.bounds.minY;
          // Roof is on further side if its near edge is further from viewer than the closer wall
          roofIsOnFurtherSide = roofNearEdge > transition.closerDepth;
        } else {
          // South: viewer from south (positive Y), so maxY is closest to viewer
          roofNearEdge = currentRoof.bounds.maxY;
          roofIsOnFurtherSide = roofNearEdge < transition.closerDepth;
        }
      } else {
        // For E/W elevations, check X bounds
        if (elevation === 'west') {
          // Viewer from west (negative X), so minX is closest to viewer
          roofNearEdge = currentRoof.bounds.minX;
          roofIsOnFurtherSide = roofNearEdge > transition.closerDepth;
        } else {
          // East: viewer from east (positive X), so maxX is closest to viewer
          roofNearEdge = currentRoof.bounds.maxX;
          roofIsOnFurtherSide = roofNearEdge < transition.closerDepth;
        }
      }

      // Also check if the roof's position is on the CLOSER side of the transition
      // A roof should only be clipped if it extends INTO the closer wall's territory
      const roofCenter = roofOffsetX + roofElevWidth / 2;
      const roofExtendsIntoCloserSide = transition.closerSide === 'left'
        ? roofCenter < transition.screenX  // Roof extends into left (closer) side
        : roofCenter > transition.screenX; // Roof extends into right (closer) side

      console.log(`  Roof ${roofIndex} depth check: roofNearEdge=${roofNearEdge.toFixed(1)}, closerDepth=${transition.closerDepth.toFixed(1)}, roofIsOnFurtherSide=${roofIsOnFurtherSide}, roofExtendsIntoCloserSide=${roofExtendsIntoCloserSide}, transitionX=${transition.screenX.toFixed(1)}`);

      // Only clip if the roof is on the further side AND extends into the closer wall's territory
      if (roofIsOnFurtherSide && roofExtendsIntoCloserSide) {
        // This roof is behind a closer wall - clip it at the transition
        if (transition.closerSide === 'left') {
          // The closer wall is on the left - clip this roof on the left side
          if (clipLeft === null || transition.screenX > clipLeft) {
            clipLeft = transition.screenX;
          }
        } else {
          // The closer wall is on the right - clip this roof on the right side
          if (clipRight === null || transition.screenX < clipRight) {
            clipRight = transition.screenX;
          }
        }
      }
    });

    // Apply clipping if needed to hide roof portions behind closer walls
    // The clipping shows:
    // 1. Non-occluded side: full height including fascia
    // 2. Occluded side: only roof peak above wall top (fascia hidden by closer wall)
    let useClipping = clipLeft !== null || clipRight !== null;
    if (useClipping) {
      ctx.save();
      ctx.beginPath();

      const roofFullLeft = roofOffsetX - roofDrawOverhang * 2;
      const roofFullRight = roofOffsetX + roofElevWidth + roofDrawOverhang * 2;
      const wallTopY = roofBaseY;

      console.log(`  Roof ${roofIndex} clipping: clipLeft=${clipLeft}, clipRight=${clipRight}, roofBaseY=${roofBaseY.toFixed(1)}`);

      if (clipRight !== null && clipLeft === null) {
        // Occluded on the RIGHT side
        // Region 1: Left of transition - full height including fascia (not occluded)
        ctx.rect(roofFullLeft, 0, clipRight - roofFullLeft, bounds.y + bounds.height);
        // Region 2: Right of transition - only above wall (fascia hidden by closer wall)
        ctx.rect(clipRight, 0, roofFullRight - clipRight, wallTopY);
      } else if (clipLeft !== null && clipRight === null) {
        // Occluded on the LEFT side
        // Region 1: Right of transition - full height including fascia (not occluded)
        ctx.rect(clipLeft, 0, roofFullRight - clipLeft, bounds.y + bounds.height);
        // Region 2: Left of transition - only above wall (fascia hidden by closer wall)
        ctx.rect(roofFullLeft, 0, clipLeft - roofFullLeft, wallTopY);
      } else if (clipLeft !== null && clipRight !== null) {
        // Occluded on both sides - middle is visible, edges hidden
        ctx.rect(clipLeft, 0, clipRight - clipLeft, bounds.y + bounds.height);
        ctx.rect(roofFullLeft, 0, clipLeft - roofFullLeft, wallTopY);
        ctx.rect(clipRight, 0, roofFullRight - clipRight, wallTopY);
      }

      ctx.clip();
    }

    console.log(`Roof ${roofIndex}: type=${currentRoof.type}, ridgeDir=${currentRoof.ridgeDirection}, isGableEnd=${currentRoofIsGableEnd}, screenX=${roofOffsetX.toFixed(1)}, width=${roofElevWidth.toFixed(1)}, peak=${roofDrawPeak.toFixed(1)}, overhang=${roofDrawOverhang.toFixed(1)}, clipped=${useClipping}`);

    ctx.fillStyle = roofStyle.fill;
    ctx.strokeStyle = roofStyle.stroke;
    ctx.lineWidth = 1;

    if (currentRoof.type === 'gable') {
      // Use the pre-calculated isGableEnd from processedRoofs
      const isGableEnd = currentRoofIsGableEnd;

      if (isGableEnd) {
        // Gable END view - looking at the triangular wall with siding
        // The gable triangle is WALL (with siding), not roof
        console.log(`>>> GABLE END VIEW: roofIndex=${roofIndex}, elevation=${elevation}`);
        // Gable END view drawing continues below

        // Check for valley connections that would clip this gable
        let valleyClipLeft = null;  // {x, y} if left slope is clipped
        let valleyClipRight = null; // {x, y} if right slope is clipped
        let valleyConnection = null;
        let sideRoofTopY = null;  // Y level of the SIDE roof's top surface (for siding clipping)

        // Where two roofs abut the same slope, the taller one forms the valley and
        // the shorter tucks in beneath it - so keep the highest point found per side.
        const recordValley = (direction, point, connection) => {
          const current = direction === 'left' ? valleyClipLeft : valleyClipRight;
          if (current && current.y <= point.y) return;
          // Remember which roof forms this valley - the wedge that closes it has to
          // be painted as that roof's surface, and the two are not the same colour.
          point.roof = connection.sideRoof || connection.shedRoof || null;
          if (direction === 'left') valleyClipLeft = point;
          else valleyClipRight = point;
          valleyConnection = connection;
          sideRoofTopY = point.y;
        };

        // Every abutting roof forms its own valley: a porch gable can meet a shed on
        // its left, another on its right and the main roof behind it, all at once.
        // Reading only the first connection left the other slopes uncut - and when
        // that first one yielded no valley, as it does when the main roof passes
        // above this gable's peak, the gable ran straight through its neighbours.
        for (const connection of (currentRoof.valleyConnections || [])) {
          const { gableSlopeDirection, sideRoof, connectionType, shedRoof } = connection;

          console.log(`  Valley connection found: connectionType=${connectionType}, hasShedRoof=${!!shedRoof}, gableSlopeDirection=${gableSlopeDirection}`);

          // Calculate the valley intersection point
          const gablePeakX = roofOffsetX + roofElevWidth / 2;
          const gablePeakY = roofBaseY - roofDrawPeak;
          const gableCornerX = gableSlopeDirection === 'left' ? roofOffsetX : roofOffsetX + roofElevWidth;
          const gableCornerY = roofBaseY;

          // Handle shed-gable connection differently from gable-gable
          if (connectionType === 'shed-gable' && shedRoof) {
            console.log(`  Entering shed-gable valley calculation`);
            // For shed-gable: the gable slope meets the shed roof's SLOPED surface
            const shedEdge = shedRoof.shedSlopeEdge || 'north';
            const shedPitchDef = ROOF_PITCHES[shedRoof.pitch] || ROOF_PITCHES['6:12'];
            const shedBounds = shedRoof.bounds;
            const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
            const shedRunDim = slopeRunsNS ? (shedBounds.maxY - shedBounds.minY) : (shedBounds.maxX - shedBounds.minX);
            const shedPeakHeight = (shedRunDim * (6 / GRID_SIZE)) * (shedPitchDef.rise / shedPitchDef.run) * elevScale;

            // Determine which side of shed is high based on screen coordinate mapping
            // flipX is true for north/east elevations, false for south/west elevations
            // With flipX=false (south/west): screen LEFT = model WEST, screen RIGHT = model EAST
            // With flipX=true (north/east): screen LEFT = model EAST, screen RIGHT = model WEST
            let shedLeftIsHigh;
            if (shedEdge === 'north') {
              // North is LOW, South is HIGH
              shedLeftIsHigh = elevation === 'east';
            } else if (shedEdge === 'south') {
              // South is LOW, North is HIGH
              shedLeftIsHigh = elevation === 'west';
            } else if (shedEdge === 'east') {
              // East is LOW, West is HIGH
              shedLeftIsHigh = elevation === 'south';
            } else { // west
              // West is LOW, East is HIGH
              shedLeftIsHigh = elevation === 'north';
            }

            const shedLeftY = shedLeftIsHigh ? (roofBaseY - shedPeakHeight) : roofBaseY;
            const shedRightY = shedLeftIsHigh ? roofBaseY : (roofBaseY - shedPeakHeight);
            const shedLeftX = shedRoof.screenX;
            const shedRightX = shedRoof.screenX + shedRoof.screenWidth;

            // The shed roof's slope line equation: Y = shedLeftY + (shedRightY - shedLeftY) * (X - shedLeftX) / (shedRightX - shedLeftX)
            // The gable's slope line: from (gablePeakX, gablePeakY) to (gableCornerX, gableCornerY)

            // Find intersection of two lines
            const gableSlopeRun = gableCornerX - gablePeakX;
            const gableSlopeRise = gableCornerY - gablePeakY;
            const shedSlopeRun = shedRightX - shedLeftX;
            const shedSlopeRise = shedRightY - shedLeftY;

            // Parametric form: gable line: P1 + t*(P2-P1), shed line: P3 + s*(P4-P3)
            // Solve for intersection
            const denom = gableSlopeRun * shedSlopeRise - gableSlopeRise * shedSlopeRun;
            if (Math.abs(denom) > 0.1) {
              const t = ((shedLeftX - gablePeakX) * shedSlopeRise - (shedLeftY - gablePeakY) * shedSlopeRun) / denom;
              const intersectX = gablePeakX + t * gableSlopeRun;
              const intersectY = gablePeakY + t * gableSlopeRise;

              console.log(`  Shed-Gable valley calc: gablePeak=(${gablePeakX.toFixed(1)}, ${gablePeakY.toFixed(1)}), gableCorner=(${gableCornerX.toFixed(1)}, ${gableCornerY.toFixed(1)})`);
              console.log(`    shed: leftX=${shedLeftX.toFixed(1)}, rightX=${shedRightX.toFixed(1)}, leftY=${shedLeftY.toFixed(1)}, rightY=${shedRightY.toFixed(1)}`);
              console.log(`    intersection: t=${t.toFixed(3)}, X=${intersectX.toFixed(1)}, Y=${intersectY.toFixed(1)}`);

              // Check if intersection is valid (on the gable slope between peak and corner)
              const intersectOnSlope = t > 0.1 && t < 0.9;
              if (intersectOnSlope) {
                recordValley(gableSlopeDirection, { x: intersectX, y: intersectY }, connection);
                console.log(`  Shed-Gable valley clip: direction=${gableSlopeDirection}, point=(${intersectX.toFixed(1)}, ${intersectY.toFixed(1)})`);
              }
            }
          } else {
            // Original gable-gable logic

            // Side roof edges and TOP surface
            const sideRoofLeftX = sideRoof.screenX;
            const sideRoofRightX = sideRoof.screenX + sideRoof.screenWidth;

            // The side roof's TOP surface in elevation view
            // Use the same calculation as the SIDE roof drawing code:
            // visibleRoofDepth = roofDrawPeak > 0 ? roofDrawPeak : fasciaHeight * 2
            const sideRoofDrawPeak = sideRoof.drawPeakHeight || 0;
            const sideRoofVisibleDepth = sideRoofDrawPeak > 0 ? sideRoofDrawPeak : fasciaHeight * 2;
            const connSideRoofTopY = roofBaseY - sideRoofVisibleDepth;

            console.log(`  Valley calc: gablePeak=(${gablePeakX.toFixed(1)}, ${gablePeakY.toFixed(1)}), gableCorner=(${gableCornerX.toFixed(1)}, ${gableCornerY.toFixed(1)})`);
            console.log(`    sideRoof: left=${sideRoofLeftX.toFixed(1)}, right=${sideRoofRightX.toFixed(1)}, topY=${connSideRoofTopY.toFixed(1)}, depth=${sideRoofVisibleDepth.toFixed(1)}`);

            // Find where the gable slope line intersects the side roof's TOP horizontal line
            // Slope from (gablePeakX, gablePeakY) to (gableCornerX, gableCornerY)
            // Find X when Y = sideRoofTopY
            const slopeRun = gableCornerX - gablePeakX;
            const slopeRise = gableCornerY - gablePeakY;

            if (Math.abs(slopeRise) > 0.1) {
              // t = (Y - peakY) / rise, then X = peakX + run * t
              const t = (connSideRoofTopY - gablePeakY) / slopeRise;
              const intersectX = gablePeakX + slopeRun * t;

              console.log(`    slope: run=${slopeRun.toFixed(1)}, rise=${slopeRise.toFixed(1)}, t=${t.toFixed(3)}, intersectX=${intersectX.toFixed(1)}`);

              // Only apply valley if intersection is on the slope (0 < t < 1)
              // The intersection X doesn't need to be inside the side roof bounds -
              // the valley forms where the gable's slope plane meets the side roof's top plane
              const intersectOnSlope = t > 0 && t < 1;

              // The clip point should be between the gable peak and corner
              const clipBetweenPeakAndCorner = (gableSlopeDirection === 'right' && intersectX > gablePeakX && intersectX < gableCornerX) ||
                                                (gableSlopeDirection === 'left' && intersectX < gablePeakX && intersectX > gableCornerX);

              console.log(`    intersectOnSlope=${intersectOnSlope}, clipBetweenPeakAndCorner=${clipBetweenPeakAndCorner}`);

            if (intersectOnSlope && clipBetweenPeakAndCorner) {
              recordValley(gableSlopeDirection, { x: intersectX, y: connSideRoofTopY }, connection);

              console.log(`  Valley clip calculated: slope=${gableSlopeDirection}, clipPoint=(${intersectX.toFixed(1)}, ${connSideRoofTopY.toFixed(1)})`);
            } else {
              console.log(`  Valley clip SKIPPED: conditions not met`);
            }
            }
          }
        }

        // Gable draws fully - no clipping needed
        // In 2D elevation, the gable naturally occludes any SIDE roof behind it

        // Draw gable wall triangle (siding continues up).
        //
        // Below a valley this wall stands behind the roof that meets it there, so the
        // triangle is cut off at the valley on that side. Drawn in full it covered the
        // porch roof - the gable end draws after it - and ran up to the rake where the
        // fascia had already been cut short, which reads as siding pushing into the
        // fascia board.
        const gableLeftLimitX = valleyClipLeft ? valleyClipLeft.x : roofOffsetX;
        const gableRightLimitX = valleyClipRight ? valleyClipRight.x : roofOffsetX + roofElevWidth;
        const gableLeftLimitY = valleyClipLeft ? valleyClipLeft.y : roofBaseY;
        const gableRightLimitY = valleyClipRight ? valleyClipRight.y : roofBaseY;

        ctx.fillStyle = wallStyle.fill;
        ctx.beginPath();
        ctx.moveTo(gableLeftLimitX, roofBaseY);
        ctx.lineTo(gableLeftLimitX, gableLeftLimitY);
        ctx.lineTo(roofOffsetX + roofElevWidth / 2, roofBaseY - roofDrawPeak);
        ctx.lineTo(gableRightLimitX, gableRightLimitY);
        ctx.lineTo(gableRightLimitX, roofBaseY);
        ctx.closePath();
        ctx.fill();

        // Draw siding pattern in gable triangle based on sidingPattern option
        ctx.strokeStyle = sidingStyle.stroke;
        ctx.lineWidth = sidingStyle.lineWidth;
        const gableSidingSpacing = 6 * elevScale;
        const peakX = roofOffsetX + roofElevWidth / 2;
        const peakY = roofBaseY - roofDrawPeak;

        // Helper to get X bounds of gable triangle at a given Y
        const getGableBounds = (y) => {
          const progress = (roofBaseY - y) / roofDrawPeak; // 0 at base, 1 at peak
          const halfWidthAtY = (roofElevWidth / 2) * (1 - progress);
          // Held inside the valley limits so the siding stops where the wall does
          const leftX = Math.max(peakX - halfWidthAtY, gableLeftLimitX);
          const rightX = Math.min(peakX + halfWidthAtY, gableRightLimitX);
          return { leftX, rightX, valid: rightX - leftX > 4 };
        };

        // Helper to check if point is inside gable triangle
        const isInGable = (x, y) => {
          if (y >= roofBaseY || y <= peakY) return false;
          const bounds = getGableBounds(y);
          return x >= bounds.leftX && x <= bounds.rightX;
        };

        if (sidingPattern === 'horizontal' || sidingPattern === 'none') {
          // Horizontal lap siding in gable
          // Draw base line at roofBaseY (wall-to-gable boundary)
          // Skip this line when there's a shed-gable connection - the shed roof covers this boundary
          if (!(valleyConnection && valleyConnection.connectionType === 'shed-gable')) {
            ctx.beginPath();
            ctx.moveTo(gableLeftLimitX, roofBaseY);
            ctx.lineTo(gableRightLimitX, roofBaseY);
            ctx.stroke();
          }

          // When there's a valley connection, clip siding lines where SIDE roof occludes the gable
          for (let sy = roofBaseY - gableSidingSpacing; sy > peakY; sy -= gableSidingSpacing) {
            const bounds = getGableBounds(sy);
            if (bounds.valid) {
              let drawLeftX = bounds.leftX;
              let drawRightX = bounds.rightX;

              // Note: No clipping needed for valley connections since the SIDE roof fill
              // no longer extends into the gable area - the gable siding draws normally

              // Only draw if there's a valid line segment
              if (drawRightX > drawLeftX) {
                ctx.beginPath();
                ctx.moveTo(drawLeftX, sy);
                ctx.lineTo(drawRightX, sy);
                ctx.stroke();
              }
            }
          }
        } else if (sidingPattern === 'board-batten') {
          // Vertical board-and-batten in gable triangle
          // IMPORTANT: Use same spacing reference as wall (the wall's left edge) for alignment
          const boardSpacing = 24 * elevScale;
          const battenWidth = 3 * elevScale;
          const slope = roofDrawPeak / (roofElevWidth / 2);
          const gableLeft = roofOffsetX;
          const gableRight = roofOffsetX + roofElevWidth;

          // Helper to get the top Y for a vertical line at a given x position
          const getTopY = (x) => {
            if (x <= peakX) {
              // Left side of gable
              return roofBaseY - (x - roofOffsetX) * slope;
            } else {
              // Right side of gable
              return roofBaseY - (roofOffsetX + roofElevWidth - x) * slope;
            }
          };

          // Use GLOBAL spacing from the wall's left edge (same as wall) for alignment
          for (let sx = wallLeftEdgeX + boardSpacing; sx < gableRight; sx += boardSpacing) {
            // Skip if outside gable horizontal bounds
            if (sx < gableLeft || sx > gableRight) continue;

            const topY = getTopY(sx);
            if (topY < roofBaseY) {
              // Draw board line
              ctx.beginPath();
              ctx.moveTo(sx, roofBaseY);
              ctx.lineTo(sx, Math.max(topY, peakY));
              ctx.stroke();

              // Draw batten line (always draw if board was drawn, using appropriate slope)
              const battenX = sx + battenWidth / 2;
              if (battenX <= gableRight && battenX >= gableLeft) {
                const battenTopY = getTopY(battenX);
                ctx.lineWidth = sidingStyle.lineWidth * 2;
                ctx.beginPath();
                ctx.moveTo(battenX, roofBaseY);
                ctx.lineTo(battenX, Math.max(battenTopY, peakY));
                ctx.stroke();
                ctx.lineWidth = sidingStyle.lineWidth;
              }
            }
          }
        } else if (sidingPattern === 'shingle') {
          // Shingle pattern in gable - horizontal rows with staggered vertical joints
          // Must align with wall shingle pattern for seamless transition
          const rowHeight = 10 * elevScale;
          const shingleWidth = 8 * elevScale;

          // Draw from bottom (roofBaseY) up to peak
          // Start at roofBaseY so we don't miss the bottom row
          for (let sy = roofBaseY; sy > peakY; sy -= rowHeight) {
            // Calculate row index based on distance from floorY (same as wall pattern)
            // Wall pattern starts at floorY with index 0 and increments going up
            // This ensures consistent staggering between wall and gable
            const shingleRowIndex = Math.floor((floorY - sy) / rowHeight);

            const bounds = getGableBounds(sy);

            // Only draw horizontal line if we're above the base (not at roofBaseY itself)
            // The roofBaseY line is the wall/gable boundary, no line needed there
            if (sy < roofBaseY && bounds.valid) {
              // Horizontal row line
              ctx.beginPath();
              ctx.moveTo(bounds.leftX, sy);
              ctx.lineTo(bounds.rightX, sy);
              ctx.stroke();
            }

            // Vertical shingle joints - draw from current row down to next row
            // For each row, joints go from sy down to sy + rowHeight (clamped to roofBaseY)
            const rowOffset = (shingleRowIndex % 2) * (shingleWidth / 2);
            const jointTop = sy;
            const jointBottom = Math.min(sy + rowHeight, roofBaseY);

            // Get bounds at bottom of joint to know valid x range there
            const bottomBounds = getGableBounds(jointBottom);

            if (jointTop < jointBottom && bounds.valid) {
              // Calculate x positions aligned with wall pattern (starting from roofOffsetX, not bounds.leftX)
              // This ensures vertical joints in gable align with those in wall
              for (let sx = roofOffsetX + rowOffset + shingleWidth; sx < roofOffsetX + roofElevWidth; sx += shingleWidth) {
                // Only draw if x is within gable bounds at both top and bottom of joint
                if (sx >= bounds.leftX && sx <= bounds.rightX &&
                    sx >= bottomBounds.leftX && sx <= bottomBounds.rightX) {
                  ctx.beginPath();
                  ctx.moveTo(sx, jointTop);
                  ctx.lineTo(sx, jointBottom);
                  ctx.stroke();
                }
              }
            }
          }
        }

        // No closure patch here. One used to fill from the valley down to the eave, to
        // stand in for roofs that stopped at their own footprint instead of running on
        // into this gable. They extend properly now, so the patch only double-drew -
        // and being a full-height wedge it buried the wall between a low shed's surface
        // and this rake, which is siding and belongs on show.

        // Draw gable triangle outline - sloped edges only (no horizontal base needed)
        // The horizontal base at roofBaseY is part of the siding pattern
        // If there's a valley on one side, skip that slope line (it connects to another roof)

        // Pre-calculate valley flags for use in both outline and fascia drawing
        // Check ALL valley connections, not just the first one
        // Only a connection that actually produced a valley cuts the rake short. Set
        // from the mere presence of a neighbour, these flags dropped the rake line on
        // both slopes while the fascia still ran its full length - so the roof edge
        // vanished and the band beneath it carried straight on past the roof it met.
        const leftHasValley = !!valleyClipLeft;
        const rightHasValley = !!valleyClipRight;

        // The rake and its fascia always run the full slope to the eave. Unlike the
        // wall behind them, they are the roof's own edge and project outward past it,
        // so the roof abutting this gable tucks under them rather than covering them
        // - stopped at the valley instead, the fascia board went missing for the last
        // few feet down to the eave. Without overhang for the outline, with it for
        // the fascia.
        const leftRakeStart = { x: roofOffsetX, y: roofBaseY };
        const rightRakeEnd = { x: roofOffsetX + roofElevWidth, y: roofBaseY };
        const leftFasciaStart = { x: roofOffsetX - roofDrawOverhang, y: roofBaseY };
        const rightFasciaEnd = { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY };
        console.log(`  Valley flags from ${currentRoof.valleyConnections?.length || 0} connections: leftHasValley=${leftHasValley}, rightHasValley=${rightHasValley}`);

        ctx.strokeStyle = wallStyle.stroke;
        ctx.lineWidth = isSketchyMode ? 1.5 : 1;

        // Draw sloped edges - skip the side with valley connection
        if (isSketchyMode) {
          drawSketchyLine(ctx, leftRakeStart.x, leftRakeStart.y, roofOffsetX + roofElevWidth / 2, roofBaseY - roofDrawPeak);
          drawSketchyLine(ctx, roofOffsetX + roofElevWidth / 2, roofBaseY - roofDrawPeak, rightRakeEnd.x, rightRakeEnd.y);
        } else {
          ctx.beginPath();
          ctx.moveTo(leftRakeStart.x, leftRakeStart.y);
          ctx.lineTo(roofOffsetX + roofElevWidth / 2, roofBaseY - roofDrawPeak);
          ctx.lineTo(rightRakeEnd.x, rightRakeEnd.y);
          ctx.stroke();
        }

        // Draw fascia boards running up the slope to the ridge
        // Fascia hangs vertically below the roof edge (covers rafter ends)
        const slopedFasciaHeight = fasciaHeight;
        // peakX and peakY already defined above for siding patterns

        // Draw fascia boards - skip white background masking to avoid covering other roofs' fascias
        // Now draw with normal fascia style
        ctx.fillStyle = roofStyle.fasciaFill;
        ctx.strokeStyle = roofStyle.stroke;
        ctx.lineWidth = 1;

        // leftHasValley and rightHasValley already calculated above for gable outline drawing

        console.log(`  Fascia drawing: valleyConnection=${valleyConnection ? 'exists' : 'null'}, gableSlopeDirection=${valleyConnection?.gableSlopeDirection}, leftHasValley=${leftHasValley}, rightHasValley=${rightHasValley}`);

        // Left fascia - runs from left eave up to peak (always draw)
        ctx.beginPath();
        ctx.moveTo(leftFasciaStart.x, leftFasciaStart.y);
        ctx.lineTo(peakX, peakY);
        ctx.lineTo(peakX, peakY + slopedFasciaHeight);
        ctx.lineTo(leftFasciaStart.x, leftFasciaStart.y + slopedFasciaHeight);
        ctx.closePath();
        ctx.fill();
        // Stroke left fascia - all edges except top slope (drawn separately as emphasized line)
        ctx.beginPath();
        ctx.moveTo(peakX, peakY);
        ctx.lineTo(peakX, peakY + slopedFasciaHeight);
        ctx.lineTo(leftFasciaStart.x, leftFasciaStart.y + slopedFasciaHeight);
        ctx.lineTo(leftFasciaStart.x, leftFasciaStart.y);
        ctx.stroke();

        // Right fascia - runs from right eave up to peak (always draw)
        ctx.beginPath();
        ctx.moveTo(rightFasciaEnd.x, rightFasciaEnd.y);
        ctx.lineTo(peakX, peakY);
        ctx.lineTo(peakX, peakY + slopedFasciaHeight);
        ctx.lineTo(rightFasciaEnd.x, rightFasciaEnd.y + slopedFasciaHeight);
        ctx.closePath();
        ctx.fill();
        // Stroke right fascia - all edges except top slope (drawn separately as emphasized line)
        ctx.beginPath();
        ctx.moveTo(peakX, peakY);
        ctx.lineTo(peakX, peakY + slopedFasciaHeight);
        ctx.lineTo(rightFasciaEnd.x, rightFasciaEnd.y + slopedFasciaHeight);
        ctx.lineTo(rightFasciaEnd.x, rightFasciaEnd.y);
        ctx.stroke();

        // Fascia returns - small horizontal pieces at eave level (at corners only)
        ctx.fillStyle = roofStyle.fasciaFill;

        console.log(`  Drawing fascia returns: roofOffsetX=${roofOffsetX.toFixed(1)}, roofElevWidth=${roofElevWidth.toFixed(1)}, roofBaseY=${roofBaseY.toFixed(1)}, overhang=${roofDrawOverhang.toFixed(1)}`);
        console.log(`    Left return: x=${(roofOffsetX - roofDrawOverhang).toFixed(1)} to ${roofOffsetX.toFixed(1)}, y=${roofBaseY.toFixed(1)} to ${(roofBaseY + slopedFasciaHeight).toFixed(1)}`);
        console.log(`    Right return: x=${(roofOffsetX + roofElevWidth).toFixed(1)} to ${(roofOffsetX + roofElevWidth + roofDrawOverhang).toFixed(1)}, y=${roofBaseY.toFixed(1)} to ${(roofBaseY + slopedFasciaHeight).toFixed(1)}`);

        // Left fascia return piece
        ctx.beginPath();
        ctx.moveTo(roofOffsetX - roofDrawOverhang, roofBaseY);
        ctx.lineTo(roofOffsetX, roofBaseY);
        ctx.lineTo(roofOffsetX, roofBaseY + slopedFasciaHeight);
        ctx.lineTo(roofOffsetX - roofDrawOverhang, roofBaseY + slopedFasciaHeight);
        ctx.closePath();
        ctx.fill();

        // Right fascia return piece
        ctx.beginPath();
        ctx.moveTo(roofOffsetX + roofElevWidth, roofBaseY);
        ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, roofBaseY);
        ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, roofBaseY + slopedFasciaHeight);
        ctx.lineTo(roofOffsetX + roofElevWidth, roofBaseY + slopedFasciaHeight);
        ctx.closePath();
        ctx.fill();

        // Outline the fascia returns (bottom edge and vertical terminating lines)
        ctx.strokeStyle = roofStyle.stroke;
        ctx.lineWidth = 1;
        // Left fascia return - bottom edge
        ctx.beginPath();
        ctx.moveTo(roofOffsetX - roofDrawOverhang, roofBaseY + slopedFasciaHeight);
        ctx.lineTo(roofOffsetX, roofBaseY + slopedFasciaHeight);
        ctx.stroke();
        // Left fascia return - outer vertical line (at overhang edge)
        ctx.beginPath();
        ctx.moveTo(roofOffsetX - roofDrawOverhang, roofBaseY);
        ctx.lineTo(roofOffsetX - roofDrawOverhang, roofBaseY + slopedFasciaHeight);
        ctx.stroke();
        // Left fascia return - inner vertical line (where it meets the sloped fascia)
        // Calculate where the sloped fascia bottom edge intersects x = roofOffsetX
        const leftFasciaBottomStartX = roofOffsetX - roofDrawOverhang;
        const leftFasciaBottomStartY = roofBaseY + slopedFasciaHeight;
        const leftFasciaBottomEndX = peakX;
        const leftFasciaBottomEndY = peakY + slopedFasciaHeight;
        const leftT = (roofOffsetX - leftFasciaBottomStartX) / (leftFasciaBottomEndX - leftFasciaBottomStartX);
        const leftInnerTopY = leftFasciaBottomStartY + leftT * (leftFasciaBottomEndY - leftFasciaBottomStartY);
        ctx.beginPath();
        ctx.moveTo(roofOffsetX, roofBaseY + slopedFasciaHeight);
        ctx.lineTo(roofOffsetX, leftInnerTopY);
        ctx.stroke();
        // Right fascia return - bottom edge
        ctx.beginPath();
        ctx.moveTo(roofOffsetX + roofElevWidth, roofBaseY + slopedFasciaHeight);
        ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, roofBaseY + slopedFasciaHeight);
        ctx.stroke();
        // Right fascia return - inner vertical line (where it meets the sloped fascia)
        const rightFasciaBottomStartX = roofOffsetX + roofElevWidth + roofDrawOverhang;
        const rightFasciaBottomStartY = roofBaseY + slopedFasciaHeight;
        const rightFasciaBottomEndX = peakX;
        const rightFasciaBottomEndY = peakY + slopedFasciaHeight;
        const rightInnerX = roofOffsetX + roofElevWidth;
        const rightT = (rightInnerX - rightFasciaBottomStartX) / (rightFasciaBottomEndX - rightFasciaBottomStartX);
        const rightInnerTopY = rightFasciaBottomStartY + rightT * (rightFasciaBottomEndY - rightFasciaBottomStartY);
        ctx.beginPath();
        ctx.moveTo(rightInnerX, roofBaseY + slopedFasciaHeight);
        ctx.lineTo(rightInnerX, rightInnerTopY);
        ctx.stroke();
        // Right fascia return - outer vertical line (at overhang edge)
        ctx.beginPath();
        ctx.moveTo(roofOffsetX + roofElevWidth + roofDrawOverhang, roofBaseY);
        ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, roofBaseY + slopedFasciaHeight);
        ctx.stroke();

        // Draw the outer roof line (top edge of fascia) with emphasis
        // Always use full overhang positions - the gable slope angle must be consistent
        ctx.strokeStyle = roofStyle.stroke;
        ctx.lineWidth = roofStyle.lineWidth;
        // Stops on the valley like the fascia beneath it - drawn to the eave regardless,
        // this was the heavy line that carried on straight through the abutting roof.
        if (isSketchyMode) {
          drawSketchyLine(ctx, leftFasciaStart.x, leftFasciaStart.y, peakX, peakY);
          drawSketchyLine(ctx, peakX, peakY, rightFasciaEnd.x, rightFasciaEnd.y);
        } else {
          ctx.beginPath();
          ctx.moveTo(leftFasciaStart.x, leftFasciaStart.y);
          ctx.lineTo(peakX, peakY);
          ctx.lineTo(rightFasciaEnd.x, rightFasciaEnd.y);
          ctx.stroke();
        }

        // Valley line / Ridge extension for shed-gable connections
        // When a gable END view meets a shed roof, the gable's ridge extends horizontally
        // to meet the shed's sloped surface
        if (valleyConnection && valleyConnection.connectionType === 'shed-gable' && valleyConnection.shedRoof) {
          const shedRoof = valleyConnection.shedRoof;
          const gableSlopeDirection = valleyConnection.gableSlopeDirection;

          // Calculate where the shed roof surface is at the ridge height (peakY)
          const shedEdge = shedRoof.shedSlopeEdge || 'north';
          const shedPitchDef = ROOF_PITCHES[shedRoof.pitch] || ROOF_PITCHES['6:12'];
          const shedBounds = shedRoof.bounds;
          const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
          const shedRunDim = slopeRunsNS ? (shedBounds.maxY - shedBounds.minY) : (shedBounds.maxX - shedBounds.minX);
          const shedPeakHeight = (shedRunDim * (6 / GRID_SIZE)) * (shedPitchDef.rise / shedPitchDef.run) * elevScale;

          // Determine which side of shed is high based on elevation
          let shedLeftIsHigh;
          if (shedEdge === 'north') {
            shedLeftIsHigh = elevation === 'east';
          } else if (shedEdge === 'south') {
            shedLeftIsHigh = elevation === 'west';
          } else if (shedEdge === 'east') {
            shedLeftIsHigh = elevation === 'south';
          } else {
            shedLeftIsHigh = elevation === 'north';
          }

          const shedLeftY = shedLeftIsHigh ? (roofBaseY - shedPeakHeight) : roofBaseY;
          const shedRightY = shedLeftIsHigh ? roofBaseY : (roofBaseY - shedPeakHeight);
          const shedLeftX = shedRoof.screenX;
          const shedRightX = shedRoof.screenX + shedRoof.screenWidth;

          // Find where the horizontal ridge line at peakY intersects the shed's sloped surface
          // Shed slope: Y = shedLeftY + (shedRightY - shedLeftY) * (X - shedLeftX) / (shedRightX - shedLeftX)
          // Solve for X when Y = peakY
          const shedSlopeRise = shedRightY - shedLeftY;
          const shedSlopeRun = shedRightX - shedLeftX;

          if (Math.abs(shedSlopeRise) > 0.1) {
            const t = (peakY - shedLeftY) / shedSlopeRise;
            const ridgeIntersectX = shedLeftX + t * shedSlopeRun;

            // Only draw if intersection is valid (within shed bounds)
            if (ridgeIntersectX >= shedLeftX && ridgeIntersectX <= shedRightX) {
              // Draw horizontal ridge extension from gable peak to shed intersection
              ctx.strokeStyle = roofStyle.stroke;
              ctx.lineWidth = roofStyle.lineWidth;
              ctx.beginPath();

              if (gableSlopeDirection === 'left') {
                // Gable is to the right of shed - extend ridge to the LEFT
                ctx.moveTo(peakX, peakY);
                ctx.lineTo(ridgeIntersectX, peakY);
              } else {
                // Gable is to the left of shed - extend ridge to the RIGHT
                ctx.moveTo(peakX, peakY);
                ctx.lineTo(ridgeIntersectX, peakY);
              }
              ctx.stroke();

              console.log(`  Ridge extension drawn: from gable peak (${peakX.toFixed(1)}, ${peakY.toFixed(1)}) to shed intersection (${ridgeIntersectX.toFixed(1)}, ${peakY.toFixed(1)})`);
            }
          }
        }

        // Store outline for selection highlight (always full gable)
        roofOutlinePoints = [
          { x: roofOffsetX - roofDrawOverhang, y: roofBaseY + slopedFasciaHeight },
          { x: roofOffsetX - roofDrawOverhang, y: roofBaseY },
          { x: peakX, y: peakY },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY + slopedFasciaHeight },
        ];
      } else {
        // Gable SIDE view - see roof surface sloping away + fascia board
        // The visible depth should match the actual roof height from the gable end
        console.log(`>>> GABLE SIDE VIEW: roofIndex=${roofIndex}, elevation=${elevation}`);
        const visibleRoofDepth = roofDrawPeak > 0 ? roofDrawPeak : fasciaHeight * 2;

        // Check if this SIDE roof is adjacent to a gable END roof
        // If so, extend the flat roof surface to where it intersects the gable slope
        let leftX = roofOffsetX - roofDrawOverhang;
        let rightX = roofOffsetX + roofElevWidth + roofDrawOverhang;
        let valleyConnection = null;
        let valleyIntersectX = null;
        let shedGableConnection = null;  // For shed-gable where this gable is in SIDE view

        if (currentRoof.valleyConnections && currentRoof.valleyConnections.length > 0) {
          // Check for shed-gable connection where this gable is in SIDE view
          const shedConnection = currentRoof.valleyConnections.find(conn =>
            conn.connectionType === 'shed-gable' &&
            conn.gableRoof === currentRoof &&
            conn.isGableSideView
          );
          if (shedConnection) {
            shedGableConnection = shedConnection;
          }

          const potentialConnection = currentRoof.valleyConnections.find(conn => conn.sideRoof === currentRoof);
          if (potentialConnection) {
            const { endRoof, gableSlopeDirection } = potentialConnection;

            // Calculate where the gable slope intersects the SIDE roof's top surface
            // This is where the ridge line should extend to
            const gablePeakX = endRoof.screenX + endRoof.screenWidth / 2;
            const gablePeakY = roofBaseY - endRoof.drawPeakHeight;
            const gableCornerX = gableSlopeDirection === 'left'
              ? endRoof.screenX
              : endRoof.screenX + endRoof.screenWidth;
            const gableCornerY = roofBaseY;

            // Calculate intersection with SIDE roof top
            const sideRoofTopY = roofBaseY - visibleRoofDepth;
            const slopeRun = gableCornerX - gablePeakX;
            const slopeRise = gableCornerY - gablePeakY;
            const t = (sideRoofTopY - gablePeakY) / slopeRise;
            valleyIntersectX = gablePeakX + slopeRun * t;

            // Only apply valley connection if the intersection is valid:
            // 1. t must be between 0 and 1 (intersection on the slope)
            // 2. Intersection X must be between peak and corner
            const intersectOnSlope = t > 0 && t < 1;
            const clipBetweenPeakAndCorner = (gableSlopeDirection === 'right' && valleyIntersectX > gablePeakX && valleyIntersectX < gableCornerX) ||
                                              (gableSlopeDirection === 'left' && valleyIntersectX < gablePeakX && valleyIntersectX > gableCornerX);

            if (intersectOnSlope && clipBetweenPeakAndCorner) {
              valleyConnection = potentialConnection;
              // Extend the SIDE roof to the intersection point
              if (gableSlopeDirection === 'right') {
                // Gable is to the left - extend left edge to intersection
                leftX = valleyIntersectX;
              } else {
                // Gable is to the right - extend right edge to intersection
                rightX = valleyIntersectX;
              }
            }
          }
        }

        const topY = roofBaseY - visibleRoofDepth;
        const bottomY = roofBaseY;

        // Draw roof surface
        const normalLeftX = roofOffsetX - roofDrawOverhang;
        const normalRightX = roofOffsetX + roofElevWidth + roofDrawOverhang;
        ctx.fillStyle = roofStyle.fasciaFill;

        // Fill the roof surface (no stroke - strokes drawn separately to avoid diagonal line)
        // When connected to a gable, use polygon that follows the gable slope
        // so we don't draw into the gable wall triangle area
        ctx.strokeStyle = roofStyle.stroke;
        ctx.lineWidth = 1;
        if (valleyConnection) {
          const { gableSlopeDirection, endRoof } = valleyConnection;
          const gablePeakX = endRoof.screenX + endRoof.screenWidth / 2;
          const gablePeakY = roofBaseY - endRoof.drawPeakHeight;
          const slopeRise = roofBaseY - gablePeakY;
          const tTop = (topY - gablePeakY) / slopeRise;

          // gableSlopeDirection indicates which slope of the gable faces the SIDE roof
          // 'left' = gable's LEFT slope faces SIDE roof (SIDE is to LEFT of gable)
          // 'right' = gable's RIGHT slope faces SIDE roof (SIDE is to RIGHT of gable)
          if (gableSlopeDirection === 'right') {
            // SIDE roof is to the RIGHT of gable - gable's right slope faces us
            const gableRightCornerX = endRoof.screenX + endRoof.screenWidth;
            const slopeRun = gableRightCornerX - gablePeakX;
            const leftAtTop = gablePeakX + slopeRun * tTop;

            // Fill polygon (no stroke here - strokes handled separately)
            ctx.beginPath();
            ctx.moveTo(leftAtTop, topY);
            ctx.lineTo(normalRightX, topY);
            ctx.lineTo(normalRightX, bottomY);
            ctx.lineTo(gableRightCornerX, bottomY);
            ctx.closePath();
            ctx.fill();

            // Stroke only far edges - NOT diagonal or bottom (gable covers those)
            // Right vertical edge and top edge
            ctx.beginPath();
            ctx.moveTo(normalRightX, bottomY);
            ctx.lineTo(normalRightX, topY);
            ctx.lineTo(leftAtTop, topY);
            ctx.stroke();
          } else {
            // SIDE roof is to the LEFT of gable - gable's left slope faces us
            const gableLeftCornerX = endRoof.screenX;
            const slopeRun = gableLeftCornerX - gablePeakX;
            const rightAtTop = gablePeakX + slopeRun * tTop;

            // Fill polygon (no stroke here - strokes handled separately)
            ctx.beginPath();
            ctx.moveTo(normalLeftX, topY);
            ctx.lineTo(rightAtTop, topY);
            ctx.lineTo(gableLeftCornerX, bottomY);
            ctx.lineTo(normalLeftX, bottomY);
            ctx.closePath();
            ctx.fill();

            // Stroke only far edges - NOT diagonal or bottom (gable covers those)
            // Left vertical edge and top edge
            ctx.beginPath();
            ctx.moveTo(normalLeftX, bottomY);
            ctx.lineTo(normalLeftX, topY);
            ctx.lineTo(rightAtTop, topY);
            ctx.stroke();
          }
        } else if (shedGableConnection) {
          // Shed-gable connection: extend roof surface to meet shed's sloped surface
          const shedRoof = shedGableConnection.shedRoof;
          const shedEdge = shedRoof.shedSlopeEdge || 'north';
          const shedPitchDef = ROOF_PITCHES[shedRoof.pitch] || ROOF_PITCHES['6:12'];
          const shedBounds = shedRoof.bounds;
          const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
          const shedRunDim = slopeRunsNS ? (shedBounds.maxY - shedBounds.minY) : (shedBounds.maxX - shedBounds.minX);
          const shedPeakHeight = (shedRunDim * (6 / GRID_SIZE)) * (shedPitchDef.rise / shedPitchDef.run) * elevScale;

          // Determine which side of shed is high based on elevation
          let shedLeftIsHigh;
          if (shedEdge === 'north') {
            shedLeftIsHigh = elevation === 'east';
          } else if (shedEdge === 'south') {
            shedLeftIsHigh = elevation === 'west';
          } else if (shedEdge === 'east') {
            shedLeftIsHigh = elevation === 'south';
          } else {
            shedLeftIsHigh = elevation === 'north';
          }

          const shedLeftY = shedLeftIsHigh ? (roofBaseY - shedPeakHeight) : roofBaseY;
          const shedRightY = shedLeftIsHigh ? roofBaseY : (roofBaseY - shedPeakHeight);
          const shedLeftX = shedRoof.screenX;
          const shedRightX = shedRoof.screenX + shedRoof.screenWidth;

          // Find where the horizontal top edge at topY intersects the shed's sloped surface
          const shedSlopeRise = shedRightY - shedLeftY;
          const shedSlopeRun = shedRightX - shedLeftX;

          // Determine which direction to extend based on relative positions
          const gableCenterX = roofOffsetX + roofElevWidth / 2;
          const shedCenterX = (shedLeftX + shedRightX) / 2;

          let extendedLeftX = normalLeftX;
          let extendedRightX = normalRightX;

          if (Math.abs(shedSlopeRise) > 0.1) {
            const t = (topY - shedLeftY) / shedSlopeRise;
            const intersectX = shedLeftX + t * shedSlopeRun;

            if (shedCenterX < gableCenterX) {
              // Shed is to the left - extend left edge
              extendedLeftX = Math.min(normalLeftX, intersectX);
            } else {
              // Shed is to the right - extend right edge
              extendedRightX = Math.max(normalRightX, intersectX);
            }
          }

          // Draw extended roof surface as polygon to handle the sloped shed edge
          ctx.beginPath();
          if (shedCenterX < gableCenterX) {
            // Shed on left - polygon extends left to meet shed slope
            const shedIntersectBottomT = (bottomY - shedLeftY) / shedSlopeRise;
            const shedIntersectBottomX = shedLeftX + shedIntersectBottomT * shedSlopeRun;
            ctx.moveTo(extendedLeftX, topY);
            ctx.lineTo(normalRightX, topY);
            ctx.lineTo(normalRightX, bottomY);
            ctx.lineTo(shedIntersectBottomX, bottomY);
            ctx.closePath();
            ctx.fill();
            // Only stroke the visible edges (top and right), not the bottom edge
            ctx.beginPath();
            ctx.moveTo(normalRightX, bottomY);
            ctx.lineTo(normalRightX, topY);
            ctx.lineTo(extendedLeftX, topY);
            ctx.stroke();
          } else {
            // Shed on right - polygon extends right to meet shed slope
            const shedIntersectBottomT = (bottomY - shedLeftY) / shedSlopeRise;
            const shedIntersectBottomX = shedLeftX + shedIntersectBottomT * shedSlopeRun;
            ctx.moveTo(normalLeftX, topY);
            ctx.lineTo(extendedRightX, topY);
            ctx.lineTo(shedIntersectBottomX, bottomY);
            ctx.lineTo(normalLeftX, bottomY);
            ctx.closePath();
            ctx.fill();
            // Only stroke the visible edges (top and left), not the bottom edge
            ctx.beginPath();
            ctx.moveTo(normalLeftX, bottomY);
            ctx.lineTo(normalLeftX, topY);
            ctx.lineTo(extendedRightX, topY);
            ctx.stroke();
          }
        } else {
          ctx.fillRect(normalLeftX, topY, normalRightX - normalLeftX, visibleRoofDepth);
          ctx.strokeRect(normalLeftX, topY, normalRightX - normalLeftX, visibleRoofDepth);
        }

        // Draw fascia board below roof
        // When there's a valley, stop at gable's overhang edge - gable's sloped fascia starts there
        // When there's a shed connection, stop at the shed's eave line
        let fasciaLeftX = roofOffsetX - roofDrawOverhang;
        let fasciaRightX = roofOffsetX + roofElevWidth + roofDrawOverhang;
        if (valleyConnection) {
          const { gableSlopeDirection, endRoof } = valleyConnection;
          const gableOverhang = endRoof.drawOverhang || 12;
          if (gableSlopeDirection === 'right') {
            // SIDE is to RIGHT of gable - fascia starts at gable's right overhang edge
            fasciaLeftX = endRoof.screenX + endRoof.screenWidth + gableOverhang;
          } else {
            // SIDE is to LEFT of gable - fascia ends at gable's left overhang edge
            fasciaRightX = endRoof.screenX - gableOverhang;
          }
        } else if (shedGableConnection) {
          // Shed-gable connection: stop fascia at shed's overhang edge
          const shedRoof = shedGableConnection.shedRoof;
          const shedOverhang = shedRoof.drawOverhang || 12;
          const shedLeftX = shedRoof.screenX;
          const shedRightX = shedRoof.screenX + shedRoof.screenWidth;
          const gableCenterX = roofOffsetX + roofElevWidth / 2;
          const shedCenterX = (shedLeftX + shedRightX) / 2;

          if (shedCenterX < gableCenterX) {
            // Shed is to the left - fascia starts at shed's right overhang edge
            fasciaLeftX = shedRightX + shedOverhang;
          } else {
            // Shed is to the right - fascia ends at shed's left overhang edge
            fasciaRightX = shedLeftX - shedOverhang;
          }
        }
        console.log(`GABLE SIDE VIEW FASCIA: roofIndex=${roofIndex}, elevation=${elevation}, fasciaLeftX=${fasciaLeftX.toFixed(1)}, fasciaRightX=${fasciaRightX.toFixed(1)}, bottomY=${bottomY.toFixed(1)}, fasciaHeight=${fasciaHeight}, hasValleyConn=${!!valleyConnection}, hasShedGableConn=${!!shedGableConnection}`);
        ctx.fillStyle = roofStyle.fasciaFill;
        ctx.fillRect(fasciaLeftX, bottomY, fasciaRightX - fasciaLeftX, fasciaHeight);
        ctx.strokeStyle = roofStyle.stroke;
        ctx.lineWidth = 1;
        ctx.strokeRect(fasciaLeftX, bottomY, fasciaRightX - fasciaLeftX, fasciaHeight);

        // Top line (ridge/back edge) emphasized - extends to valley intersection
        // This line represents where the SIDE roof top meets the gable slope
        ctx.lineWidth = roofStyle.lineWidth;
        ctx.beginPath();
        let ridgeLeftX = normalLeftX;
        let ridgeRightX = normalRightX;
        if (valleyConnection) {
          const { gableSlopeDirection, endRoof } = valleyConnection;
          const gablePeakX = endRoof.screenX + endRoof.screenWidth / 2;
          const gablePeakY = roofBaseY - endRoof.drawPeakHeight;
          const slopeRise = roofBaseY - gablePeakY;
          const tTop = (topY - gablePeakY) / slopeRise;

          // gableSlopeDirection indicates which slope of the gable faces the SIDE roof
          // 'left' = gable's LEFT slope faces SIDE roof (SIDE is to left of gable)
          // 'right' = gable's RIGHT slope faces SIDE roof (SIDE is to right of gable)
          if (gableSlopeDirection === 'right') {
            // Gable's RIGHT slope faces SIDE roof - SIDE roof is to the RIGHT of gable
            // Ridge extends LEFT to meet gable's right slope
            const gableRightCornerX = endRoof.screenX + endRoof.screenWidth;
            const slopeRun = gableRightCornerX - gablePeakX;
            ridgeLeftX = gablePeakX + slopeRun * tTop;
          } else {
            // Gable's LEFT slope faces SIDE roof - SIDE roof is to the LEFT of gable
            // Ridge extends RIGHT to meet gable's left slope
            const gableLeftCornerX = endRoof.screenX;
            const slopeRun = gableLeftCornerX - gablePeakX;
            ridgeRightX = gablePeakX + slopeRun * tTop;
          }
        }
        ctx.moveTo(ridgeLeftX, topY);
        ctx.lineTo(ridgeRightX, topY);
        ctx.stroke();
        console.log(`SIDE roof ridge line: from x=${ridgeLeftX.toFixed(1)} to x=${ridgeRightX.toFixed(1)} at y=${topY.toFixed(1)}`);

        // Shed-gable connection: extend ridge to meet shed's sloped surface
        // When this gable is in SIDE view, the ridge is visible as a horizontal line
        // and should extend to intersect with the adjacent shed's sloped surface
        if (shedGableConnection) {
          const shedRoof = shedGableConnection.shedRoof;
          const shedEdge = shedRoof.shedSlopeEdge || 'north';
          const shedPitchDef = ROOF_PITCHES[shedRoof.pitch] || ROOF_PITCHES['6:12'];
          const shedBounds = shedRoof.bounds;
          const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
          const shedRunDim = slopeRunsNS ? (shedBounds.maxY - shedBounds.minY) : (shedBounds.maxX - shedBounds.minX);
          const shedPeakHeight = (shedRunDim * (6 / GRID_SIZE)) * (shedPitchDef.rise / shedPitchDef.run) * elevScale;

          // Determine which side of shed is high based on elevation
          let shedLeftIsHigh;
          if (shedEdge === 'north') {
            shedLeftIsHigh = elevation === 'east';
          } else if (shedEdge === 'south') {
            shedLeftIsHigh = elevation === 'west';
          } else if (shedEdge === 'east') {
            shedLeftIsHigh = elevation === 'south';
          } else {
            shedLeftIsHigh = elevation === 'north';
          }

          const shedLeftY = shedLeftIsHigh ? (roofBaseY - shedPeakHeight) : roofBaseY;
          const shedRightY = shedLeftIsHigh ? roofBaseY : (roofBaseY - shedPeakHeight);
          const shedLeftX = shedRoof.screenX;
          const shedRightX = shedRoof.screenX + shedRoof.screenWidth;

          // Find where the horizontal ridge line at topY intersects the shed's sloped surface
          const shedSlopeRise = shedRightY - shedLeftY;
          const shedSlopeRun = shedRightX - shedLeftX;

          if (Math.abs(shedSlopeRise) > 0.1) {
            const t = (topY - shedLeftY) / shedSlopeRise;
            const ridgeIntersectX = shedLeftX + t * shedSlopeRun;

            // Determine which direction to extend based on relative positions
            const gableCenterX = roofOffsetX + roofElevWidth / 2;
            const shedCenterX = (shedLeftX + shedRightX) / 2;

            // Only draw if intersection is valid (within shed bounds or close)
            if (ridgeIntersectX >= shedLeftX - 10 && ridgeIntersectX <= shedRightX + 10) {
              ctx.strokeStyle = roofStyle.stroke;
              ctx.lineWidth = roofStyle.lineWidth;
              ctx.beginPath();

              if (shedCenterX < gableCenterX) {
                // Shed is to the left - extend ridge to the LEFT
                ctx.moveTo(ridgeLeftX, topY);
                ctx.lineTo(ridgeIntersectX, topY);
              } else {
                // Shed is to the right - extend ridge to the RIGHT
                ctx.moveTo(ridgeRightX, topY);
                ctx.lineTo(ridgeIntersectX, topY);
              }
              ctx.stroke();

              console.log(`  Gable SIDE view ridge extension: from gable ridge to shed intersection at x=${ridgeIntersectX.toFixed(1)}, topY=${topY.toFixed(1)}`);
            }
          }
        }

        // Note: No separate valley line is drawn here
        // The valley intersection is represented by the ridge line extending to meet the gable slope
        // In this elevation view, the valley runs along the top of the SIDE roof surface

        // Eave line emphasized - skip when there's a valley or shed connection
        // The fascia stroke already draws the eave edge, no need to duplicate
        if (!valleyConnection && !shedGableConnection) {
          ctx.beginPath();
          const eaveStartX = roofOffsetX - roofDrawOverhang;
          const eaveEndX = roofOffsetX + roofElevWidth + roofDrawOverhang;
          ctx.moveTo(eaveStartX, bottomY);
          ctx.lineTo(eaveEndX, bottomY);
          ctx.stroke();
        }

        // Store outline for selection highlight
        roofOutlinePoints = [
          { x: leftX, y: bottomY + fasciaHeight },
          { x: leftX, y: topY },
          { x: rightX, y: topY },
          { x: rightX, y: bottomY + fasciaHeight },
        ];
      }
    } else if (currentRoof.type === 'hip' && roofDrawPeak > 0) {
      // Hip roof - trapezoid or pyramid view
      const roofPoints = currentRoof.points || [
        { x: currentRoof.x, y: currentRoof.y },
        { x: currentRoof.x + currentRoof.width, y: currentRoof.y },
        { x: currentRoof.x + currentRoof.width, y: currentRoof.y + currentRoof.height },
        { x: currentRoof.x, y: currentRoof.y + currentRoof.height },
      ];
      const roofMinX = Math.min(...roofPoints.map(p => p.x));
      const roofMaxX = Math.max(...roofPoints.map(p => p.x));
      const roofMinY = Math.min(...roofPoints.map(p => p.y));
      const roofMaxY = Math.max(...roofPoints.map(p => p.y));
      const roofWidthModel = roofMaxX - roofMinX;
      const roofHeightModel = roofMaxY - roofMinY;

      const perpDim = isHorizontal ? roofHeightModel : roofWidthModel;
      const parallelDim = isHorizontal ? roofWidthModel : roofHeightModel;

      const hipInsetModel = perpDim / 2;
      const hipInsetInches = hipInsetModel * (6 / GRID_SIZE);
      const parallelInches = parallelDim * (6 / GRID_SIZE);
      const isPyramid = hipInsetInches * 2 >= parallelInches;

      const hipInsetPx = Math.min(hipInsetInches * elevScale, roofElevWidth / 2);

      ctx.fillStyle = roofStyle.fill;
      ctx.strokeStyle = roofStyle.stroke;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(roofOffsetX - roofDrawOverhang, roofBaseY);
      if (isPyramid) {
        ctx.lineTo(roofOffsetX + roofElevWidth / 2, roofBaseY - roofDrawPeak);
        // Store outline for selection highlight (pyramid)
        roofOutlinePoints = [
          { x: roofOffsetX - roofDrawOverhang, y: roofBaseY + fasciaHeight },
          { x: roofOffsetX - roofDrawOverhang, y: roofBaseY },
          { x: roofOffsetX + roofElevWidth / 2, y: roofBaseY - roofDrawPeak },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY + fasciaHeight },
        ];
      } else {
        ctx.lineTo(roofOffsetX + hipInsetPx, roofBaseY - roofDrawPeak);
        ctx.lineTo(roofOffsetX + roofElevWidth - hipInsetPx, roofBaseY - roofDrawPeak);
        // Store outline for selection highlight (trapezoid)
        roofOutlinePoints = [
          { x: roofOffsetX - roofDrawOverhang, y: roofBaseY + fasciaHeight },
          { x: roofOffsetX - roofDrawOverhang, y: roofBaseY },
          { x: roofOffsetX + hipInsetPx, y: roofBaseY - roofDrawPeak },
          { x: roofOffsetX + roofElevWidth - hipInsetPx, y: roofBaseY - roofDrawPeak },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY + fasciaHeight },
        ];
      }
      ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, roofBaseY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      // Fascia
      console.log(`HIP ROOF FASCIA: roofIndex=${roofIndex}, elevation=${elevation}`);
      ctx.fillStyle = roofStyle.fasciaFill;
      ctx.fillRect(roofOffsetX - roofDrawOverhang, roofBaseY, roofElevWidth + roofDrawOverhang * 2, fasciaHeight);
      ctx.strokeRect(roofOffsetX - roofDrawOverhang, roofBaseY, roofElevWidth + roofDrawOverhang * 2, fasciaHeight);
    } else if (currentRoof.type === 'shed') {
      const shedEdge = currentRoof.shedSlopeEdge || 'north';
      const slopeRunsNS = shedEdge === 'north' || shedEdge === 'south';
      const slopeRunsEW = shedEdge === 'east' || shedEdge === 'west';

      // Calculate the full roof peak height for high-side END views
      // (roofDrawPeak is 0 for END views, so calculate it here)
      const shedPitchDef = ROOF_PITCHES[currentRoof.pitch] || ROOF_PITCHES['6:12'];

      // Get roof dimensions - handle both points array and legacy format
      let shedRoofPoints;
      if (currentRoof.points && currentRoof.points.length >= 3) {
        shedRoofPoints = currentRoof.points;
      } else if (typeof currentRoof.x === 'number' && typeof currentRoof.width === 'number') {
        shedRoofPoints = [
          { x: currentRoof.x, y: currentRoof.y },
          { x: currentRoof.x + currentRoof.width, y: currentRoof.y },
          { x: currentRoof.x + currentRoof.width, y: currentRoof.y + currentRoof.height },
          { x: currentRoof.x, y: currentRoof.y + currentRoof.height },
        ];
      } else {
        // Fallback to wall bounds
        shedRoofPoints = [
          { x: allMinX, y: allMinY },
          { x: allMaxX, y: allMinY },
          { x: allMaxX, y: allMaxY },
          { x: allMinX, y: allMaxY },
        ];
      }
      const shedRoofWidth = Math.max(...shedRoofPoints.map(p => p.x)) - Math.min(...shedRoofPoints.map(p => p.x));
      const shedRoofHeight = Math.max(...shedRoofPoints.map(p => p.y)) - Math.min(...shedRoofPoints.map(p => p.y));

      // For shed roof, the run dimension depends on which way the slope runs:
      // - If slope runs N-S (slopeRunsNS): the run is the N-S dimension (roofHeight)
      // - If slope runs E-W (slopeRunsEW): the run is the E-W dimension (roofWidth)
      const shedRunDim = slopeRunsNS ? shedRoofHeight : shedRoofWidth;
      const shedPeakHeight = (shedRunDim * (6 / GRID_SIZE)) * (shedPitchDef.rise / shedPitchDef.run) * elevScale;

      // END view: looking at low or high end (N/S views when slope runs N-S, E/W views when slope runs E-W)
      // SLOPE view: looking perpendicular to slope direction (E/W views when slope runs N-S, N/S views when slope runs E-W)
      const isEndView = (slopeRunsNS && isHorizontal) || (slopeRunsEW && !isHorizontal);

      if (isEndView) {
        // End view - looking at one end of the shed roof
        const viewingLowSide = (elevation === shedEdge);

        if (viewingLowSide) {
          // Low side END view - looking at the eave/low side
          // The roof slopes UP and AWAY from the viewer toward the high side (back wall)
          // We should see:
          // 1. The back wall extending up to the high roof point
          // 2. The sloped roof surface going from low (front) to high (back)
          // 3. The fascia band at the low eave

          // The back wall extends up by the full roof height
          const highSideWallTop = roofBaseY - shedPeakHeight;

          // Draw roof surface - trapezoidal shape showing slope
          // Front edge is at roofBaseY (low), back edge is at highSideWallTop (high)
          // We show a "depth" to indicate the roof surface going away
          const roofDepth = Math.min(40 * elevScale, shedPeakHeight * 0.5); // Visual depth of roof surface

          ctx.fillStyle = roofStyle.fill;
          ctx.strokeStyle = roofStyle.stroke;
          ctx.lineWidth = roofStyle.lineWidth;

          // Define shed roof bounds
          let shedRoofLeftX = roofOffsetX - roofDrawOverhang;
          let shedRoofRightX = roofOffsetX + roofElevWidth + roofDrawOverhang;

          // Fill the roof surface
          ctx.beginPath();
          ctx.moveTo(shedRoofLeftX, roofBaseY);
          ctx.lineTo(shedRoofRightX, roofBaseY);
          ctx.lineTo(shedRoofRightX, highSideWallTop);
          ctx.lineTo(shedRoofLeftX, highSideWallTop);
          ctx.closePath();
          ctx.fill();

          // Stroke only the edges that don't overlap gable walls
          // Draw top, left, and right edges
          ctx.beginPath();
          ctx.moveTo(shedRoofLeftX, roofBaseY);
          ctx.lineTo(shedRoofLeftX, highSideWallTop);
          ctx.lineTo(shedRoofRightX, highSideWallTop);
          ctx.lineTo(shedRoofRightX, roofBaseY);
          ctx.stroke();

          // Draw bottom edge in segments, skipping gable overlap
          // Build segments for bottom edge (same logic as fascia)
          let bottomEdgeSegments = [{ left: shedRoofLeftX, right: shedRoofRightX }];
          for (const otherRoof of processedRoofs) {
            if (otherRoof === currentRoof) continue;
            if (otherRoof.type !== 'gable' || !otherRoof.isGableEnd) continue;

            const gableLeftX = otherRoof.screenX;
            const gableRightX = otherRoof.screenX + otherRoof.screenWidth;
            const gableOverhang = otherRoof.drawOverhang || 12;
            const gableExcludeLeft = gableLeftX - gableOverhang;
            const gableExcludeRight = gableRightX + gableOverhang;

            const newSegments = [];
            for (const seg of bottomEdgeSegments) {
              if (gableExcludeRight <= seg.left || gableExcludeLeft >= seg.right) {
                newSegments.push(seg);
              } else {
                if (seg.left < gableExcludeLeft) {
                  newSegments.push({ left: seg.left, right: gableExcludeLeft });
                }
                if (seg.right > gableExcludeRight) {
                  newSegments.push({ left: gableExcludeRight, right: seg.right });
                }
              }
            }
            bottomEdgeSegments = newSegments;
          }

          // Draw each bottom edge segment
          for (const seg of bottomEdgeSegments) {
            if (seg.right > seg.left) {
              ctx.beginPath();
              ctx.moveTo(seg.left, roofBaseY);
              ctx.lineTo(seg.right, roofBaseY);
              ctx.stroke();
            }
          }

          // Draw fascia band at the low eave
          // NOTE: Removed white background masking to prevent covering siding on adjacent walls
          // Check for any gable END view roofs that this shed fascia would overlap
          // We need to draw fascia in segments, skipping where gable walls are
          const shedFasciaFullLeftX = roofOffsetX - roofDrawOverhang;
          const shedFasciaFullRightX = roofOffsetX + roofElevWidth + roofDrawOverhang;

          // Collect segments to draw (initially the full width)
          let fasciaSegments = [{ left: shedFasciaFullLeftX, right: shedFasciaFullRightX }];

          // Check all other roofs to see if any gable END views overlap horizontally
          for (const otherRoof of processedRoofs) {
            if (otherRoof === currentRoof) continue;
            if (otherRoof.type !== 'gable' || !otherRoof.isGableEnd) continue;

            const gableLeftX = otherRoof.screenX;
            const gableRightX = otherRoof.screenX + otherRoof.screenWidth;
            const gableOverhang = otherRoof.drawOverhang || 12;
            const gableExcludeLeft = gableLeftX - gableOverhang;
            const gableExcludeRight = gableRightX + gableOverhang;

            // Split segments where gable overlaps
            const newSegments = [];
            for (const seg of fasciaSegments) {
              if (gableExcludeRight <= seg.left || gableExcludeLeft >= seg.right) {
                // No overlap - keep segment as is
                newSegments.push(seg);
              } else {
                // Overlap - split segment
                if (seg.left < gableExcludeLeft) {
                  newSegments.push({ left: seg.left, right: gableExcludeLeft });
                }
                if (seg.right > gableExcludeRight) {
                  newSegments.push({ left: gableExcludeRight, right: seg.right });
                }
              }
            }
            fasciaSegments = newSegments;
          }

          // Draw each fascia segment
          console.log(`SHED LOW-SIDE END VIEW FASCIA: roofIndex=${roofIndex}, elevation=${elevation}, segments=${fasciaSegments.length}`);
          ctx.fillStyle = roofStyle.fasciaFill;
          ctx.strokeStyle = roofStyle.stroke;
          ctx.lineWidth = 1;
          for (const seg of fasciaSegments) {
            const segWidth = seg.right - seg.left;
            if (segWidth > 0) {
              console.log(`  Drawing fascia segment: left=${seg.left.toFixed(1)}, right=${seg.right.toFixed(1)}`);
              ctx.fillRect(seg.left, roofBaseY, segWidth, fasciaHeight);
              if (isSketchyMode) {
                drawSketchyRect(ctx, seg.left, roofBaseY, segWidth, fasciaHeight);
              } else {
                ctx.strokeRect(seg.left, roofBaseY, segWidth, fasciaHeight);
              }
            }
          }

          // Draw ridge line at the top (back edge)
          ctx.strokeStyle = roofStyle.stroke;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(roofOffsetX - roofDrawOverhang, highSideWallTop);
          ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, highSideWallTop);
          ctx.stroke();

          // Store roof outline for selection
          roofOutlinePoints = [
            { x: roofOffsetX - roofDrawOverhang, y: roofBaseY + fasciaHeight },
            { x: roofOffsetX - roofDrawOverhang, y: highSideWallTop },
            { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: highSideWallTop },
            { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: roofBaseY + fasciaHeight },
          ];
        } else {
          // High side END view - looking at the high side (ridge end)
          // Structure from top to bottom:
          // 1. Ridge fascia board at top (same height as side fascia - fasciaHeight)
          // 2. Overhang/soffit area below fascia (based on roof overhang setting)
          // 3. Wall top - where the wall stops

          // The ridge is at roofBaseY - shedPeakHeight
          const ridgeY = roofBaseY - shedPeakHeight;

          // Ridge fascia uses same height as side fascia (fasciaHeight is already defined above)
          const ridgeFasciaBtmY = ridgeY + fasciaHeight;

          // Overhang depth - use the roof's overhang value
          const overhangDepthInches = currentRoof?.overhang || 12;
          const overhangDepth = overhangDepthInches * elevScale;

          // Wall top is at the bottom of the overhang area
          const wallTopY = ridgeFasciaBtmY + overhangDepth;

          // Draw wall extension from standard wall top (roofBaseY) up to soffit bottom
          // Wall extension height = distance from roofBaseY up to wallTopY (soffitBtmY)
          const wallExtensionHeight = roofBaseY - wallTopY;
          if (wallExtensionHeight > 0) {
            ctx.fillStyle = wallStyle.fill;
            ctx.fillRect(roofOffsetX, wallTopY, roofElevWidth, wallExtensionHeight);

            // Draw siding pattern in wall extension - match the main wall siding pattern
            ctx.strokeStyle = sidingStyle.stroke;
            ctx.lineWidth = sidingStyle.lineWidth;
            const shedSidingSpacing = 6 * elevScale;

            if (sidingPattern === 'horizontal') {
              // Horizontal lap siding
              for (let sy = roofBaseY - shedSidingSpacing; sy > wallTopY; sy -= shedSidingSpacing) {
                ctx.beginPath();
                ctx.moveTo(roofOffsetX, sy);
                ctx.lineTo(roofOffsetX + roofElevWidth, sy);
                ctx.stroke();
              }
            } else if (sidingPattern === 'board-batten') {
              // Vertical board-and-batten siding
              // IMPORTANT: Use same spacing reference as main wall (the wall's left edge) for alignment
              const boardSpacing = 24 * elevScale;
              const battenWidth = 3 * elevScale;
              const shedLeft = roofOffsetX;
              const shedRight = roofOffsetX + roofElevWidth;
              // Use GLOBAL spacing from the wall's left edge (same as main wall) for alignment
              for (let sx = wallLeftEdgeX + boardSpacing; sx < shedRight; sx += boardSpacing) {
                // Skip if outside shed horizontal bounds
                if (sx < shedLeft || sx > shedRight) continue;
                // Main board edge
                ctx.lineWidth = sidingStyle.lineWidth;
                ctx.beginPath();
                ctx.moveTo(sx, wallTopY);
                ctx.lineTo(sx, roofBaseY);
                ctx.stroke();
                // Batten (slightly offset, thicker look)
                const battenX = sx + battenWidth / 2;
                if (battenX <= shedRight && battenX >= shedLeft) {
                  ctx.lineWidth = sidingStyle.lineWidth * 2;
                  ctx.beginPath();
                  ctx.moveTo(battenX, wallTopY);
                  ctx.lineTo(battenX, roofBaseY);
                  ctx.stroke();
                }
              }
              ctx.lineWidth = sidingStyle.lineWidth;
            } else if (sidingPattern === 'shingle') {
              // Shingle/shake siding pattern
              const rowHeight = 10 * elevScale;
              const shingleWidth = 8 * elevScale;
              let shingleRowIndex = 0;
              for (let sy = roofBaseY - rowHeight; sy > wallTopY; sy -= rowHeight) {
                const rowOffset = (shingleRowIndex % 2) * (shingleWidth / 2);
                // Horizontal row line
                ctx.beginPath();
                ctx.moveTo(roofOffsetX, sy);
                ctx.lineTo(roofOffsetX + roofElevWidth, sy);
                ctx.stroke();
                // Vertical shingle joints
                for (let sx = roofOffsetX + rowOffset + shingleWidth; sx < roofOffsetX + roofElevWidth; sx += shingleWidth) {
                  ctx.beginPath();
                  ctx.moveTo(sx, sy);
                  ctx.lineTo(sx, sy + rowHeight);
                  ctx.stroke();
                }
                shingleRowIndex++;
              }
            }
          }

          // NO outline stroke on wall extension - this was causing the dark line at 9' level

          // Draw soffit area as one continuous piece spanning full width (including side overhangs)
          // This fills from ridge fascia bottom down to wall top, full width
          const soffitColor = roofStyle.soffitFill || '#d8d0c8';
          ctx.fillStyle = soffitColor;
          ctx.fillRect(roofOffsetX - roofDrawOverhang, ridgeFasciaBtmY, roofElevWidth + roofDrawOverhang * 2, overhangDepth);

          // Draw closing siding pattern at wallTopY AFTER soffit so it's not covered
          // This fills the gap between the last shingle row and the soffit
          if (wallExtensionHeight > 0) {
            ctx.strokeStyle = sidingStyle.stroke;
            ctx.lineWidth = sidingStyle.lineWidth;

            if (sidingPattern === 'shingle') {
              // Fill the gap with a complete partial row
              const rowHeight = 10 * elevScale;
              const shingleWidth = 8 * elevScale;

              // Find the last row Y position that was drawn
              let lastSy = roofBaseY - rowHeight;
              let rowCount = 0;
              while (lastSy - rowHeight > wallTopY) {
                lastSy -= rowHeight;
                rowCount++;
              }

              // Draw horizontal line at wallTopY
              ctx.beginPath();
              ctx.moveTo(roofOffsetX, wallTopY + 1);
              ctx.lineTo(roofOffsetX + roofElevWidth, wallTopY + 1);
              ctx.stroke();

              // Draw vertical joints from lastSy down to wallTopY
              const rowOffset = ((rowCount + 1) % 2) * (shingleWidth / 2);
              for (let sx = roofOffsetX + rowOffset + shingleWidth; sx < roofOffsetX + roofElevWidth; sx += shingleWidth) {
                ctx.beginPath();
                ctx.moveTo(sx, lastSy);
                ctx.lineTo(sx, wallTopY + 1);
                ctx.stroke();
              }
            } else if (sidingPattern === 'horizontal') {
              // Just draw closing line for horizontal siding
              ctx.beginPath();
              ctx.moveTo(roofOffsetX, wallTopY + 1);
              ctx.lineTo(roofOffsetX + roofElevWidth, wallTopY + 1);
              ctx.stroke();
            }
          }

          // Draw ridge fascia at top - same as side fascia boards
          // Goes all the way across (full width including side overhangs)
          console.log(`SHED HIGH-SIDE END VIEW RIDGE FASCIA: roofIndex=${roofIndex}, elevation=${elevation}`);
          ctx.fillStyle = roofStyle.fasciaFill;
          ctx.strokeStyle = '#333';
          ctx.lineWidth = 1.5;
          ctx.fillRect(roofOffsetX - roofDrawOverhang, ridgeY, roofElevWidth + roofDrawOverhang * 2, fasciaHeight);
          ctx.strokeRect(roofOffsetX - roofDrawOverhang, ridgeY, roofElevWidth + roofDrawOverhang * 2, fasciaHeight);

          // Draw left side eave/overhang - thin vertical strip from soffit bottom down to eave fascia
          const sideOverhangHeight = roofBaseY - wallTopY;
          ctx.fillStyle = soffitColor; // Same color as main soffit
          ctx.fillRect(roofOffsetX - roofDrawOverhang, wallTopY, roofDrawOverhang, sideOverhangHeight);

          // Draw right side eave/overhang - thin vertical strip
          ctx.fillRect(roofOffsetX + roofElevWidth, wallTopY, roofDrawOverhang, sideOverhangHeight);

          // Draw only the outer edges of the soffit area (no internal lines)
          // Use darker stroke for better visibility
          ctx.strokeStyle = '#333';
          ctx.lineWidth = 1.5;
          // Left outer edge (from ridge fascia bottom to eave fascia top)
          ctx.beginPath();
          ctx.moveTo(roofOffsetX - roofDrawOverhang, ridgeFasciaBtmY);
          ctx.lineTo(roofOffsetX - roofDrawOverhang, roofBaseY);
          ctx.stroke();
          // Right outer edge
          ctx.beginPath();
          ctx.moveTo(roofOffsetX + roofElevWidth + roofDrawOverhang, ridgeFasciaBtmY);
          ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, roofBaseY);
          ctx.stroke();

          // Draw fascia at bottom of side overhangs (at eave level)
          console.log(`SHED HIGH-SIDE END VIEW EAVE FASCIA: roofIndex=${roofIndex}, elevation=${elevation}`);
          ctx.fillStyle = roofStyle.fasciaFill;
          ctx.strokeStyle = '#333';
          ctx.lineWidth = 1.5;
          // Left eave fascia
          ctx.fillRect(roofOffsetX - roofDrawOverhang, roofBaseY, roofDrawOverhang, fasciaHeight);
          ctx.strokeRect(roofOffsetX - roofDrawOverhang, roofBaseY, roofDrawOverhang, fasciaHeight);
          // Right eave fascia
          ctx.fillRect(roofOffsetX + roofElevWidth, roofBaseY, roofDrawOverhang, fasciaHeight);
          ctx.strokeRect(roofOffsetX + roofElevWidth, roofBaseY, roofDrawOverhang, fasciaHeight);

          // Draw soffit bottom edge - only spans the wall width (not the side overhangs)
          // This defines the bottom of the soffit area where it meets the wall
          ctx.strokeStyle = '#333';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(roofOffsetX, wallTopY);
          ctx.lineTo(roofOffsetX + roofElevWidth, wallTopY);
          ctx.stroke();

          // Draw wall outline - extends from grade up to soffit bottom (wallTopY)
          ctx.strokeStyle = '#333';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(roofOffsetX, wallTopY, roofElevWidth, gradeY - wallTopY);

          // Store roof outline for selection (the ridge fascia area)
          roofOutlinePoints = [
            { x: roofOffsetX - roofDrawOverhang, y: ridgeY },
            { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: ridgeY },
            { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: ridgeFasciaBtmY },
            { x: roofOffsetX - roofDrawOverhang, y: ridgeFasciaBtmY },
          ];
        }
      } else {
        // Slope view - see sloped roof line from high to low
        console.log(`>>> SHED SLOPE VIEW: roofIndex=${roofIndex}, elevation=${elevation}`);
        // Determine which side (left or right) is high based on screen coordinate mapping:
        // flipX is true for north/east elevations, false for south/west elevations
        //
        // With flipX=false (south/west elevations):
        //   Screen LEFT = model WEST (low X), Screen RIGHT = model EAST (high X)
        // With flipX=true (north/east elevations):
        //   Screen LEFT = model EAST (high X), Screen RIGHT = model WEST (low X)
        //
        // For N/S elevations, we see E-W slopes (shedEdge is east or west)
        // For E/W elevations, we see N-S slopes (shedEdge is north or south)
        let leftIsHigh;
        if (shedEdge === 'north') {
          // North is LOW, South is HIGH
          // East elevation (flipX=true): left=East→model=South(HIGH), right=West→model=North(LOW) → leftIsHigh=true
          // West elevation (flipX=false): left=West→model=North(LOW), right=East→model=South(HIGH) → leftIsHigh=false
          leftIsHigh = elevation === 'east';
        } else if (shedEdge === 'south') {
          // South is LOW, North is HIGH
          // East elevation (flipX=true): left=East→model=South(LOW), right=West→model=North(HIGH) → leftIsHigh=false
          // West elevation (flipX=false): left=West→model=North(HIGH), right=East→model=South(LOW) → leftIsHigh=true
          leftIsHigh = elevation === 'west';
        } else if (shedEdge === 'east') {
          // East is LOW, West is HIGH
          // North elevation (flipX=true): left=East→model=East(LOW), right=West→model=West(HIGH) → leftIsHigh=false
          // South elevation (flipX=false): left=West→model=West(HIGH), right=East→model=East(LOW) → leftIsHigh=true
          leftIsHigh = elevation === 'south';
        } else { // west
          // West is LOW, East is HIGH
          // North elevation (flipX=true): left=East→model=West(LOW), right=West→model=East(HIGH) → leftIsHigh=false
          // South elevation (flipX=false): left=West→model=West(LOW), right=East→model=East(HIGH) → leftIsHigh=false
          leftIsHigh = elevation === 'north';
        }

        const leftRoofY = leftIsHigh ? (roofBaseY - roofDrawPeak) : roofBaseY;
        const rightRoofY = leftIsHigh ? roofBaseY : (roofBaseY - roofDrawPeak);
        const leftFasciaBottom = leftRoofY + fasciaHeight;
        const rightFasciaBottom = rightRoofY + fasciaHeight;

        // The fascia line spans the full width including overhangs
        // Calculate the slope of the fascia bottom line across the full overhang span
        const fullFasciaWidth = roofElevWidth + 2 * roofDrawOverhang;
        const fasciaBtmSlope = (rightFasciaBottom - leftFasciaBottom) / fullFasciaWidth;

        // Calculate fascia bottom Y at the WALL edge positions (not the overhang edges)
        // At x = roofOffsetX (left wall edge), the fascia bottom is:
        const leftWallFasciaBtm = leftFasciaBottom + fasciaBtmSlope * roofDrawOverhang;
        // At x = roofOffsetX + roofElevWidth (right wall edge), the fascia bottom is:
        const rightWallFasciaBtm = rightFasciaBottom - fasciaBtmSlope * roofDrawOverhang;

        // Draw wall extension - trapezoidal area from standard wall top up to the FASCIA BOTTOM at wall positions
        // The wall and siding stop at the bottom of the fascia board (at the wall edge positions)
        // Only draw where fascia bottom is above standard wall top (roofBaseY)
        const leftWallExtension = Math.max(0, roofBaseY - leftWallFasciaBtm);
        const rightWallExtension = Math.max(0, roofBaseY - rightWallFasciaBtm);

        if (leftWallExtension > 0 || rightWallExtension > 0) {
          // Wall extension is a trapezoid from roofBaseY up to the sloped fascia bottom at wall positions
          ctx.fillStyle = wallStyle.fill;
          ctx.beginPath();
          ctx.moveTo(roofOffsetX, roofBaseY); // Bottom left
          ctx.lineTo(roofOffsetX + roofElevWidth, roofBaseY); // Bottom right
          ctx.lineTo(roofOffsetX + roofElevWidth, Math.min(rightWallFasciaBtm, roofBaseY)); // Top right (clamped)
          ctx.lineTo(roofOffsetX, Math.min(leftWallFasciaBtm, roofBaseY)); // Top left (clamped)
          ctx.closePath();
          ctx.fill();

          // Draw siding lines in wall extension, clipped to the sloped FASCIA BOTTOM
          // The wall extension is a trapezoid bounded by:
          // - Bottom: roofBaseY (standard wall top / plate line)
          // - Top: sloped line from leftWallFasciaBtm to rightWallFasciaBtm
          ctx.strokeStyle = sidingStyle.stroke;
          ctx.lineWidth = sidingStyle.lineWidth;

          // Slope within the wall width (not including overhangs)
          const fasciaSlope = (rightWallFasciaBtm - leftWallFasciaBtm) / roofElevWidth;

          // Helper to get fascia bottom Y at a given X position (within wall bounds)
          const getFasciaBottomYAtX = (x) => leftWallFasciaBtm + fasciaSlope * (x - roofOffsetX);

          // Helper to get valid X range for horizontal line at given Y
          const getValidXRange = (y) => {
            let drawStartX = roofOffsetX;
            let drawEndX = roofOffsetX + roofElevWidth;

            if (Math.abs(fasciaSlope) > 0.0001) {
              const intersectX = roofOffsetX + (y - leftWallFasciaBtm) / fasciaSlope;
              if (fasciaSlope > 0) {
                drawEndX = Math.min(roofOffsetX + roofElevWidth, Math.max(roofOffsetX, intersectX));
              } else {
                drawStartX = Math.max(roofOffsetX, Math.min(roofOffsetX + roofElevWidth, intersectX));
              }
            } else if (y <= leftWallFasciaBtm) {
              return null; // Line above fascia bottom
            }
            return drawEndX > drawStartX ? { start: drawStartX, end: drawEndX } : null;
          };

          if (sidingPattern === 'horizontal') {
            const slopeSidingSpacing = 6 * elevScale;
            // Start at roofBaseY (plate line) so the first row line closes the gap at the bottom
            for (let sy = roofBaseY; sy > Math.min(leftWallFasciaBtm, rightWallFasciaBtm); sy -= slopeSidingSpacing) {
              const range = getValidXRange(sy);
              if (range) {
                ctx.beginPath();
                ctx.moveTo(range.start, sy);
                ctx.lineTo(range.end, sy);
                ctx.stroke();
              }
            }
          } else if (sidingPattern === 'board-batten') {
            // Vertical board-and-batten siding in the trapezoid
            // IMPORTANT: Use same spacing reference as main wall (the wall's left edge) for alignment
            const boardSpacing = 24 * elevScale;
            const battenWidth = 3 * elevScale;
            const shedLeft = roofOffsetX;
            const shedRight = roofOffsetX + roofElevWidth;
            // Use GLOBAL spacing from the wall's left edge (same as main wall) for alignment
            for (let sx = wallLeftEdgeX + boardSpacing; sx < shedRight; sx += boardSpacing) {
              // Skip if outside shed horizontal bounds
              if (sx < shedLeft || sx > shedRight) continue;
              // Get the Y position where the fascia bottom is at this X
              const fasciaY = getFasciaBottomYAtX(sx);
              // Only draw if fascia is above roofBaseY (i.e., there's wall extension here)
              if (fasciaY < roofBaseY) {
                // Main board edge
                ctx.lineWidth = sidingStyle.lineWidth;
                ctx.beginPath();
                ctx.moveTo(sx, fasciaY);
                ctx.lineTo(sx, roofBaseY);
                ctx.stroke();
                // Batten (slightly offset, thicker look)
                const battenX = sx + battenWidth / 2;
                if (battenX <= shedRight && battenX >= shedLeft) {
                  ctx.lineWidth = sidingStyle.lineWidth * 2;
                  ctx.beginPath();
                  ctx.moveTo(battenX, fasciaY);
                  ctx.lineTo(battenX, roofBaseY);
                  ctx.stroke();
                }
              }
            }
            ctx.lineWidth = sidingStyle.lineWidth;
          } else if (sidingPattern === 'shingle') {
            // Shingle/shake siding pattern in the trapezoid
            const rowHeight = 10 * elevScale;
            const shingleWidth = 8 * elevScale;
            let shingleRowIndex = 0;
            // Start at roofBaseY (plate line) so the first row line closes the gap at the bottom
            for (let sy = roofBaseY; sy > Math.min(leftWallFasciaBtm, rightWallFasciaBtm); sy -= rowHeight) {
              const rowOffset = (shingleRowIndex % 2) * (shingleWidth / 2);
              // Horizontal row line - clipped to fascia
              const range = getValidXRange(sy);
              if (range) {
                ctx.beginPath();
                ctx.moveTo(range.start, sy);
                ctx.lineTo(range.end, sy);
                ctx.stroke();
                // Vertical shingle joints - clipped to fascia
                for (let sx = roofOffsetX + rowOffset + shingleWidth; sx < range.end; sx += shingleWidth) {
                  if (sx >= range.start) {
                    const fasciaY = getFasciaBottomYAtX(sx);
                    const topY = Math.max(sy - rowHeight, fasciaY);
                    if (sy > topY) {
                      ctx.beginPath();
                      ctx.moveTo(sx, topY);
                      ctx.lineTo(sx, sy);
                      ctx.stroke();
                    }
                  }
                }
              }
              shingleRowIndex++;
            }
          }

        }

        // Draw complete vertical wall edges from grade to fascia TOP
        // The wall stops where it meets the fascia roof line, not the fascia bottom
        // At the wall positions (roofOffsetX and roofOffsetX+roofElevWidth), calculate where the
        // fascia top line intersects those X positions
        ctx.strokeStyle = wallStyle.stroke;
        ctx.lineWidth = wallStyle.lineWidth;

        // The fascia top line goes from (roofOffsetX - roofDrawOverhang, leftRoofY) to
        // (roofOffsetX + roofElevWidth + roofDrawOverhang, rightRoofY)
        // Calculate fascia top Y at the wall edge positions
        const fasciaTopSlope = (rightRoofY - leftRoofY) / (roofElevWidth + 2 * roofDrawOverhang);
        // On the HIGH side, the wall extends above roofBaseY to meet the fascia
        // On the LOW side, the wall stops at roofBaseY (plate line) - don't create a gap
        const leftWallTopY = Math.min(leftRoofY + fasciaTopSlope * roofDrawOverhang, roofBaseY);
        const rightWallTopY = Math.min(rightRoofY - fasciaTopSlope * roofDrawOverhang, roofBaseY);

        // Wall edges for shed roofs: only draw on the OUTER edges (away from center/connected roofs)
        // The inner edges are handled by connected roof sections (gable fascia, etc.)
        // Determine which side is outer based on roof position relative to elevation center
        const elevationCenterX = offsetX + elevWidth / 2;
        const roofCenterX = roofOffsetX + roofElevWidth / 2;
        const isRoofOnLeft = roofCenterX < elevationCenterX;

        // Only draw the outer edge (left edge if roof is on left, right edge if roof is on right)
        // On the HIGH side, extend wall up to meet fascia; on LOW side, stop at plate line
        ctx.strokeStyle = wallStyle.stroke;
        ctx.lineWidth = wallStyle.lineWidth;
        if (isRoofOnLeft) {
          // Roof is on left side - draw left edge (outer)
          // If leftIsHigh, the outer (left) edge is the HIGH side - extend to fascia
          const outerWallTopY = leftIsHigh ? leftWallTopY : roofBaseY;
          if (isSketchyMode) {
            drawSketchyLine(ctx, roofOffsetX, floorY, roofOffsetX, outerWallTopY);
          } else {
            ctx.beginPath();
            ctx.moveTo(roofOffsetX, floorY);
            ctx.lineTo(roofOffsetX, outerWallTopY);
            ctx.stroke();
          }
        } else {
          // Roof is on right side - draw right edge (outer)
          // If leftIsHigh is false (right is high), the outer (right) edge is the HIGH side - extend to fascia
          const outerWallTopY = leftIsHigh ? roofBaseY : rightWallTopY;
          if (isSketchyMode) {
            drawSketchyLine(ctx, roofOffsetX + roofElevWidth, floorY, roofOffsetX + roofElevWidth, outerWallTopY);
          } else {
            ctx.beginPath();
            ctx.moveTo(roofOffsetX + roofElevWidth, floorY);
            ctx.lineTo(roofOffsetX + roofElevWidth, outerWallTopY);
            ctx.stroke();
          }
        }

        // Draw sloped roof/fascia surface
        // NOTE: Removed white background masking to prevent covering siding on adjacent walls
        ctx.fillStyle = roofStyle.fill;
        ctx.strokeStyle = roofStyle.stroke;
        ctx.beginPath();
        ctx.moveTo(roofOffsetX - roofDrawOverhang, leftRoofY);
        ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, rightRoofY);
        ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, rightRoofY + fasciaHeight);
        ctx.lineTo(roofOffsetX - roofDrawOverhang, leftRoofY + fasciaHeight);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Draw horizontal fascia line at the low side (eave level)
        // The low side is at roofBaseY, the high side is at roofBaseY - roofDrawPeak
        ctx.strokeStyle = roofStyle.stroke;
        ctx.lineWidth = 1.5;
        if (leftIsHigh) {
          // Right side is low - draw horizontal line at right edge
          ctx.beginPath();
          ctx.moveTo(roofOffsetX + roofElevWidth, rightRoofY + fasciaHeight);
          ctx.lineTo(roofOffsetX + roofElevWidth + roofDrawOverhang, rightRoofY + fasciaHeight);
          ctx.stroke();
        } else {
          // Left side is low - draw horizontal line at left edge
          ctx.beginPath();
          ctx.moveTo(roofOffsetX - roofDrawOverhang, leftRoofY + fasciaHeight);
          ctx.lineTo(roofOffsetX, leftRoofY + fasciaHeight);
          ctx.stroke();
        }

        // Store roof outline for selection (the sloped fascia area)
        roofOutlinePoints = [
          { x: roofOffsetX - roofDrawOverhang, y: leftRoofY },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: rightRoofY },
          { x: roofOffsetX + roofElevWidth + roofDrawOverhang, y: rightRoofY + fasciaHeight },
          { x: roofOffsetX - roofDrawOverhang, y: leftRoofY + fasciaHeight },
        ];
      }
    }

    // Restore clipping context if we applied clipping for this roof
    if (useClipping) {
      ctx.restore();
    }

    // Add bounds for this roof to the collection for hit testing
    if (roofOutlinePoints.length >= 3) {
      const xs = roofOutlinePoints.map(p => p.x);
      const ys = roofOutlinePoints.map(p => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      allRoofBounds.push({
        id: currentRoof.id,
        bounds: {
          x: minX,
          y: minY,
          width: maxX - minX,
          height: maxY - minY,
        },
        outlinePoints: [...roofOutlinePoints],
      });
    }
  }; // End drawRoof function

  // TWO-PASS DRAWING: First pass - draw SIDE view roofs (flat roofs that extend under gables)
  processedRoofs.forEach((currentRoof, roofIndex) => {
    // Only draw SIDE view gables in first pass (they go behind END views)
    if (currentRoof.type === 'gable' && !currentRoof.isGableEnd) {
      drawRoof(currentRoof, roofIndex);
    }
  });

  // Second pass - draw END view roofs (gables that appear on top) and non-gable roofs
  processedRoofs.forEach((currentRoof, roofIndex) => {
    // Draw END view gables and all other roof types (hip, shed, etc.)
    if (currentRoof.type !== 'gable' || currentRoof.isGableEnd) {
      drawRoof(currentRoof, roofIndex);
    }
  });

  // Valley lines are now drawn as part of the gable clipping above

  // ========== POSTS ==========
  // Porch and carport posts stand outboard of the facade and carry the roof above
  // them, so they draw after the walls and roofs - in front of both.
  const allPostBounds = [];

  (posts || []).forEach(post => {
    const footprint = getPostFootprint(post);

    // A post turned 90 degrees presents its other face to this elevation.
    const turned = Math.round(Math.abs(((post.rotation || 0) % 180)) / 90) === 1;
    const acrossInches = isHorizontal
      ? (turned ? footprint.depth : footprint.width)
      : (turned ? footprint.width : footprint.depth);

    const axisPos = isHorizontal ? post.x : post.y;

    // Hidden if any wall this elevation can see stands nearer than the post
    const acrossPos = isHorizontal ? post.y : post.x;
    const postDepth = (elevation === 'north' || elevation === 'west') ? acrossPos : -acrossPos;
    const hidden = elevationWalls.some(wall => {
      const span = wallSpan(wall);
      return wallDepth(wall) < postDepth - sideTolerance
        && axisPos > span.min - sideTolerance
        && axisPos < span.max + sideTolerance;
    });
    if (hidden) return;

    const centerX = modelToScreenX(axisPos);
    const halfW = Math.max(1, (acrossInches / 2) * elevScale);
    const postFloorElevation = post.floorElevation || 0;
    const baseInches = POST_BASE_TYPES[post.base]?.height || 0;

    // A porch post bears on its own footing or slab at grade, not on the house
    // floor - starting it at floorY left it standing on top of the foundation,
    // a foot in the air. `bearing: 'floor'` is for a post on a raised deck.
    const bearsOnFloor = post.bearing === 'floor';
    const postBottomY = (bearsOnFloor ? floorY : gradeY) - postFloorElevation * elevScale;

    // The post stops under the beam it carries, not at the eave line: the fascia
    // hangs below that line, so topping out at the eave buried the post's head in
    // the fascia board. Find the roof standing over this post in plan and stop at
    // the underside of its fascia; with several stacked, the lowest one is the one
    // actually bearing on the post.
    const roofOverPost = processedRoofs
      .filter(r => post.x >= r.bounds.minX - 1 && post.x <= r.bounds.maxX + 1
        && post.y >= r.bounds.minY - 1 && post.y <= r.bounds.maxY + 1)
      .sort((a, b) => (a.plateHeight || 96) - (b.plateHeight || 96))[0];

    const postTopY = roofOverPost
      ? floorY - (roofOverPost.plateHeight || wallHeightInches) * elevScale + fasciaHeight
      : postBottomY - (post.height || 96) * elevScale;

    const baseTopY = postBottomY - baseInches * elevScale;

    // Shaft. A tapered post is wider at the base, a turned one is drawn straight
    // here and picks up its profile from the cap and base trim.
    const taper = post.style === 'tapered' ? 0.72 : 1;
    ctx.fillStyle = getPostColor(post);
    ctx.strokeStyle = roofStyle.stroke;
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.moveTo(centerX - halfW, baseTopY);
    ctx.lineTo(centerX - halfW * taper, postTopY);
    ctx.lineTo(centerX + halfW * taper, postTopY);
    ctx.lineTo(centerX + halfW, baseTopY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Base / plinth
    if (baseInches > 0) {
      const baseHalfW = halfW * (post.base === 'pier' ? 1.35 : 1.15);
      ctx.beginPath();
      ctx.rect(centerX - baseHalfW, baseTopY, baseHalfW * 2, postBottomY - baseTopY);
      ctx.fill();
      ctx.stroke();
    }

    // Hit bounds for selection, generous enough that a 5.5" post stays clickable
    const hitPad = Math.max(3, 3 * elevScale);
    allPostBounds.push({
      id: post.id,
      floorId: post.floorId,
      bounds: {
        x: centerX - halfW - hitPad,
        y: postTopY - hitPad,
        width: halfW * 2 + hitPad * 2,
        height: postBottomY - postTopY + hitPad * 2,
      },
    });

    if (selectedPostId && post.id === selectedPostId) {
      ctx.strokeStyle = '#00c8ff';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(centerX - halfW - 3, postTopY - 3, halfW * 2 + 6, postBottomY - postTopY + 6);
      ctx.setLineDash([]);
      ctx.strokeStyle = roofStyle.stroke;
      ctx.lineWidth = 1;
    }

    // Cap trim on boxed columns
    if (post.style === 'boxed') {
      const capH = Math.max(2, 4 * elevScale);
      const capHalfW = halfW * 1.2;
      ctx.beginPath();
      ctx.rect(centerX - capHalfW, postTopY, capHalfW * 2, capH);
      ctx.fill();
      ctx.stroke();
    }
  });

  // Draw selection highlight for the selected roof (if any)
  if (selectedRoofId) {
    const selectedRoofBounds = allRoofBounds.find(rb => rb.id === selectedRoofId);
    if (selectedRoofBounds) {
      ctx.strokeStyle = '#00c8ff';
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 4]);
      ctx.beginPath();

      // Use the outline points if available, otherwise fall back to rectangle
      if (selectedRoofBounds.outlinePoints && selectedRoofBounds.outlinePoints.length >= 3) {
        ctx.moveTo(selectedRoofBounds.outlinePoints[0].x, selectedRoofBounds.outlinePoints[0].y);
        for (let i = 1; i < selectedRoofBounds.outlinePoints.length; i++) {
          ctx.lineTo(selectedRoofBounds.outlinePoints[i].x, selectedRoofBounds.outlinePoints[i].y);
        }
        ctx.closePath();
      } else {
        const b = selectedRoofBounds.bounds;
        ctx.rect(b.x, b.y, b.width, b.height);
      }

      ctx.stroke();
      ctx.setLineDash([]);
    }
  }

  // ========== DIMENSIONS ==========
  // Calculate scale factor for annotation elements based on viewport size
  const annotationScale = Math.max(0.5, Math.min(1.2, bounds.width / 400));

  if (showDimensions && elevationWalls.length > 0) {
    const dimColor = renderMode === 'color' ? '#2060a0' : '#333333';
    const dimFontSize = Math.round(9 * annotationScale);
    const dimOffset = 20 * annotationScale;
    const tickLength = 6 * annotationScale;

    ctx.strokeStyle = dimColor;
    ctx.fillStyle = dimColor;
    ctx.lineWidth = 1;
    ctx.font = `${dimFontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Horizontal dimension at top (total width)
    const dimY = gradeY + drawFoundationHeight + drawWallHeight + drawRoofPeak + dimOffset + 10;
    const leftX = offsetX - drawOverhang;
    const rightX = offsetX + elevWidth + drawOverhang;

    // Draw dimension line
    ctx.beginPath();
    ctx.moveTo(leftX, dimY);
    ctx.lineTo(rightX, dimY);
    ctx.stroke();

    // Ticks at ends
    ctx.beginPath();
    ctx.moveTo(leftX, dimY - tickLength);
    ctx.lineTo(leftX, dimY + tickLength);
    ctx.moveTo(rightX, dimY - tickLength);
    ctx.lineTo(rightX, dimY + tickLength);
    ctx.stroke();

    // Width label
    const totalWidthInches = modelWidthInches + (foundationHeight > 0 ? 0 : 0); // Account for overhang in drawing
    const widthFeet = Math.floor(totalWidthInches / 12);
    const widthInchesRemainder = Math.round(totalWidthInches % 12);
    const widthLabel = widthInchesRemainder > 0
      ? `${widthFeet}' - ${widthInchesRemainder}"`
      : `${widthFeet}' - 0"`;

    // Background for label
    const labelWidth = ctx.measureText(widthLabel).width + 8;
    const labelX = (leftX + rightX) / 2;
    ctx.fillStyle = renderMode === 'color' ? '#f5f5f5' : '#ffffff';
    ctx.fillRect(labelX - labelWidth / 2, dimY - dimFontSize / 2 - 2, labelWidth, dimFontSize + 4);
    ctx.fillStyle = dimColor;
    ctx.fillText(widthLabel, labelX, dimY);

    // Vertical dimension on left (wall height)
    const vDimX = offsetX - drawOverhang - dimOffset - 10;
    const topY = roofBaseY;
    const bottomY = floorY;

    // Draw dimension line
    ctx.beginPath();
    ctx.moveTo(vDimX, topY);
    ctx.lineTo(vDimX, bottomY);
    ctx.stroke();

    // Ticks at ends
    ctx.beginPath();
    ctx.moveTo(vDimX - tickLength, topY);
    ctx.lineTo(vDimX + tickLength, topY);
    ctx.moveTo(vDimX - tickLength, bottomY);
    ctx.lineTo(vDimX + tickLength, bottomY);
    ctx.stroke();

    // Height label
    const heightFeet = Math.floor(wallHeightInches / 12);
    const heightInchesRemainder = Math.round(wallHeightInches % 12);
    const heightLabel = heightInchesRemainder > 0
      ? `${heightFeet}' - ${heightInchesRemainder}"`
      : `${heightFeet}' - 0"`;

    ctx.save();
    ctx.translate(vDimX, (topY + bottomY) / 2);
    ctx.rotate(-Math.PI / 2);

    // Background for label
    const vLabelWidth = ctx.measureText(heightLabel).width + 8;
    ctx.fillStyle = renderMode === 'color' ? '#f5f5f5' : '#ffffff';
    ctx.fillRect(-vLabelWidth / 2, -dimFontSize / 2 - 2, vLabelWidth, dimFontSize + 4);
    ctx.fillStyle = dimColor;
    ctx.fillText(heightLabel, 0, 0);
    ctx.restore();
  }

  // ========== LEVEL LINES ==========

  // Build level bounds for return (even if not drawing, useful for hit testing)
  const baseLevelLineExtension = 80;
  const markerSize = 8 * annotationScale;
  const circleRadius = 10 * annotationScale;
  const gripRadius = 6 * annotationScale;

  // Define levels with string IDs for ElevationView compatibility
  // Include levels for each unique wall height to show stepped wall profiles
  const hasFoundation = foundationHeight > 0;

  // Get unique wall heights from wall segments AND roof plate heights, sorted from low to high
  // This ensures level lines appear for all significant heights, even if no walls face this elevation
  const wallHeightsFromSegments = wallSegments.map(s => s.height);
  const roofPlateHeights = processedRoofs.map(r => r.plateHeight || 96);
  const allHeights = [...wallHeightsFromSegments, ...roofPlateHeights];
  const uniqueWallHeights = [...new Set(allHeights)].sort((a, b) => a - b);

  // Build levels array with a level for each unique wall height
  const levels = [];
  levels.push({ id: 'grade', name: 'G', heightInches: 0, y: gradeY });

  if (hasFoundation) {
    levels.push({ id: 'level1', name: '1', heightInches: foundationHeight, y: floorY });
  }

  // Add level for each unique wall height
  uniqueWallHeights.forEach((height, idx) => {
    const levelNum = hasFoundation ? idx + 2 : idx + 1;
    const totalHeight = hasFoundation ? foundationHeight + height : height;
    const levelY = floorY - (height * elevScale);
    levels.push({
      id: `level${levelNum}`,
      name: `${levelNum}`,
      heightInches: totalHeight,
      y: levelY,
    });
  });

  // Fallback if no wall segments - use max wall height
  if (uniqueWallHeights.length === 0) {
    const levelNum = hasFoundation ? 2 : 1;
    levels.push({
      id: `level${levelNum}`,
      name: `${levelNum}`,
      heightInches: hasFoundation ? foundationHeight + wallHeightInches : wallHeightInches,
      y: roofBaseY,
    });
  }

  // Calculate line end X for each level (with optional extensions)
  const getLevelLineEndX = (levelId) => {
    const ext = levelLineExtensions ? (levelLineExtensions[levelId] || 0) : 0;
    return offsetX + elevWidth + drawOverhang + baseLevelLineExtension + ext;
  };

  // Build level bounds for hit testing (includes grip position)
  const levelBounds = levels.map(level => {
    const lineEndX = getLevelLineEndX(level.id);
    return {
      id: level.id,
      bounds: {
        x: lineEndX - 30 * annotationScale,
        y: level.y,
        width: 60 * annotationScale,
        height: 1,
      },
      gripX: lineEndX + 30 * annotationScale, // Position for grip point (after circle and label)
      gripY: level.y,
      heightInches: level.heightInches,
    };
  });

  // Draw level markers on the right side of the elevation (if enabled)
  if (showLevelLines) {
    const circleFontSize = Math.round(9 * annotationScale);
    const labelFontSize = Math.round(7 * annotationScale);

    levels.forEach(level => {
      const levelY = level.y;
      const levelLineEndX = getLevelLineEndX(level.id);
      const markerX = levelLineEndX - 30 * annotationScale;
      const circleX = markerX + markerSize + (12 * annotationScale);
      const isSelected = selectedLevelId === level.id;

      // Level line (dashed) - from left edge to marker
      ctx.strokeStyle = isSelected ? '#00c8ff' : '#2060a0';
      ctx.lineWidth = isSelected ? 1.5 : 0.75;
      ctx.setLineDash([4, 2]);
      ctx.beginPath();
      ctx.moveTo(offsetX - drawOverhang, levelY);
      ctx.lineTo(markerX - 2, levelY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Triangle marker pointing right
      ctx.fillStyle = isSelected ? '#00c8ff' : '#2060a0';
      ctx.beginPath();
      ctx.moveTo(markerX, levelY - markerSize / 2);
      ctx.lineTo(markerX + markerSize, levelY);
      ctx.lineTo(markerX, levelY + markerSize / 2);
      ctx.closePath();
      ctx.fill();

      // Circle with level number
      ctx.beginPath();
      ctx.arc(circleX, levelY, circleRadius, 0, Math.PI * 2);
      ctx.fillStyle = isSelected ? 'rgba(0,200,255,0.3)' : 'rgba(32,96,160,0.15)';
      ctx.fill();
      ctx.strokeStyle = isSelected ? '#00c8ff' : '#2060a0';
      ctx.lineWidth = isSelected ? 1.5 : 1;
      ctx.stroke();

      // Level number text inside circle
      ctx.font = `bold ${circleFontSize}px Arial`;
      ctx.fillStyle = isSelected ? '#00c8ff' : '#2060a0';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(level.name, circleX, levelY);

      // Height label to the right of circle
      const heightFeet = Math.floor(level.heightInches / 12);
      const heightInchesRemainder = Math.round(level.heightInches % 12);
      const heightLabel = `${heightFeet}' - ${heightInchesRemainder}"`;

      ctx.font = `bold ${labelFontSize}px Arial`;
      ctx.fillStyle = isSelected ? '#00c8ff' : '#2060a0';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(heightLabel, circleX + circleRadius + (4 * annotationScale), levelY);

      // Draw grip point if enabled (small circle at the end for dragging)
      if (showGripPoints) {
        const gripX = circleX + circleRadius + (4 * annotationScale) + ctx.measureText(heightLabel).width + (10 * annotationScale);

        // Horizontal line from label to grip
        ctx.strokeStyle = isSelected ? '#00c8ff' : '#2060a0';
        ctx.lineWidth = 0.5;
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(circleX + circleRadius + (4 * annotationScale) + ctx.measureText(heightLabel).width + 2, levelY);
        ctx.lineTo(gripX - gripRadius, levelY);
        ctx.stroke();
        ctx.setLineDash([]);

        // Grip circle
        ctx.beginPath();
        ctx.arc(gripX, levelY, gripRadius, 0, Math.PI * 2);
        ctx.fillStyle = isSelected ? '#00c8ff' : '#4080c0';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Update levelBounds with actual grip position
        const boundIndex = levelBounds.findIndex(b => b.id === level.id);
        if (boundIndex !== -1) {
          levelBounds[boundIndex].gripX = gripX;
        }
      }
    });
  }

  ctx.restore();

  // Return render info for potential use by caller
  return {
    elevScale,
    actualScaleLabel, // The actual scale being used (useful when auto-fitting)
    offsetX,
    groundY,
    gradeY,
    floorY,
    roofBaseY,
    elevWidth,
    drawWallHeight,
    drawFoundationHeight,
    drawRoofPeak,
    drawOverhang,
    elevationWalls,
    relevantRoof,
    modelWidthInches,
    modelHeightInches,
    wallHeightInches,
    annotationScale,
    levelBounds,
    roofBounds: allRoofBounds, // Array of all roof bounds for hit testing
    postBounds: allPostBounds, // Array of all post bounds for hit testing
    // Additional fields for window/door placement
    // Use wall-only bounds (minPos/maxPos) to match window/door drawing code
    minPos,
    maxPos,
    // The origin openings are actually drawn from. minPos/maxPos cover only the
    // walls, so anything hit-testing against them is out by however far the roofs
    // project past the wall - a whole porch depth on an elevation with one.
    modelMinPos,
    modelMaxPos,
    flipX,
  };
}

export default drawElevation;
